import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import flatpickr from "flatpickr"
import { track } from "../lib/mixpanel"

let sharedConsumer = null
function getConsumer() {
  if (!sharedConsumer) sharedConsumer = createConsumer()
  return sharedConsumer
}

export default class extends Controller {
  static targets = [
    "tableBody", "tableContainer", "sentinel", "empty", "skeleton",
    "searchInput",
    "kindTabsContainer",
    "statusChips", "priorityChips",
    "dateFrom", "dateTo", "datePresets",
    "filterBadge", "clearFiltersRow",
    "detailModal", "detailBackdrop", "detailPanel", "detailContent", "detailLoading",
    "detailTitle", "detailStatus", "detailQuickActions", "detailFooter", "commentInput", "sendBtn",
    "editLink", "pdfLink", "deleteBtn", "prevBtn", "nextBtn",
    "documentsModal", "documentsList", "documentsSelectAll", "documentsDownloadBtn",
    "deleteConfirmModal",
    "actionDropdownContainer", "actionDropdownMenu",
    "reopenBtn", "reopenConfirmModal",
    "bicContainer", "bicBtn", "bicPicker", "bicSearch", "bicMemberList",
    "bicPromptModal", "bicPromptBicWrap", "bicPromptStatusWrap", "bicPromptStatus", "bicPromptTitle", "bicPromptHint", "bicPromptList", "bicPromptApply", "bicPromptAddBtn",
    "bicSelectedChips", "bicConfirmRow",
    "fileInput", "uploadBtn", "uploadStatus",
    "stagedFilesContainer", "stagedBicContainer", "stagedBicChips",
    "stagedTargetDateContainer", "stagedTargetDateLabel",
    "targetDateBtn", "targetDateInput",
    "remindersModal", "remindBefore", "remindAfter", "remindersError", "remindersSave",
    "remindersNote", "remindersCancel", "remindersHistory",
    "stagedActionContainer", "notifySwitch", "notifyTrack", "notifyKnob",
    "seenByContainer",
    "exportModal", "exportKindList",
    "sortIconType", "sortIconTitle", "sortIconStatus", "sortIconDueDate",
    "sortIconBallInCourt", "sortIconApprovers", "sortIconMessages",
    "pdfSelectModal", "pdfSelectList", "pdfSelectAll", "pdfDownloadBtn",
    "chatDeleteModal",
    "bcfExportModal", "bcfExportModelSelect", "bcfExportBtn",
    "bcfImportModal", "bcfImportDropZone", "bcfImportInput", "bcfImportStatus",
    "aiBtn", "aiBadge", "aiPanel", "aiRadarList", "aiRerunBtn",
    "historyModal", "historyImports", "historyEvents", "historyConfirmModal", "historyConfirmText"
  ]

  static values = {
    // Submittals and RFIs are US / Canada only (Company#formal_action_items?).
    formal: { type: Boolean, default: true },
    projectId: Number,
    itemsUrl: String,
    kindUrl: String,
    currentUserId: Number,
    currentUserName: String,
    currentUserInitials: String,
    savedFilters: { type: Object, default: {} },
    savedKindTab: { type: String, default: "" },
    // The columns in the company's order (Studio step 4): the page's keys, "cf_<key>" for its own fields.
    columns: { type: Array, default: [] },
    saveFiltersUrl: String,
    heicWorkerUrl: String,
    deepLinkId: { type: String, default: "" },
    baseUrl: String,
    exportCsvUrl: String,
    exportDocumentsListUrl: String,
    exportDocumentsUrl: String,
    exportPdfUrl: String,
    bcfExportUrl: String,
    bcfImportUrl: String,
    historyUrl: String,
    importRevertUrl: String,
    bimEnabled: { type: Boolean, default: false },
    allowMultiBic: { type: Boolean, default: true },
    promptBic: { type: Boolean, default: false },
    builderProject: { type: Boolean, default: false },
    i18nDeleted: { type: String, default: "Action item deleted" },
    i18nNotified: { type: String, default: "Participants notified by email" },
    i18nNotify: { type: String, default: "Notify participants" },
    i18nNotifyHint: { type: String, default: "Turn off to close this item without emailing participants" },
    i18nNotifiedSilently: { type: String, default: "Status updated — no email sent" },
    // 25 seconds to undo before the emails go (Phelipe 2026-10-26): the
    // action_item_undo strings, English below when a key is missing.
    i18nUndo: { type: Object, default: {} },
    // The item's History pop-up (Phelipe 2026-10-26).
    i18nHistory: { type: Object, default: {} },
    i18nUploadingOne: { type: String, default: "Uploading 1 file..." },
    i18nUploadingMany: { type: String, default: "Uploading %{count} files..." },
    i18nApproved: { type: String, default: "Approved" },
    i18nClosed: { type: String, default: "Closed" },
    i18nReopened: { type: String, default: "Reopened" },
    i18nCompleted: { type: String, default: "Completed" },
    i18nOpen: { type: String, default: "Open" },
    i18nDue: { type: String, default: "Due" },
    i18nAddedBy: { type: String, default: "added by" },
    i18nFromProcore: { type: String, default: "From Procore" },
    i18nRemindersChip: { type: String, default: "Reminders: %{label}" },
    i18nRemindersSaved: { type: String, default: "Reminders saved" },
    i18nRemindersFailed: { type: String, default: "The reminders couldn't be saved. Try again." },
    i18nRemindersCompany: { type: String, default: "Your company sets the reminders for every action item." },
    i18nRemindersReopen: { type: String, default: "Re-open this item to change its reminders." },
    i18nRemindersHistoryChanged: { type: String, default: "%{name} changed them to %{label}" },
    i18nRemindersHistoryCreated: { type: String, default: "%{name} set them to %{label} when creating the item" },
    i18nRemindersHistoryEmpty: { type: String, default: "No changes yet." },
    i18nAppr: { type: String, default: "Appr" },
    i18nSpecDrawings: { type: String, default: "Spec/Drawings" },
    i18nReopenToComment: { type: String, default: "Re-open this item to add comments" },
    i18nMarkComplete: { type: String, default: "Mark Complete" },
    i18nDaysBehindOne: { type: String, default: "1 day behind" },
    i18nDaysBehindMany: { type: String, default: "%{count} days behind" }
  }

  connect() {
    // Studio changed this list's columns (step 4, 2026-10-19): the rows redraw in the new order, no reload.
    this._onStudioColumns = () => { try { this._reRenderTable() } catch (e) { /* the list draws on the next load */ } }
    window.addEventListener("studio:table-columns", this._onStudioColumns)
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._searchTimeout = null
    this._rowIndex = 0
    this._filters = {}
    this._itemIds = []
    this._activeItemId = null
    this._modalOpen = false
    this._allItems = []
    this._sort = { key: null, order: null }
    this._kindsLoaded = false
    this._projectMembers = []
    this._currentReaders = []
    this._stagedFiles = []   // [{s3Key, filename, contentType, blobUrl, uploading, chipEl}]
    this._stagedBic = null   // [{user_id, name, email}] or null
    this._stagedAction = null // {id, name, closes_item, increments_version} or null
    this._notifyParticipants = true // when false, status change is sent silently (no emails/push)
    this._stagedTargetDate = null // "YYYY-MM-DD" or null
    this._targetDateFp = null    // flatpickr instance
    this._kindActions = []   // cached from timeline data
    this._onKeydown = this._onKeydown.bind(this)
    this._onClickOutsideDropdown = this._onClickOutsideDropdown.bind(this)
    document.addEventListener("click", this._onClickOutsideDropdown)

    // Truncation tooltips: floating tooltip when text is clipped
    this._onTruncateEnter = (e) => {
      const el = e.target.closest("[data-truncate-tip]")
      if (!el || el.scrollWidth <= el.clientWidth) return
      this._showTruncateTip(el)
    }
    this._onTruncateLeave = (e) => {
      const el = e.target.closest("[data-truncate-tip]")
      if (el) this._hideTruncateTip()
    }
    this.element.addEventListener("mouseenter", this._onTruncateEnter, true)
    this.element.addEventListener("mouseleave", this._onTruncateLeave, true)

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) {
          this._loadPage(this._page)
        }
      },
      { rootMargin: "300px", threshold: 0 }
    )

    this._onChatPaste = e => this.handleChatPaste(e)
    document.addEventListener("paste", this._onChatPaste)

    this._restoreSavedFilters()
    this._loadPage(1)
    this._checkDeepLink()
    this._showPendingToast()
  }

  disconnect() {
    this._closeMessageMenus()
    window.removeEventListener("studio:table-columns", this._onStudioColumns)
    if (this._observer) this._observer.disconnect()
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("click", this._onClickOutsideDropdown)
    if (this._onChatPaste) document.removeEventListener("paste", this._onChatPaste)
    this.element.removeEventListener("mouseenter", this._onTruncateEnter, true)
    this.element.removeEventListener("mouseleave", this._onTruncateLeave, true)
    this._hideTruncateTip()
    this._unsubscribeChannel()
    if (this._heicWorker) { this._heicWorker.terminate(); this._heicWorker = null }
    if (this._targetDateFp) { this._targetDateFp.destroy(); this._targetDateFp = null }
  }

  // ── Column Sorting ──────────────────────────────────────────────

  sortColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this._sort.key === key) {
      this._sort.order = this._sort.order === "asc" ? "desc" : "asc"
    } else {
      this._sort.key = key
      this._sort.order = "asc"
    }
    this._reRenderTable()
  }

  _reRenderTable() {
    this._rowIndex = 0
    const sorted = this._sortItems(this._allItems)
    if (this.hasTableBodyTarget) this.tableBodyTarget.innerHTML = sorted.map(item => this._renderRow(item)).join("")
    this._updateSortIcons()
  }

  _sortItems(items) {
    if (!this._sort.key) return items
    const sorted = [...items]
    const dir = this._sort.order === "desc" ? -1 : 1
    sorted.sort((a, b) => {
      let va, vb
      switch (this._sort.key) {
        // Type, then the number inside it (Phelipe 2026-10-16): SUBMITTAL 001,
        // 002, 003… instead of the due-date order the list comes in.
        case "type":
          va = (a.kind_name || "").toLowerCase()
          vb = (b.kind_name || "").toLowerCase()
          if (va !== vb) return va < vb ? -dir : dir
          return ((Number(a.sequential) || 0) - (Number(b.sequential) || 0) || (Number(a.version) || 0) - (Number(b.version) || 0)) * dir
        case "title":
          va = (a.title || "").toLowerCase()
          vb = (b.title || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "status":
          va = (a.display_status || "").toLowerCase()
          vb = (b.display_status || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "due_date":
          va = a.due_date || ""; vb = b.due_date || ""
          return va < vb ? -dir : va > vb ? dir : 0
        // People columns sort by the FIRST name shown in the cell — the same
        // one the chip renders — so the order on screen matches the order
        // in the column (Phelipe 2026-09-01).
        case "ball_in_court":
          va = this._firstPersonName(a.ball_in_court)
          vb = this._firstPersonName(b.ball_in_court)
          return va < vb ? -dir : va > vb ? dir : 0
        case "approvers":
          va = this._firstPersonName(a.approvers)
          vb = this._firstPersonName(b.approvers)
          return va < vb ? -dir : va > vb ? dir : 0
        // Messages: by how many there are, unread breaking ties.
        case "messages":
          va = (a.attached_messages_sum || 0) - (b.attached_messages_sum || 0)
          if (va !== 0) return va * dir
          return ((a.unread_count || 0) - (b.unread_count || 0)) * dir
        default: return 0
      }
    })
    return sorted
  }

  // What the chip prints for the first person in a list: their name, or the
  // local part of an email-only entry. Empty list → "" (sorts to one end).
  _firstPersonName(list) {
    const p = Array.isArray(list) ? list[0] : null
    if (!p) return ""
    const raw = (!p.name || (p.email && p.name === p.email)) ? (p.email || "") : p.name
    return (raw.includes("@") ? raw.split("@")[0] : raw).toLowerCase()
  }

  _updateSortIcons() {
    const arrowUp = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>`
    const arrowDown = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>`
    const keys = { type: "Type", title: "Title", status: "Status", due_date: "DueDate",
                   ball_in_court: "BallInCourt", approvers: "Approvers", messages: "Messages" }
    for (const [key, name] of Object.entries(keys)) {
      const targetName = `sortIcon${name}Target`
      const hasName = `hasSortIcon${name}Target`
      if (this[hasName]) {
        this[targetName].innerHTML = this._sort.key === key ? (this._sort.order === "asc" ? arrowUp : arrowDown) : ""
      }
    }
  }

  // ── Search (500ms debounce) ────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._filters.search = this.searchInputTarget.value.trim()
      this._resetAndLoad()
    }, 500)
  }

  // ── Filters ────────────────────────────────────────────────────────

  toggleStatusChip(event) {
    this._toggleChip(event.currentTarget)
    this._syncStatusFilter()
  }

  togglePriorityChip(event) {
    this._toggleChip(event.currentTarget)
    this._syncPriorityFilter()
  }

  _toggleChip(btn) {
    const isActive = btn.classList.contains("bg-brand/10")
    if (isActive) {
      btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
      btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
    } else {
      btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
      btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
    }
  }

  _clearDatepickers() {
    [this.hasDateFromTarget && this.dateFromTarget, this.hasDateToTarget && this.dateToTarget]
      .filter(Boolean)
      .forEach(el => {
        const fp = el._flatpickr
        fp ? fp.clear() : el.value = ""
      })
  }

  _restoreSavedFilters() {
    const saved = this.savedFiltersValue || {}

    // Restore status chips
    if (saved.status && this.hasStatusChipsTarget) {
      const statuses = Array.isArray(saved.status) ? saved.status : [saved.status]
      this.statusChipsTarget.querySelectorAll("button").forEach(btn => {
        if (statuses.includes(btn.dataset.status)) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
      this._filters.status = statuses
    }

    // Restore priority chips
    if (saved.critical_level && this.hasPriorityChipsTarget) {
      const levels = Array.isArray(saved.critical_level) ? saved.critical_level : [saved.critical_level]
      this.priorityChipsTarget.querySelectorAll("button").forEach(btn => {
        if (levels.includes(btn.dataset.priority)) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
      this._filters.critical_level = levels
    }

    // Restore date preset
    if (saved.date_preset && this.hasDatePresetsTarget) {
      // Highlight the saved preset and compute its date range without triggering a reload
      this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === saved.date_preset) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        } else {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
      // Compute dates from preset
      const preset = saved.date_preset
      if (preset === "7") {
        const today = new Date()
        const weekStart = new Date(today); weekStart.setDate(today.getDate() - today.getDay())
        this._filters.date_from = this._formatDate(weekStart)
        this._filters.date_to = this._formatDate(today)
      } else if (preset === "30") {
        const today = new Date()
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
        this._filters.date_from = this._formatDate(monthStart)
        this._filters.date_to = this._formatDate(today)
      } else if (preset === "last_30") {
        const today = new Date()
        const from = new Date(today); from.setDate(from.getDate() - 30)
        this._filters.date_from = this._formatDate(from)
        this._filters.date_to = this._formatDate(today)
      }
    } else if (saved.date_from || saved.date_to) {
      // Restore custom date range — deselect "All Dates" preset
      if (this.hasDatePresetsTarget) {
        this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        })
      }
      if (saved.date_from && this.hasDateFromTarget) {
        const fp = this.dateFromTarget._flatpickr
        fp ? fp.setDate(saved.date_from, true) : this.dateFromTarget.value = saved.date_from
        this._filters.date_from = saved.date_from
      }
      if (saved.date_to && this.hasDateToTarget) {
        const fp = this.dateToTarget._flatpickr
        fp ? fp.setDate(saved.date_to, true) : this.dateToTarget.value = saved.date_to
        this._filters.date_to = saved.date_to
      }
    }

    // Restore kind tab (button styling happens in _loadKinds after they're fetched)
    const savedKind = this.savedKindTabValue
    if (savedKind) {
      this._filters.kind_id = savedKind
    }

    this._updateFilterBadge()
  }

  _persistFilters() {
    if (!this.saveFiltersUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    const filters = {}
    if (this._filters.status) filters.status = this._filters.status
    if (this._filters.critical_level) filters.critical_level = this._filters.critical_level
    if (this._filters.date_from) filters.date_from = this._filters.date_from
    if (this._filters.date_to) filters.date_to = this._filters.date_to
    if (this.hasDatePresetsTarget) {
      const activePreset = [...this.datePresetsTarget.querySelectorAll("button")].find(b => b.classList.contains("bg-brand/10"))
      if (activePreset && activePreset.dataset.preset) filters.date_preset = activePreset.dataset.preset
    }

    fetch(this.saveFiltersUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      credentials: "same-origin",
      body: JSON.stringify({
        filters: filters,
        kind_tab: this._filters.kind_id || ""
      })
    }).catch(() => {})
  }

  _syncStatusFilter() {
    if (!this.hasStatusChipsTarget) return
    const buttons = this.statusChipsTarget.querySelectorAll("button")
    const active = [...buttons].filter(b => b.classList.contains("bg-brand/10"))
    if (active.length === 0) {
      delete this._filters.status
    } else {
      this._filters.status = active.map(b => b.dataset.status)
    }
    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  _syncPriorityFilter() {
    if (!this.hasPriorityChipsTarget) return
    const buttons = this.priorityChipsTarget.querySelectorAll("button")
    const active = [...buttons].filter(b => b.classList.contains("bg-brand/10"))
    if (active.length === 0) {
      delete this._filters.critical_level
    } else {
      this._filters.critical_level = active.map(b => b.dataset.priority)
    }
    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  filterByDate() {
    const from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const to = this.hasDateToTarget ? this.dateToTarget.value : ""
    this._filters.date_from = from
    this._filters.date_to = to
    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset

    // Highlight the active preset button
    if (this.hasDatePresetsTarget) {
      this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === preset) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        } else {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
    }

    // Clear custom date inputs when using a preset
    this._clearDatepickers()

    if (!preset) {
      this._filters.date_from = ""
      this._filters.date_to = ""
    } else if (preset === "7") {
      const today = new Date()
      const weekStart = new Date(today)
      weekStart.setDate(today.getDate() - today.getDay())
      this._filters.date_from = this._formatDate(weekStart)
      this._filters.date_to = this._formatDate(today)
    } else if (preset === "30") {
      const today = new Date()
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      this._filters.date_from = this._formatDate(monthStart)
      this._filters.date_to = this._formatDate(today)
    } else if (preset === "last_30") {
      const today = new Date()
      const from = new Date(today)
      from.setDate(from.getDate() - 30)
      this._filters.date_from = this._formatDate(from)
      this._filters.date_to = this._formatDate(today)
    }

    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  clearFilters() {
    // Preserve kind tab selection (it's separate navigation)
    const kindId = this._filters.kind_id
    this._filters = {}
    if (kindId) this._filters.kind_id = kindId

    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    // Reset status chips to all inactive (no filter = show everything)
    if (this.hasStatusChipsTarget) {
      this.statusChipsTarget.querySelectorAll("button").forEach(btn => {
        btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
        btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
      })
    }
    // Reset priority chips to all inactive
    if (this.hasPriorityChipsTarget) {
      this.priorityChipsTarget.querySelectorAll("button").forEach(btn => {
        btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
        btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
      })
    }
    this._clearDatepickers()

    // Reset date preset buttons
    if (this.hasDatePresetsTarget) {
      this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === "") {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        } else {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
    }

    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  // ── Kind tab selection ─────────────────────────────────────────────

  selectKindTab(event) {
    const kindId = event.currentTarget.dataset.kindId

    // Update tab styling
    if (this.hasKindTabsContainerTarget) {
      this.kindTabsContainerTarget.querySelectorAll("button").forEach(btn => {
        const badge = btn.querySelector("[data-count-badge]")
        if (btn.dataset.kindId === kindId) {
          btn.classList.add("text-brand", "font-bold")
          btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
          if (badge) {
            badge.classList.remove("bg-gray-200", "text-gray-600")
            badge.classList.add("bg-blue-100", "text-blue-600")
          }
        } else {
          btn.classList.remove("text-brand", "font-bold")
          btn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
          if (badge) {
            badge.classList.remove("bg-blue-100", "text-blue-600")
            badge.classList.add("bg-gray-200", "text-gray-600")
          }
        }
      })
    }

    if (kindId) {
      this._filters.kind_id = kindId
    } else {
      delete this._filters.kind_id
    }

    this._persistFilters()
    this._applySubmittalColumns()
    this._resetAndLoad()
  }

  // ── Detail slide-over ──────────────────────────────────────────────

  openItem(event) {
    const itemId = event.currentTarget.dataset.itemId
    if (itemId) this._openDetail(itemId)
  }

  closeDetail() {
    this._closeModal()
    // Clear the cached detail so a future ACC sync action (Send / Resolve)
    // can't fire against a stale punchlist if the user closes mid-render.
    this._currentDetail = null
  }

  // § 3.5 Tier 5 Step 52 — manual "Send to ACC" trigger from the
  // slide-over header pill. POSTs to the controller endpoint that
  // enqueues `AccIssueSyncJob` for the current punchlist.
  async triggerAccSync(event) {
    if (event && event.preventDefault) event.preventDefault()
    const acc = this._currentDetail?.acc_sync
    if (!acc?.sync_to_acc_url) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(acc.sync_to_acc_url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": token }
      })
      if (resp.ok) {
        this._showToast("Sync to Autodesk Construction Cloud queued.", "notice")
      } else {
        this._showToast("Could not queue sync — check the project link.", "alert")
      }
    } catch (e) {
      this._showToast("Could not queue sync — network error.", "alert")
    }
  }

  // § 3.5 Tier 5 Step 57 — open the sync-history dropdown. Lazy-loads
  // the last 5 attempts the first time it's clicked, then toggles
  // visibility on subsequent clicks.
  async toggleAccSyncMenu(event) {
    if (event && event.preventDefault) event.preventDefault()
    const acc = this._currentDetail?.acc_sync
    if (!acc?.sync_history_url) return
    if (acc.external_url) {
      // Right now the dropdown only carries the deep link; surfacing
      // it as an immediate new-tab open keeps the slide-over chrome
      // light. The richer dropdown lives in the public API surface
      // for first-customer feedback.
      window.open(acc.external_url, "_blank", "noopener")
    }
  }

  // § 3.5 Tier 5 Step 58 — resolve a pending field conflict. POSTs the
  // user's pick (`conslog` / `acc` / `merge`) to the controller; the
  // server clears the conflict + re-pushes when the user kept ConsLog
  // or chose Merge.
  async resolveAccConflict(event) {
    if (event && event.preventDefault) event.preventDefault()
    const winner = event.currentTarget?.dataset?.winner
    const acc = this._currentDetail?.acc_sync
    if (!winner || !acc?.resolve_conflict_url) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(acc.resolve_conflict_url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({ winner: winner })
      })
      if (resp.ok) {
        const banner = this.element.querySelector("[data-acc-conflict-banner]")
        if (banner) banner.remove()
        this._showToast("Conflict resolved.", "notice")
      } else {
        this._showToast("Could not resolve conflict.", "alert")
      }
    } catch (e) {
      this._showToast("Could not resolve conflict — network error.", "alert")
    }
  }

  backdropMousedown(event) {
    this._mousedownTarget = event.target
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget && this._mousedownTarget === event.currentTarget) {
      this._closeModal()
    }
    this._mousedownTarget = null
  }

  prevItem() {
    const idx = this._itemIds.indexOf(this._activeItemId)
    if (idx > 0) this._openDetail(this._itemIds[idx - 1])
  }

  nextItem() {
    const idx = this._itemIds.indexOf(this._activeItemId)
    if (idx >= 0 && idx < this._itemIds.length - 1) {
      this._openDetail(this._itemIds[idx + 1])
    }
  }

  showDeleteConfirm() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.remove("hidden")
    }
  }

  cancelDelete() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.add("hidden")
    }
  }

  async confirmDelete() {
    this.cancelDelete()
    if (!this._activeItemId) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}`)

    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (res.ok) {
        // Remove row from table
        const row = this.element.querySelector(`[data-item-id="${this._activeItemId}"]`)
        if (row) row.remove()

        // Remove from item IDs list
        this._itemIds = this._itemIds.filter(id => id !== this._activeItemId)

        // Close the slide-over
        this.closeDetail()

        // Update count badge
        const countEl = this.element.querySelector("[data-items-count]")
        if (countEl) countEl.textContent = this._itemIds.length

        this._showToast(this.i18nDeletedValue)
      } else {
        const data = await res.json().catch(() => ({}))
        this._showToast(data.error || "Failed to delete action item", "alert")
      }
    } catch (e) {
      console.error("[ActionItems] delete error:", e)
      this._showToast("Network error. Please try again.", "alert")
    }
  }

  // ── History modal (creator company): bulk uploads + revert, activity ──

  openHistoryModal() {
    if (!this.hasHistoryModalTarget) return
    this.historyModalTarget.classList.remove("hidden")
    this._loadHistory()
  }

  closeHistoryModal() {
    if (this.hasHistoryModalTarget) this.historyModalTarget.classList.add("hidden")
  }

  async _loadHistory() {
    this.historyImportsTarget.innerHTML = `<p class="text-[13px] text-gray-400">Loading…</p>`
    this.historyEventsTarget.innerHTML = ""
    try {
      const res = await fetch(this.historyUrlValue, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      if (!res.ok) throw new Error()
      const data = await res.json()
      this._renderHistory(data)
    } catch {
      this.historyImportsTarget.innerHTML = `<p class="text-[13px] text-gray-400">Couldn't load the history.</p>`
    }
  }

  _renderHistory(data) {
    const sourceLabels = { register: "Submittal log", files: "File upload", text: "Pasted list" }
    const imports = data.imports || []
    this.historyImportsTarget.innerHTML = imports.length ? imports.map(imp => {
      const bits = []
      if (imp.created) bits.push(`${imp.created} item${imp.created === 1 ? "" : "s"} created`)
      if (imp.updated) bits.push(`${imp.updated} updated`)
      if (imp.packages) bits.push(`${imp.packages} package${imp.packages === 1 ? "" : "s"}`)
      const reverted = imp.status === "reverted"
      const right = reverted
        ? `<span class="inline-flex px-2 py-0.5 rounded-full text-[12px] font-medium bg-gray-100 text-gray-500">Reverted${imp.reverted_by ? ` by ${this._escapeHtml(imp.reverted_by)}` : ""}</span>`
        : `<button type="button" data-import-id="${imp.id}" data-import-summary="${this._escapeHtml(bits.join(" · "))}"
                   data-action="click->action-items#revertImport"
                   class="px-2.5 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-[13px] font-medium cursor-pointer">Revert</button>`
      return `<div class="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2 ${reverted ? "opacity-60" : ""}">
        <div class="min-w-0">
          <p class="text-[13px] font-medium text-gray-900">${this._escapeHtml(sourceLabels[imp.source] || "Bulk upload")}</p>
          <p class="text-[12px] text-gray-500 truncate">${this._escapeHtml(bits.join(" · ") || "Nothing created")} · ${this._escapeHtml(imp.who || "")} · ${this._displayDateTime(imp.when)}</p>
        </div>
        ${right}
      </div>`
    }).join("") : `<p class="text-[13px] text-gray-400">No bulk uploads yet.</p>`

    const actionLabels = {
      created: "created", deleted: "deleted", completed: "completed", approved: "approved",
      commented: "commented on", reassigned: "reassigned", target_date_changed: "changed the date of",
      reminders_changed: "changed reminders on", published: "published", bulk_upload_reverted: "reverted a bulk upload"
    }
    const events = data.events || []
    this.historyEventsTarget.innerHTML = events.length ? events.map(ev => {
      const verb = ev.label || actionLabels[ev.action] || ev.action.replace(/_/g, " ")
      const title = ev.title ? ` <span class="text-gray-700">${this._escapeHtml(ev.title)}</span>` : ""
      return `<div class="flex items-baseline justify-between gap-3 py-1.5">
        <p class="text-[13px] text-gray-600 min-w-0 truncate"><span class="font-medium text-gray-900">${this._escapeHtml(ev.who || "Someone")}</span> ${this._escapeHtml(verb)}${title}</p>
        <span class="text-[12px] text-gray-400 whitespace-nowrap">${this._displayDateTime(ev.when)}</span>
      </div>`
    }).join("") : `<p class="text-[13px] text-gray-400">No activity yet.</p>`
  }

  revertImport(event) {
    const btn = event.currentTarget
    this._revertImportId = btn.dataset.importId
    this.historyConfirmTextTarget.textContent =
      `This removes everything that upload created (${btn.dataset.importSummary}). Items it only updated keep their changes. You can't undo this here.`
    this.historyConfirmModalTarget.classList.remove("hidden")
  }

  cancelRevert() {
    this._revertImportId = null
    if (this.hasHistoryConfirmModalTarget) this.historyConfirmModalTarget.classList.add("hidden")
  }

  async confirmRevert() {
    const id = this._revertImportId
    if (!id) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.importRevertUrlValue.replace("IMPORT_ID", id), {
        method: "POST", credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": token }
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Couldn't revert the upload.")
      const msg = `Upload reverted — ${data.removed} item${data.removed === 1 ? "" : "s"} removed.`
      sessionStorage.setItem("actionItemToast", JSON.stringify({ message: msg, type: "notice" }))
      window.location.reload()
    } catch (err) {
      this.cancelRevert()
      this._showToast(err.message || "Couldn't revert the upload.", "alert")
    }
  }

  // ── Export modal ──────────────────────────────────────────────────

  openExportModal() {
    if (!this.hasExportModalTarget) return
    this.exportModalTarget.classList.remove("hidden")
    this._populateExportKinds()
  }

  closeExportModal() {
    if (this.hasExportModalTarget) this.exportModalTarget.classList.add("hidden")
  }

  _populateExportKinds() {
    if (!this.hasExportKindListTarget) return
    const kinds = this._kinds || []
    const activeKindId = this._filters.kind_id || ""

    if (kinds.length === 0) {
      this.exportKindListTarget.innerHTML = '<p class="text-sm text-gray-400">Loading types...</p>'
      return
    }

    this.exportKindListTarget.innerHTML = kinds.map(k => {
      const checked = !activeKindId || String(k.id) === String(activeKindId) ? "checked" : ""
      return `<label class="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" value="${k.id}" ${checked}
               class="rounded border-gray-300 text-brand focus:ring-brand cursor-pointer">
        <span class="text-sm text-gray-700">${this._escapeHtml(k.name)}</span>
      </label>`
    }).join("")
  }

  _getSelectedKindIds() {
    if (!this.hasExportKindListTarget) return []
    return Array.from(this.exportKindListTarget.querySelectorAll("input:checked")).map(el => el.value)
  }

  _buildExportParams() {
    const params = new URLSearchParams()
    const kindIds = this._getSelectedKindIds()
    kindIds.forEach(id => params.append("kind_ids[]", id))
    if (this._filters.status) {
      const statuses = Array.isArray(this._filters.status) ? this._filters.status : [this._filters.status]
      statuses.forEach(s => params.append("status[]", s))
    }
    if (this._filters.critical_level) {
      const levels = Array.isArray(this._filters.critical_level) ? this._filters.critical_level : [this._filters.critical_level]
      levels.forEach(l => params.append("critical_level[]", l))
    }
    if (this._filters.date_from) params.append("date_from", this._filters.date_from)
    if (this._filters.date_to) params.append("date_to", this._filters.date_to)
    return params.toString()
  }

  exportCsv() {
    const qs = this._buildExportParams()
    const url = this.exportCsvUrlValue + (qs ? `?${qs}` : "")
    window.open(url, "_blank")
    this.closeExportModal()
  }

  exportPdf() {
    const qs = this._buildExportParams()
    const url = this.exportPdfUrlValue + (qs ? `?${qs}` : "")
    window.open(url, "_blank")
    this.closeExportModal()
  }

  // Documents export (Chad 2026-08-20): CSV/PDF above hand back the log;
  // this hands back the items themselves, merged, to forward to a sub.
  async openDocumentsModal() {
    this.closeExportModal()
    if (!this.hasDocumentsModalTarget) return
    this.documentsModalTarget.classList.remove("hidden")
    this.documentsModalTarget.classList.add("flex")
    if (this.hasDocumentsSelectAllTarget) this.documentsSelectAllTarget.checked = false

    const qs = this._buildExportParams()
    const url = this.exportDocumentsListUrlValue + (qs ? `?${qs}` : "")
    try {
      const resp = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await resp.json().catch(() => ({}))
      const items = data.items || []
      this.documentsListTarget.innerHTML = items.length
        ? items.map(it => `<label class="flex items-center gap-2 py-0.5 cursor-pointer">
            <input type="checkbox" value="${it.id}" data-documents-checkbox
                   data-action="change->action-items#syncDocumentsSelectAll"
                   class="rounded border-gray-300 text-brand focus:ring-brand">
            <span class="flex-1 truncate">${this._escapeHtml(it.title || "")}</span>
            <span class="text-gray-400 shrink-0">${it.files === 1 ? "1 file" : `${it.files} files`}</span>
          </label>`).join("")
        : `<p class="text-gray-400">Nothing to export for those types.</p>`
    } catch {
      this.documentsListTarget.innerHTML = `<p class="text-red-500">Couldn't load the list — try again.</p>`
    }
  }

  closeDocumentsModal() {
    if (!this.hasDocumentsModalTarget) return
    this.documentsModalTarget.classList.add("hidden")
    this.documentsModalTarget.classList.remove("flex")
  }

  toggleAllDocuments(event) {
    const on = event.target.checked
    this.documentsListTarget.querySelectorAll("input[data-documents-checkbox]").forEach(cb => { cb.checked = on })
  }

  syncDocumentsSelectAll() {
    if (!this.hasDocumentsSelectAllTarget) return
    const boxes = this.documentsListTarget.querySelectorAll("input[data-documents-checkbox]")
    this.documentsSelectAllTarget.checked = boxes.length > 0 && Array.from(boxes).every(cb => cb.checked)
  }

  async downloadDocuments() {
    const ids = Array.from(
      this.documentsListTarget.querySelectorAll("input[data-documents-checkbox]:checked")
    ).map(cb => cb.value)
    if (ids.length === 0) return

    const btn = this.hasDocumentsDownloadBtnTarget ? this.documentsDownloadBtnTarget : null
    const original = btn?.innerHTML
    if (btn) {
      btn.disabled = true
      btn.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Generating…`
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.exportDocumentsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        credentials: "same-origin",
        body: JSON.stringify({ punchlist_ids: ids })
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      window.open(URL.createObjectURL(blob), "_blank")
      this.closeDocumentsModal()
    } catch (e) {
      alert("Couldn't build that PDF — try fewer items, or one at a time.")
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = original
      }
    }
  }

  // ── BCF 2.1 export / import (Tier 7 Steps 75-77) ─────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  openBcfExportModal() {
    this.closeExportModal()
    if (!this.hasBcfExportModalTarget) return
    if (this.hasBcfExportModelSelectTarget) this.bcfExportModelSelectTarget.value = ""
    this.bcfExportModalTarget.classList.remove("hidden")
  }

  openBcfImportModal() {
    if (!this.hasBcfImportModalTarget) return
    if (this.hasBcfImportStatusTarget) {
      this.bcfImportStatusTarget.classList.add("hidden")
      this.bcfImportStatusTarget.textContent = ""
    }
    this.bcfImportModalTarget.classList.remove("hidden")
  }

  closeBcfExportModal() {
    if (this.hasBcfExportModalTarget) this.bcfExportModalTarget.classList.add("hidden")
  }

  async downloadBcfExport(event) {
    event.preventDefault()
    if (this.hasBcfExportBtnTarget) this.bcfExportBtnTarget.disabled = true
    const formData = new FormData()
    // Bundle every action item on the project. The server caps at 500
    // items per export so this stays bounded — bigger projects can
    // re-run after archiving older items, which is the same contract
    // the CSV export uses.
    if (this.hasBcfExportModelSelectTarget && this.bcfExportModelSelectTarget.value) {
      formData.append("bim_model_id", this.bcfExportModelSelectTarget.value)
    }

    try {
      const response = await fetch(this.bcfExportUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/octet-stream" },
        body: formData,
        credentials: "same-origin"
      })
      if (!response.ok) {
        this._showToast("BCF export failed.", "error")
        return
      }
      const blob = await response.blob()
      const filename = this._filenameFromContentDisposition(response.headers.get("Content-Disposition")) ||
                       "action_items.bcfzip"
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      this._showToast("BCF exported.", "success")
      this.closeBcfExportModal()
    } catch (e) {
      console.error("BCF export failed", e)
      this._showToast("BCF export failed.", "error")
    } finally {
      if (this.hasBcfExportBtnTarget) this.bcfExportBtnTarget.disabled = false
    }
  }

  // Import drop-zone handlers — inline highlight only (CLAUDE.md rule:
  // never `fixed inset-0` overlays).

  bcfDropZoneClick() {
    if (this.hasBcfImportInputTarget) this.bcfImportInputTarget.click()
  }

  bcfDragEnter(event) {
    event.preventDefault()
    this._highlightBcfDropZone(true)
  }

  bcfDragOver(event) {
    event.preventDefault()
    this._highlightBcfDropZone(true)
  }

  bcfDragLeave(event) {
    event.preventDefault()
    this._highlightBcfDropZone(false)
  }

  bcfDrop(event) {
    event.preventDefault()
    this._highlightBcfDropZone(false)
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
    if (file) this._uploadBcfFile(file)
  }

  bcfFileChosen(event) {
    const file = event.target.files && event.target.files[0]
    if (file) this._uploadBcfFile(file)
  }

  closeBcfImportModal() {
    if (this.hasBcfImportModalTarget) this.bcfImportModalTarget.classList.add("hidden")
    if (this.hasBcfImportStatusTarget) this.bcfImportStatusTarget.classList.add("hidden")
    if (this.hasBcfImportInputTarget) this.bcfImportInputTarget.value = ""
  }

  async _uploadBcfFile(file) {
    if (!this.hasBcfImportStatusTarget) return
    this.bcfImportStatusTarget.classList.remove("hidden", "bg-red-50", "text-red-700", "bg-emerald-50", "text-emerald-700")
    this.bcfImportStatusTarget.classList.add("bg-gray-50", "text-gray-700")
    this.bcfImportStatusTarget.textContent = `Uploading ${file.name}…`

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.bcfImportUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body: formData,
        credentials: "same-origin"
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.bcfImportStatusTarget.classList.remove("bg-gray-50", "text-gray-700")
        this.bcfImportStatusTarget.classList.add("bg-red-50", "text-red-700")
        this.bcfImportStatusTarget.textContent = data.error || "BCF import failed."
        return
      }
      this.bcfImportStatusTarget.classList.remove("bg-gray-50", "text-gray-700")
      this.bcfImportStatusTarget.classList.add("bg-emerald-50", "text-emerald-700")
      this.bcfImportStatusTarget.textContent =
        `Imported ${data.imported_count} of ${data.topic_count} topics ` +
        `(${data.recognized_model_count} with 3D context).`
      this._showToast("BCF imported. Refreshing…", "success")
      setTimeout(() => window.location.reload(), 600)
    } catch (e) {
      console.error("BCF import failed", e)
      this.bcfImportStatusTarget.classList.remove("bg-gray-50", "text-gray-700")
      this.bcfImportStatusTarget.classList.add("bg-red-50", "text-red-700")
      this.bcfImportStatusTarget.textContent = "BCF import failed."
    }
  }

  _highlightBcfDropZone(on) {
    if (!this.hasBcfImportDropZoneTarget) return
    this.bcfImportDropZoneTarget.classList.toggle("border-brand", on)
    this.bcfImportDropZoneTarget.classList.toggle("bg-brand/5", on)
  }

  _csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }

  _filenameFromContentDisposition(header) {
    if (!header) return null
    const match = /filename="?([^"]+)"?/i.exec(header)
    return match ? match[1] : null
  }

  // ── PDF file selection modal ─────────────────────────────────────────

  openPdfSelectModal(event) {
    const allFiles = [...(this._creationAttachments || []), ...(this._conversationAttachments || [])]
    const mergeableFiles = allFiles.filter(f => f.mergeable)
    if (mergeableFiles.length === 0) {
      // No files to select — open PDF directly
      return
    }
    event.preventDefault()
    this._populatePdfSelectList()
    if (this.hasPdfSelectModalTarget) this.pdfSelectModalTarget.classList.remove("hidden")
  }

  closePdfSelectModal() {
    if (this.hasPdfSelectModalTarget) this.pdfSelectModalTarget.classList.add("hidden")
  }

  _populatePdfSelectList() {
    if (!this.hasPdfSelectListTarget) return
    let html = ""

    const renderGroup = (title, files) => {
      if (!files || files.length === 0) return ""
      let g = `<div>
        <div class="text-[13px] font-medium text-gray-500 uppercase tracking-wide mb-2">${this._escapeHtml(title)}</div>
        <div class="space-y-1.5">`
      files.forEach(f => {
        const disabled = !f.mergeable
        const ext = (f.filename || "").split(".").pop()?.toUpperCase() || "FILE"
        const label = f.is_pdf ? "PDF" : ext
        const labelColor = f.is_pdf ? "text-red-600" : "text-gray-400"
        g += `<label class="flex items-center gap-2.5 ${disabled ? "opacity-50" : "cursor-pointer"}">
          <input type="checkbox" value="${f.id}" ${f.mergeable ? "checked" : "disabled"}
                 data-pdf-file-checkbox
                 data-action="change->action-items#syncPdfSelectAll"
                 class="rounded border-gray-300 text-brand focus:ring-brand ${disabled ? "" : "cursor-pointer"}">
          <span class="inline-flex items-center gap-1.5 min-w-0">
            <span class="text-[13px] font-medium ${labelColor} shrink-0">${label}</span>
            <span class="text-[13px] text-gray-700 truncate">${this._escapeHtml(f.filename || "File")}</span>
          </span>
        </label>`
      })
      g += `</div></div>`
      return g
    }

    html += renderGroup("Attached Files", this._creationAttachments)
    html += renderGroup("Conversation Files", this._conversationAttachments)

    this.pdfSelectListTarget.innerHTML = html
    this._syncPdfSelectAllState()
  }

  toggleAllPdfFiles() {
    if (!this.hasPdfSelectListTarget || !this.hasPdfSelectAllTarget) return
    const checked = this.pdfSelectAllTarget.checked
    this.pdfSelectListTarget.querySelectorAll("input[data-pdf-file-checkbox]:not(:disabled)").forEach(cb => {
      cb.checked = checked
    })
  }

  syncPdfSelectAll() {
    this._syncPdfSelectAllState()
  }

  _syncPdfSelectAllState() {
    if (!this.hasPdfSelectAllTarget || !this.hasPdfSelectListTarget) return
    const boxes = this.pdfSelectListTarget.querySelectorAll("input[data-pdf-file-checkbox]:not(:disabled)")
    if (boxes.length === 0) {
      this.pdfSelectAllTarget.checked = false
      return
    }
    this.pdfSelectAllTarget.checked = Array.from(boxes).every(cb => cb.checked)
  }

  async downloadMergedPdf() {
    const selectedIds = Array.from(
      this.pdfSelectListTarget.querySelectorAll("input[data-pdf-file-checkbox]:checked")
    ).map(cb => cb.value)

    const pdfUrl = this.pdfLinkTarget.href
    if (selectedIds.length === 0) {
      window.open(pdfUrl, "_blank")
      this.closePdfSelectModal()
      return
    }

    // POST to merge_pdf endpoint to keep attachment IDs out of the URL
    const mergeUrl = pdfUrl.replace(/\.pdf$/, "/merge_pdf")
    if (this.hasPdfDownloadBtnTarget) {
      this.pdfDownloadBtnTarget.disabled = true
      this.pdfDownloadBtnTarget.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Generating...`
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(mergeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ attachment_ids: selectedIds })
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, "_blank")
    } catch (e) {
      console.error("PDF merge failed:", e)
      this._showToast("Failed to generate PDF", "error")
    } finally {
      if (this.hasPdfDownloadBtnTarget) {
        this.pdfDownloadBtnTarget.disabled = false
        this.pdfDownloadBtnTarget.innerHTML = `<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg> Download`
      }
      this.closePdfSelectModal()
    }
  }

  // ── Delete comment ──────────────────────────────────────────────────

  // A message's three-dot menu: one open at a time; a click elsewhere or Esc closes it.
  toggleMessageMenu(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const menu = btn.parentElement.querySelector("[data-msg-menu]")
    if (!menu) return
    const opening = menu.hidden
    this._closeMessageMenus()
    if (!opening) return
    menu.hidden = false
    btn.setAttribute("aria-expanded", "true")
    this._msgMenuCloser = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return
      if (e.type === "click" && menu.contains(e.target)) return
      this._closeMessageMenus()
    }
    setTimeout(() => {
      document.addEventListener("click", this._msgMenuCloser)
      document.addEventListener("keydown", this._msgMenuCloser)
    })
  }

  _closeMessageMenus() {
    this.element.querySelectorAll("[data-msg-menu]").forEach(m => {
      m.hidden = true
      m.parentElement.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false")
    })
    if (this._msgMenuCloser) {
      document.removeEventListener("click", this._msgMenuCloser)
      document.removeEventListener("keydown", this._msgMenuCloser)
      this._msgMenuCloser = null
    }
  }

  deleteComment(event) {
    const btn = event.currentTarget
    const commentId = btn.dataset.commentId
    if (!commentId || !this._activeItemId) return
    this._closeMessageMenus()

    this._showChatDeleteConfirm(this._undoT("delete_confirm"), async () => {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}/delete_comment?comment_id=${commentId}`)
        const response = await fetch(url, {
          method: "DELETE",
          headers: { "Accept": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin"
        })
        if (response.ok) {
          const key = CSS.escape(String(commentId))
          this._chatContainer?.querySelector(`div[data-comment-id="${key}"]`)?.remove()
          this._chatContainer?.querySelectorAll(`[data-email-anchor="${key}"]`).forEach(el => el.remove())
        } else {
          const data = await response.json().catch(() => ({}))
          this._showToast(data.error || "Failed to delete message", "alert")
        }
      } catch (e) {
        console.error("[ActionItems] deleteComment error:", e)
      }
    })
  }

  // ── Delete individual attachment ───────────────────────────────────

  deleteAttachment(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const commentId = btn.dataset.commentId
    const attachmentId = btn.dataset.attachmentId
    if (!commentId || !attachmentId || !this._activeItemId) return

    this._showChatDeleteConfirm("Remove this file?", async () => {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}/delete_attachment?comment_id=${commentId}&attachment_id=${attachmentId}`)
        const response = await fetch(url, {
          method: "DELETE",
          headers: { "Accept": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin"
        })
        if (response.ok) {
          this._removeAttachmentFromUI(attachmentId, btn.closest("[data-comment-id]"))
        } else {
          const data = await response.json().catch(() => ({}))
          this._showToast(data.error || "Failed to remove file", "alert")
        }
      } catch (e) {
        console.error("[ActionItems] deleteAttachment error:", e)
      }
    })
  }

  // ── Remove attachment from all UI locations ─────────────────────────

  _removeAttachmentFromUI(attachmentId, bubble) {
    const container = this.detailContentTarget || this.element

    // 1. Remove from chat bubble
    const chatAtt = container.querySelector(`[data-comment-id] [data-attachment-id="${attachmentId}"]`)
    if (chatAtt) {
      if (!bubble) bubble = chatAtt.closest("[data-comment-id]")
      chatAtt.remove()
      // Remove empty bubble (no attachments left and no text)
      if (bubble && !bubble.querySelector("[data-attachment-id]") && !bubble.querySelector("p")) {
        bubble.remove()
      }
    }

    // 2. Remove from header file strip
    const stripAtt = container.querySelector(`[data-attachment-id="${attachmentId}"]`)
    if (stripAtt) stripAtt.remove()

    // 3. Update file count label
    const stripRow = container.querySelector(".border-t.border-gray-200.mt-2")
    if (stripRow) {
      const remaining = stripRow.querySelectorAll("[data-attachment-id]")
      if (remaining.length === 0) {
        stripRow.remove()
      } else {
        const countEl = stripRow.querySelector(".text-\\[13px\\].text-gray-500.font-medium")
        if (countEl) countEl.textContent = `${remaining.length} file${remaining.length !== 1 ? "s" : ""}`
      }
    }

    // 4. Update internal arrays
    if (this._allAttachments) {
      this._allAttachments = this._allAttachments.filter(a => a.id !== Number(attachmentId))
    }
    if (this._allImageAttachments) {
      this._allImageAttachments = this._allImageAttachments.filter(a => a.id !== Number(attachmentId))
    }
    if (this._conversationAttachments) {
      this._conversationAttachments = this._conversationAttachments.filter(a => a.id !== Number(attachmentId))
    }
  }

  // ── Chat delete confirmation modal ─────────────────────────────────

  _showChatDeleteConfirm(message, onConfirm) {
    // Remove any previous modal to avoid stacked listeners
    if (this.hasChatDeleteModalTarget) this.chatDeleteModalTarget.remove()

    const modal = document.createElement("div")
    modal.dataset.actionItemsTarget = "chatDeleteModal"
    modal.className = "fixed inset-0 z-[2147483005] flex items-center justify-center bg-black/50"
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Confirm</h3>
          <p class="text-sm text-gray-600 mb-6">${this._escapeHtml(message)}</p>
          <div class="flex justify-end gap-2">
            <button type="button" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer" data-role="cancel">Cancel</button>
            <button type="button" class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer" data-role="confirm">Delete</button>
          </div>
        </div>
      </div>`
    this.element.appendChild(modal)

    const hide = () => { modal.remove() }

    // Backdrop click closes
    modal.addEventListener("click", (e) => { if (e.target === modal) hide() })
    modal.querySelector('[data-role="cancel"]').addEventListener("click", hide)
    modal.querySelector('[data-role="confirm"]').addEventListener("click", () => {
      hide()
      onConfirm()
    })
  }

  // ── Comment ────────────────────────────────────────────────────────

  commentKeydown(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  autoGrowTextarea() {
    const el = this.commentInputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 156) + "px"
    this._updateSendButtonState()
  }

  // Legacy alias
  sendComment() { this.sendMessage() }

  async sendMessage(options = {}) {
    if (this._activeItemResolved) {
      this._showToast(this.i18nReopenToCommentValue, "alert")
      return
    }
    if (!this._activeItemId) return

    const input = this.commentInputTarget
    const text = input.value.trim()
    const hasFiles = this._stagedFiles.length > 0
    const hasBic = this._stagedBic && this._stagedBic.length > 0
    const hasAction = this._stagedAction !== null
    const hasTargetDate = this._stagedTargetDate !== null

    // Nothing to send
    if (!text && !hasFiles && !hasBic && !hasAction && !hasTargetDate) return

    // Files still uploading
    if (this._stagedFiles.some(f => f.uploading)) {
      this._showDrawerToast("Please wait for uploads to finish")
      return
    }

    // Larry's sheet row 48 (2026-09-08): with the company setting on, an
    // update that doesn't move Ball in Court first asks who has the ball —
    // both pop-up buttons then send, the pick riding along with the message.
    // Larry 09-08 call: the same pop-up also asks whether the STATUS changed
    // when it wasn't touched (ball changed but not status → status only;
    // both changed → no pop-up).
    const askBic = !hasBic
    const askStatus = !hasAction && !!this._canChangeStatus
    if (this.promptBicValue && !options.bicPromptDone && !this._activeItemResolved && (askBic || askStatus)) {
      this._openBicPrompt({ askBic, askStatus })
      return
    }

    this.sendBtnTarget.disabled = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const body = {}
      if (text) body.comment = text
      if (hasFiles) body.files = this._stagedFiles.map(f => ({ s3_key: f.s3Key, filename: f.filename, content_type: f.contentType }))
      if (hasBic) body.ball_in_court = this._stagedBic
      if (hasAction) body.kind_action = { punchlist_kind_action_id: this._stagedAction.id }
      if (hasTargetDate) body.target_date = this._stagedTargetDate
      // Silent status change: skip emails + push when the user turned off "Notify participants"
      const sentSilently = hasAction && this._notifyParticipants === false
      if (options.skipNotifications || sentSilently) body.skip_notifications = true

      const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}/send_message`)
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify(body)
      })

      if (response.ok) {
        const result = await response.json()

        // Clear textarea
        input.value = ""
        input.style.height = "auto"

        // Clear staged files
        this._stagedFiles = []
        if (this.hasStagedFilesContainerTarget) {
          this.stagedFilesContainerTarget.innerHTML = ""
          this.stagedFilesContainerTarget.classList.add("hidden")
        }

        // Clear staged BIC
        const hadBic = hasBic
        const bicData = hadBic ? [...this._stagedBic] : null
        this._stagedBic = null
        if (this.hasStagedBicContainerTarget) {
          this.stagedBicContainerTarget.classList.add("hidden")
          if (this.hasStagedBicChipsTarget) this.stagedBicChipsTarget.innerHTML = ""
        }

        // Clear staged action
        const hadAction = hasAction
        this._stagedAction = null
        this._notifyParticipants = true
        if (this.hasStagedActionContainerTarget) {
          this.stagedActionContainerTarget.classList.add("hidden")
          this.stagedActionContainerTarget.innerHTML = ""
        }

        // Clear staged target date
        this._stagedTargetDate = null
        if (this.hasStagedTargetDateContainerTarget) {
          this.stagedTargetDateContainerTarget.classList.add("hidden")
        }

        this._updateSendButtonState()

        // Append messages inline (no full reload)
        if (result.comment_message) {
          result.comment_message.user_avatar_url = this._currentUserAvatarUrl || null
          this._appendMessage(result.comment_message)
        } else if (text && !result.comment_message) {
          this._appendMessage({
            type: "comment",
            comment: text,
            user_id: this.currentUserIdValue,
            user_name: this.currentUserNameValue,
            user_initials: this.currentUserInitialsValue,
            user_avatar_url: this._currentUserAvatarUrl || null,
            created_at: new Date().toISOString(),
            attachments: []
          })
        }

        if (result.bic_message) {
          this._appendMessage(result.bic_message)
        }

        if (result.target_date_message) {
          this._appendMessage(result.target_date_message)
        }

        if (hadBic && result.row_update) {
          const serverBic = result.row_update.ball_in_court || []
          const enriched = this._enrichBicFromMembers(serverBic)
          this._updateDetailBicChips(enriched, result.row_update.display_status)
        }

        // Update detail header due date if target date changed
        if (hasTargetDate && result.row_update) {
          this._updateDetailTargetDate(result.row_update.target_date)
          // Update the in-memory item
          const item = this._allItems.find(i => i.id === this._activeItemId)
          if (item) {
            item.target_date = result.row_update.target_date
            item.days_overdue = result.row_update.days_overdue
            item.is_delayed = result.row_update.is_delayed
          }
        }

        if (result.row_update) this._updateTableRow(this._activeItemId, result.row_update)

        if (result.pending_send && result.pending_send.anchor) {
          // 25 seconds to undo before anyone is emailed (Phelipe 2026-10-26);
          // the grey "Emailed to ..." line takes the bar's place after.
          this._showUndoBar(result.pending_send, { text, emailLine: result.email_line, notifiedNames: result.notified_names || [],
                                                   entryIds: [ result.comment_message?.id, result.bic_message?.id, result.target_date_message?.id ] })
        } else if (result.notified_names && result.notified_names.length > 0) {
          // The grey "Emailed to ..." line under the message, right away
          // (Phelipe 2026-09-29); the full one, with its card, follows once
          // the emails are out.
          if (result.email_anchor && result.email_line) {
            this._showPendingEmailLine(result.email_anchor, result.email_line)
            this._scheduleEmailActivityRefresh()
          } else {
            this._appendNotifiedNote(result.notified_names)
          }
          this._showDrawerToast(this.i18nNotifiedValue)
        } else if (sentSilently) {
          this._showDrawerToast(this.i18nNotifiedSilentlyValue)
        }

        // If action changed status, update header status chip + footer state
        if (hadAction && result.has_action && result.row_update) {
          const ru = result.row_update
          const ds = ru.display_status
          // Update the status chip in the detail header
          if (this.hasDetailStatusTarget && ds) {
            this.detailStatusTarget.innerHTML = this._statusChip(ds, ru.approved_type, ru.action_name, ru.latest_action)
          }
          this._activeItemResolved = ds === "resolved"
          this._updateCommentInputState()
          this._updateSendButtonState()
          // Show/hide Reopen button
          if (this.hasReopenBtnTarget) {
            if (this._activeItemResolved) {
              this.reopenBtnTarget.classList.remove("hidden")
            } else {
              this.reopenBtnTarget.classList.add("hidden")
            }
          }
          // Update header BIC chips to reflect new status (clears on resolve)
          const serverBic = ru.ball_in_court || []
          const enriched = this._enrichBicFromMembers(serverBic)
          this._updateDetailBicChips(enriched, ds)
        }
      } else {
        // Surface server-side rejections (e.g. permission errors from
        // PunchlistService) instead of failing silently.
        const err = await response.json().catch(() => null)
        if (err?.error) this._showToast(err.error, "alert")
      }
    } catch (e) {
      console.error("[ActionItems] sendMessage error:", e)
    } finally {
      this.sendBtnTarget.disabled = false
      this._updateSendButtonState()
    }
  }

  // ── Kind filter loading ────────────────────────────────────────────

  _loadKinds() {
    if (this._kindsLoaded || !this.kindUrlValue) return
    this._kindsLoaded = true

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.kindUrlValue, {
      headers: { "Accept": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin"
    })
      .then(r => r.json())
      .then(data => {
        const kinds = data.kinds
        const total = data.total
        this._kinds = kinds
        if (!this.hasKindTabsContainerTarget) return

        const savedKind = this._filters.kind_id || ""

        // Add count badge to "All" tab
        const allBtn = this.kindTabsContainerTarget.querySelector('button[data-kind-id=""]')
        if (allBtn && total > 0) {
          const allBadge = document.createElement("span")
          const allIsActive = !savedKind
          allBadge.className = `inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[13px] font-semibold ${allIsActive ? "bg-blue-100 text-blue-600" : "bg-gray-200 text-gray-600"}`
          allBadge.dataset.countBadge = "true"
          allBadge.textContent = total
          allBtn.classList.add("inline-flex", "items-center", "gap-1.5")
          allBtn.appendChild(allBadge)
        }

        kinds.forEach(kind => {
          const btn = document.createElement("button")
          btn.type = "button"
          const isActive = String(kind.id) === savedKind
          btn.className = isActive
            ? "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer transition-colors bg-white text-brand font-bold"
            : "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer transition-colors bg-white text-text-muted font-medium hover:text-gray-700"
          btn.dataset.kindId = String(kind.id)
          btn.dataset.action = "click->action-items#selectKindTab"

          btn.textContent = kind.name

          if (kind.count > 0) {
            const badge = document.createElement("span")
            badge.className = `inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[13px] font-semibold ${isActive ? "bg-blue-100 text-blue-600" : "bg-gray-200 text-gray-600"}`
            badge.dataset.countBadge = "true"
            badge.textContent = kind.count
            btn.appendChild(badge)
          }

          this.kindTabsContainerTarget.appendChild(btn)
        })
        this._applySubmittalColumns()
        // Deactivate "All" tab if a saved kind is active
        if (savedKind) {
          if (allBtn) {
            allBtn.classList.remove("text-brand", "font-bold")
            allBtn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
          }
        }
      })
      .catch(e => console.error("[ActionItems] loadKinds error:", e))
  }

  // ── Internal ───────────────────────────────────────────────────────

  _updateFilterBadge() {
    let count = 0

    // Status: active if any chip selected
    if (this.hasStatusChipsTarget) {
      const statusActive = [...this.statusChipsTarget.querySelectorAll("button")].filter(b => b.classList.contains("bg-brand/10"))
      if (statusActive.length > 0) count++
    }

    // Date: active if any date range set
    if (this._filters.date_from || this._filters.date_to) count++

    // Priority: active if any chip selected
    if (this.hasPriorityChipsTarget) {
      const critActive = [...this.priorityChipsTarget.querySelectorAll("button")].filter(b => b.classList.contains("bg-brand/10"))
      if (critActive.length > 0) count++
    }

    if (this.hasFilterBadgeTarget) {
      if (count > 0) {
        this.filterBadgeTarget.textContent = count
        this.filterBadgeTarget.classList.remove("hidden")
        this.filterBadgeTarget.classList.add("inline-flex")
      } else {
        this.filterBadgeTarget.classList.add("hidden")
        this.filterBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Show/hide clear row
    if (this.hasClearFiltersRowTarget) {
      this.clearFiltersRowTarget.classList.toggle("hidden", count === 0)
    }
  }

  _resetAndLoad() {
    this._page = 1
    this._isLastPage = false
    this._rowIndex = 0
    this._itemIds = []
    this._allItems = []
    if (this.hasTableBodyTarget) this.tableBodyTarget.innerHTML = ""
    this._loadPage(1)
  }

  async _loadPage(page) {
    if (this._loading || this._isLastPage) return
    this._loading = true


    // Lazy-load kinds on first page load
    if (page === 1 && !this._kindsLoaded) {
      this._loadKinds()
    }

    try {
      const url = new URL(this.itemsUrlValue, window.location.origin)
      url.searchParams.set("page", page)

      if (this._filters.search) url.searchParams.set("search", this._filters.search)
      if (this._filters.status) {
        this._filters.status.forEach(s => url.searchParams.append("status[]", s))
      }
      if (this._filters.kind_id) {
        url.searchParams.set("kind_id", this._filters.kind_id)
      }
      if (this._filters.critical_level) {
        this._filters.critical_level.forEach(c => url.searchParams.append("critical_level[]", c))
      }
      if (this._filters.date_from) url.searchParams.set("date_from", this._filters.date_from)
      if (this._filters.date_to) url.searchParams.set("date_to", this._filters.date_to)

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")

      // Render table rows
      data.items.forEach(item => {
        this._allItems.push(item)
        this._itemIds.push(String(item.id))
        if (!this._sort.key) {
          this.tableBodyTarget.insertAdjacentHTML("beforeend", this._renderRow(item))
        }
      })
      if (this._sort.key) this._reRenderTable()

      // Handle empty state
      if (page === 1 && data.items.length === 0) {
        this.emptyTarget.classList.remove("hidden")
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.add("hidden")
        if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
      }

      this._isLastPage = data.is_last_page
      this._page = page + 1

      // Deep link
      if (page === 1 && this._pendingDeepLinkId) {
        const id = this._pendingDeepLinkId
        this._pendingDeepLinkId = null
        requestAnimationFrame(() => this._openDetail(id))
      }

      // Infinite scroll sentinel
      if (!this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.remove("hidden")
        this._observer.observe(this.sentinelTarget)
      } else if (this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
        this._observer.disconnect()
      }
    } catch (error) {
      console.error("[ActionItems] Failed to load page:", error)
      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")
    } finally {
      this._loading = false
    }
  }

  // ── Table row rendering ────────────────────────────────────────────

  _renderRow(item) {
    this._rowIndex++
    const muted = item.display_status === "resolved"
    const rowBg = muted ? "bg-gray-50" : "bg-white"
    const cell = "px-2 xl:px-3 py-1.5 xl:py-2 whitespace-nowrap"

    const typeCode = this._typeCodeBadge(item.kind_name, item.sequential, item.critical_level, muted)
    const submittalCols = this._submittalColumnsOn()
    // A saved draft (Larry 2026-09-08) reads "Draft" instead of a status.
    const statusChip = item.draft
      ? '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-amber-100 text-amber-700">Draft</span>'
      : this._statusChip(item.display_status, item.approved_type, item.action_name, item.latest_action)
    const draftTag = item.draft ? ' <span class="ml-1 inline-flex items-center px-1.5 py-px rounded text-[13px] font-medium bg-amber-100 text-amber-700 uppercase tracking-wide leading-tight">Draft</span>' : ""
    const dueDateHtml = this._dueDateHtml(item)
    const msgBadge = this._messageBadge(item.unread_count, item.attached_messages_sum)
    // Closed items grey out but are NOT struck through (Phelipe 2026-09-01)
    // — the strikeout read as "cancelled" on work that was actually done.
    const titleColor = muted ? "text-gray-400" : "text-gray-700"
    const versionSuffix = item.version ? ` <span class="text-gray-400 font-normal">v${item.version}</span>` : ""
    // Came in from Procore (Colin Simons 2026-10-06): Procore's mark before the title, as on Reports.
    // "3 of 6" when the item carries a checklist (K3-38).
    const cl = item.checklist
    const checklistChip = cl && cl.total > 0
      ? ` <span class="ml-1 inline-flex items-center gap-1 align-middle text-[12px] ${muted ? "text-gray-400" : "text-gray-500"}" title="Checklist: ${cl.done} of ${cl.total} done"><span class="inline-block w-10 h-1.5 rounded-full bg-gray-200 overflow-hidden"><span class="block h-full bg-emerald-500" style="width:${Math.round((cl.done / cl.total) * 100)}%"></span></span>${cl.done} of ${cl.total}</span>`
      : ""
    const titleSpan = `<span class="truncate block" data-truncate-tip>${this._escapeHtml(item.title)}${versionSuffix}${draftTag}${checklistChip}</span>`
    const titleHtml = item.from_procore
      ? `<span class="flex items-center min-w-0"><img src="/procore-icon.png" class="h-[18px] opacity-50 shrink-0 mr-1 -my-0.5" style="width:auto" alt="" title="${this._escapeHtml(this.i18nFromProcoreValue)}">${titleSpan}</span>`
      : titleSpan

    let bicTd, approverTd
    if (muted && item.resolved_by) {
      const resolvedBy = item.resolved_by
      const rawName = resolvedBy.name || "Unknown"
      const displayName = this._escapeHtml(rawName)
      const initials = this._escapeHtml(resolvedBy.initials || rawName.split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2))
      const fallbackBg = "bg-gray-100 text-gray-400"
      const avatar = resolvedBy.avatar_url && !resolvedBy.avatar_url.includes("avatar.png")
        ? `<span class="relative w-7 h-7 shrink-0"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium absolute inset-0">${initials}</span><img src="${this._escapeHtml(resolvedBy.avatar_url)}" class="w-7 h-7 rounded-full object-cover absolute inset-0 opacity-0 transition-opacity duration-300" data-controller="img-handler" data-img-handler-opacity-value="0.4" data-action="load->img-handler#revealOverInitials error->img-handler#removeSelf"></span>`
        : `<span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium shrink-0">${initials}</span>`
      const action = item.approved_type === "closed" ? "closed" : "approved"
      const resolvedChip = `<span class="inline-flex items-center gap-1 rounded-full pl-px pr-2 py-px bg-gray-50 text-gray-400 text-[13px] italic">${avatar} ${displayName} ${action}</span>`
      bicTd = `<td class="${cell}"></td>`
      approverTd = `<td class="${cell} col-approvers">${resolvedChip}</td>`
    } else {
      const bicChips = this._bicChips(item.ball_in_court || [], item.display_status)
      const approverChips = this._approverChips(item.approvers || [], muted)
      bicTd = `<td class="${cell}">${bicChips}</td>`
      approverTd = `<td class="${cell} col-approvers">${approverChips}</td>`
    }

    // Each column's cell by key; the company's order says which show, and where (Studio step 4).
    const cells = {
      type: `<td class="${cell}">${typeCode}</td>`,
      spec: `<td class="${cell} ${submittalCols ? "" : "hidden"} col-submittal ${titleColor}">${this._escapeHtml(item.spec_drawings || "")}</td>`,
      title: `<td class="${cell} ${titleColor} max-w-0">${titleHtml}</td>`,
      package: `<td class="${cell} ${submittalCols ? "" : "hidden"} col-submittal ${titleColor}">${this._escapeHtml(item.package || "")}</td>`,
      ball_in_court: bicTd,
      approvers: approverTd,
      author: `<td class="${cell} ${titleColor}">${this._escapeHtml(item.author_name || "")}</td>`,
      created: `<td class="${cell} ${titleColor}">${this._escapeHtml(item.created_at ? new Date(item.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "")}</td>`,
      priority: `<td class="${cell} ${titleColor}">${this._escapeHtml(item.critical_level ? item.critical_level.charAt(0).toUpperCase() + item.critical_level.slice(1) : "")}</td>`
    }
    const order = this.hasColumnsValue && this.columnsValue.length ? this.columnsValue : ["type", "spec", "title", "package", "ball_in_court", "approvers", "status", "due_date", "messages"]
    const cf = item.custom_fields || {}
    const tds = order.map((key) => {
      if (key.startsWith("cf_")) return `<td class="${cell} ${titleColor}">${this._escapeHtml(cf[key.slice(3)] || "")}</td>`
      if (key === "status") return `<td class="${cell}">${statusChip}${item.pco_seq ? ` <span class="ml-1 inline-flex items-center px-1.5 py-px rounded-full text-[13px] font-medium bg-purple-50 text-purple-700 border border-purple-200" title="A PCO draft was made from this item">PCO #${item.pco_seq}</span>` : ""}</td>`
      if (key === "due_date") return `<td class="${cell}">${dueDateHtml}</td>`
      if (key === "messages") return `<td class="${cell} text-center">${msgBadge}</td>`
      return cells[key] || ""
    }).join("")
    return `<tr class="${rowBg} border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04]"
                data-action="click->action-items#openItem"
                data-item-id="${item.id}">${tds}</tr>`
  }


  _statusChip(status, approvedType, actionName, latestAction) {
    if (status === "resolved") {
      return this._approvalChip(approvedType, actionName)
    }
    // Show latest action for non-resolved items
    if (latestAction) {
      return this._latestActionChip(latestAction)
    }
    return `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium border border-gray-300 bg-white text-gray-600">${this.i18nOpenValue}</span>`
  }

  _latestActionChip(action) {
    const checkSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
    const xSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>`
    const reopenSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3"/></svg>`
    const actionSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>`
    const versionSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0-6.75 6.75M12 4.5l6.75 6.75"/></svg>`

    let label, icon, color

    if (action.action_name) {
      label = this._escapeHtml(action.action_name)
      const nameLower = action.action_name.toLowerCase()
      if (nameLower.includes("reject")) {
        icon = xSvg; color = "#dc2626"
      } else if (action.action_closes_item) {
        icon = checkSvg; color = "#16a34a"
      } else if (action.action_increments_version) {
        icon = versionSvg; color = "#2563eb"
      } else {
        icon = actionSvg; color = "#d97706"
      }
    } else if (action.approval_event === "approved") {
      const types = {
        approved: { label: this.i18nApprovedValue, color: "#16a34a" },
        approved_as_noted: { label: "Approved as Noted", color: "#16a34a" },
        approved_and_resubmit: { label: "Revise & Resubmit", color: "#2563eb" },
        no_exceptions: { label: "No Exceptions", color: "#8A2BE2" },
        closed: { label: this.i18nClosedValue, color: "#16a34a" }
      }
      const t = types[action.approved_type] || types.approved
      label = t.label; color = t.color
      icon = action.approved_type === "approved_and_resubmit" ? versionSvg : checkSvg
    } else if (action.approval_event === "reopened") {
      label = this.i18nReopenedValue; icon = reopenSvg; color = "#d97706"
    } else if (action.approval_event === "closed") {
      label = this.i18nClosedValue; icon = checkSvg; color = "#16a34a"
    } else {
      return `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium border border-gray-300 bg-white text-gray-600">${this.i18nOpenValue}</span>`
    }

    return `<span class="inline-flex items-center gap-0.5 rounded px-1.5 py-px text-[13px] font-medium bg-white" style="color:${color};border:1px solid ${color};">${icon} ${label}</span>`
  }

  _approvalChip(approvedType, actionName) {
    // Resolved items use the same color/icon rules as _latestActionChip and the chat
    const checkSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
    const xSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>`
    const versionSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0-6.75 6.75M12 4.5l6.75 6.75"/></svg>`
    const actionSvg = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>`

    let label, icon, color

    if (actionName) {
      label = this._escapeHtml(actionName)
      const nameLower = actionName.toLowerCase()
      if (nameLower.includes("reject")) {
        icon = xSvg; color = "#dc2626"
      } else {
        icon = checkSvg; color = "#16a34a"
      }
    } else {
      const types = {
        approved: { label: this.i18nApprovedValue, color: "#16a34a", icon: checkSvg },
        approved_as_noted: { label: "Approved as Noted", color: "#16a34a", icon: checkSvg },
        approved_and_resubmit: { label: "Revise & Resubmit", color: "#2563eb", icon: versionSvg },
        no_exceptions: { label: "No Exceptions", color: "#8A2BE2", icon: checkSvg },
        closed: { label: this.i18nClosedValue, color: "#16a34a", icon: checkSvg }
      }
      const t = types[approvedType] || types.approved
      label = t.label; color = t.color; icon = t.icon
    }

    return `<span class="inline-flex items-center gap-0.5 rounded px-1.5 py-px text-[13px] font-medium bg-white" style="color:${color};border:1px solid ${color};">${icon} ${label}</span>`
  }

  _priorityBadge(level, muted) {
    if (level === "high") {
      const color = muted ? "text-gray-300" : "text-red-500"
      return `<svg class="w-5 h-5 ${color} shrink-0 translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>`
    }
    if (level === "medium") {
      const color = muted ? "text-gray-300" : "text-amber-500"
      return `<svg class="w-5 h-5 ${color} shrink-0 translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>`
    }
    return ""
  }

  _typeCodeBadge(kindName, sequential, criticalLevel, muted) {
    if (!kindName) return this._priorityBadge(criticalLevel, muted)
    const seqNum = sequential ? ` ${String(sequential).padStart(3, "0")}` : ""
    const label = `${this._escapeHtml(kindName)}${seqNum}`
    const priority = this._priorityBadge(criticalLevel, muted)
    const badgeColor = muted ? "text-gray-400 border border-gray-200" : "text-gray-600 border border-gray-300"
    return `<span class="inline-flex items-center gap-1.5 whitespace-nowrap"><span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-semibold uppercase bg-white ${badgeColor}">${label}</span>${priority}</span>`
  }

  _bicChips(bic, displayStatus) {
    if (!bic || bic.length === 0) return '<span class="text-[13px] text-gray-400">—</span>'

    const MAX_VISIBLE = 1
    const visible = bic.slice(0, MAX_VISIBLE)
    const overflow = bic.slice(MAX_VISIBLE)
    const muted = displayStatus === "resolved"

    const chips = visible.map(b => {
      const isEmailOnly = !b.name || (b.email && b.name === b.email)
      const rawName = isEmailOnly ? (b.email || "?") : b.name
      const displayName = this._escapeHtml(isEmailOnly ? rawName.split("@")[0] : rawName)
      const initials = this._escapeHtml(b.initials || "?")
      const isCurrentUser = b.id === this.currentUserIdValue && !muted
      const chipBg = muted ? "bg-gray-50 text-gray-400" : "bg-gray-100 text-gray-700"
      const fallbackBg = muted ? "bg-gray-100 text-gray-400" : "bg-gray-200 text-gray-500"
      const fallback = `<span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium shrink-0">${initials}</span>`
      const avatar = b.avatar_url && !b.avatar_url.includes("avatar.png")
        ? `<span class="relative w-7 h-7 shrink-0"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium absolute inset-0">${initials}</span><img src="${this._escapeHtml(b.avatar_url)}" class="w-7 h-7 rounded-full object-cover absolute inset-0 opacity-0 transition-opacity duration-300" data-controller="img-handler" data-img-handler-opacity-value="${muted ? '0.4' : '1'}" data-action="load->img-handler#revealOverInitials error->img-handler#removeSelf"></span>`
        : fallback
      const yourTurn = isCurrentUser ? ' <span class="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 ring-1 ring-amber-300 px-1.5 py-0.5 rounded-full leading-none translate-y-px">Your Turn</span>' : ''
      return `<span class="inline-flex items-center gap-1 rounded-full pl-px pr-2 py-px ${chipBg} text-[12px] font-medium">${avatar} ${displayName}${yourTurn}</span>`
    }).join("")

    let overflowChip = ""
    if (overflow.length > 0) {
      const names = overflow.map(b => {
        const isEmailOnly = !b.name || (b.email && b.name === b.email)
        return isEmailOnly ? (b.email || "?").split("@")[0] : (b.name || "?")
      }).join(", ")
      const fallbackBg = muted ? "bg-gray-100 text-gray-400" : "bg-gray-200 text-gray-500"
      overflowChip = `<span class="relative group"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-bold shrink-0 cursor-default">+${overflow.length}</span><span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-gray-800 text-white text-[12px] whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">${this._escapeHtml(names)}</span></span>`
    }

    return `<div class="flex flex-nowrap gap-1 items-center">${chips}${overflowChip}</div>`
  }

  _approverChips(approvers, muted) {
    if (!approvers || approvers.length === 0) return ''

    const MAX_VISIBLE = 1
    const visible = approvers.slice(0, MAX_VISIBLE)
    const overflow = approvers.slice(MAX_VISIBLE)
    const chipBg = muted ? "bg-gray-50 text-gray-400" : "bg-gray-100 text-gray-700"
    const fallbackBg = muted ? "bg-gray-100 text-gray-400" : "bg-gray-200 text-gray-500"

    const chips = visible.map(a => {
      const rawName = a.name || a.email || "?"
      const displayName = this._escapeHtml(rawName.includes("@") ? rawName.split("@")[0] : rawName)
      const initials = this._escapeHtml(a.initials || rawName.split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2))
      const avatar = a.avatar_url && !a.avatar_url.includes("avatar.png")
        ? `<span class="relative w-7 h-7 shrink-0"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium absolute inset-0">${initials}</span><img src="${this._escapeHtml(a.avatar_url)}" class="w-7 h-7 rounded-full object-cover absolute inset-0 opacity-0 transition-opacity duration-300" data-controller="img-handler" data-img-handler-opacity-value="${muted ? '0.4' : '1'}" data-action="load->img-handler#revealOverInitials error->img-handler#removeSelf"></span>`
        : `<span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium shrink-0">${initials}</span>`
      return `<span class="inline-flex items-center gap-1 rounded-full pl-px pr-2 py-px ${chipBg} text-[12px] font-medium">${avatar} ${displayName}</span>`
    }).join("")

    let overflowChip = ""
    if (overflow.length > 0) {
      const names = overflow.map(a => {
        const rawName = a.name || a.email || "?"
        return rawName.includes("@") ? rawName.split("@")[0] : rawName
      }).join(", ")
      overflowChip = `<span class="relative group"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-bold shrink-0 cursor-default">+${overflow.length}</span><span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-gray-800 text-white text-[12px] whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">${this._escapeHtml(names)}</span></span>`
    }

    return `<div class="flex flex-nowrap gap-1 items-center">${chips}${overflowChip}</div>`
  }

  _criticalDot(level) {
    if (!level) return ""
    const colors = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-emerald-500" }
    const c = colors[level]
    return c ? `<span class="w-2 h-2 rounded-full ${c} shrink-0" title="${level}"></span>` : ""
  }

  _dueDateHtml(item) {
    if (!item.target_date) return ""
    const dateStr = this._displayDate(item.target_date)

    if (item.display_status === "resolved") {
      return `<span class="text-[13px] text-gray-400">${dateStr}</span>`
    }
    if (item.days_overdue > 0) {
      const behind = item.days_overdue === 1 ? this.i18nDaysBehindOneValue : this.i18nDaysBehindManyValue.replace("%{count}", item.days_overdue)
      return `<span class="text-[13px] text-red-600 font-medium">${behind}</span>`
    }
    if (item.days_overdue !== null && item.days_overdue >= -7) {
      return `<span class="text-[13px] text-amber-600">${dateStr}</span>`
    }
    return `<span class="text-[13px] text-gray-500">${dateStr}</span>`
  }

  _messageBadge(unread, total) {
    if (!total) return ""
    const icon = `<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"/></svg>`
    if (unread > 0) {
      return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white">
        ${icon}<span class="text-[13px] font-bold">${unread}</span>
      </span>`
    }
    return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
      ${icon}<span class="text-[13px] font-medium">${total}</span>
    </span>`
  }

  // ── Detail modal ───────────────────────────────────────────────────

  async _openDetail(itemId) {
    // A draft opens its form to keep writing — there's nothing to discuss yet.
    const draftItem = (this._allItems || []).find(i => String(i.id) === String(itemId))
    if (draftItem && draftItem.draft) {
      window.location.href = `${this.baseUrlValue}/${itemId}/edit`
      return
    }
    this._activeItemId = String(itemId)
    this._modalOpen = true
    document.addEventListener("keydown", this._onKeydown)

    // Update prev/next buttons
    const idx = this._itemIds.indexOf(this._activeItemId)
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = idx <= 0
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = idx >= this._itemIds.length - 1

    // Show modal with animation
    this.detailModalTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.detailBackdropTarget.classList.remove("opacity-0")
      this.detailPanelTarget.classList.remove("translate-x-full")
    })

    // Show loading skeleton, hide content and clear stale HTML
    this.detailLoadingTarget.classList.remove("hidden")
    this.detailContentTarget.classList.add("hidden")
    this.detailContentTarget.innerHTML = ""
    if (this.hasDetailFooterTarget) this.detailFooterTarget.classList.add("hidden")

    // Update URL to /action_items/:id
    history.replaceState(null, "", `${this.baseUrlValue}/${itemId}`)

    // Subscribe to ActionCable channel for real-time updates
    this._subscribeChannel(itemId)

    // AI Radar: reset for this item and check it in the background so the
    // badge count is ready before the panel is even opened.
    this._aiItemId = itemId
    this.closeAiPanel()
    this._fetchAiRadar(itemId, false)

    // Load timeline
    this._loadTimeline(itemId)
  }

  async _loadTimeline(itemId) {
    if (!itemId || itemId === "null" || itemId === "undefined") return

    // Clear staged compose state when switching items
    this._clearStagedState()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const url = this.itemsUrlValue.replace(".json", `/${itemId}/timeline`)
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (response.status === 404) {
        this._closeModal()
        this._showToast("This action item was deleted or no longer exists.", "alert")
        return
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      this._renderTimeline(data)
    } catch (e) {
      console.error("[ActionItems] loadTimeline error:", e)
      this.detailContentTarget.innerHTML = `<div class="p-6 text-center text-gray-400">Failed to load details</div>`
      this.detailLoadingTarget.classList.add("hidden")
      this.detailContentTarget.classList.remove("hidden")
    }
  }

  // ── AI Radar (checks the item against project specs & drawings) ──
  toggleAiPanel() {
    if (!this.hasAiPanelTarget) return
    this.aiPanelTarget.classList.contains("hidden") ? this.openAiPanel() : this.closeAiPanel()
  }

  openAiPanel() {
    this.aiPanelTarget.classList.remove("hidden")
    this.aiPanelTarget.classList.add("flex")
  }

  closeAiPanel() {
    if (!this.hasAiPanelTarget) return
    this.aiPanelTarget.classList.add("hidden")
    this.aiPanelTarget.classList.remove("flex")
  }

  rerunAiRadar() {
    if (this.hasAiRadarListTarget) {
      this.aiRadarListTarget.innerHTML = `<p class="text-[12px] text-gray-400">Re-checking this item against your specs & drawings…</p>`
    }
    this._fetchAiRadar(this._aiItemId, true)
  }

  async _fetchAiRadar(itemId, force) {
    if (!itemId || !this.hasAiRadarListTarget) return
    if (this.hasAiBadgeTarget) {
      this.aiBadgeTarget.classList.add("hidden")
      this.aiBadgeTarget.classList.remove("inline-flex")
    }
    if (!force) {
      this.aiRadarListTarget.innerHTML = `<p class="text-[12px] text-gray-400">Checking this item against your specs & drawings…</p>`
    }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const url = this.itemsUrlValue.replace(".json", `/${itemId}/ai_cross_check${force ? "?force=true" : ""}`)
      const resp = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      if (String(itemId) !== String(this._aiItemId)) return // switched items mid-flight
      this._renderAiRadar(Array.isArray(data.findings) ? data.findings : [])
    } catch (e) {
      if (String(itemId) !== String(this._aiItemId)) return
      this.aiRadarListTarget.innerHTML = `<p class="text-[12px] text-gray-400">Couldn't run the AI check — use “Re-run check” to try again.</p>`
    }
  }

  _renderAiRadar(findings) {
    // Badge on the AI Radar button, e.g. "AI Radar (3)"
    if (this.hasAiBadgeTarget) {
      this.aiBadgeTarget.textContent = String(findings.length)
      this.aiBadgeTarget.classList.toggle("hidden", findings.length === 0)
      this.aiBadgeTarget.classList.toggle("inline-flex", findings.length > 0)
    }
    if (!this.hasAiRadarListTarget) return
    if (findings.length === 0) {
      this.aiRadarListTarget.innerHTML = `<div class="border border-green-200 bg-green-50 rounded-lg p-2.5 text-[12px] text-green-800">Nothing flagged — no spec or drawing requirements appear to conflict with this item.</div>`
      return
    }
    const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
    const styles = {
      high:   { box: "border-red-200 bg-red-50",     title: "text-red-700" },
      medium: { box: "border-amber-200 bg-amber-50", title: "text-amber-700" },
      info:   { box: "border-gray-200 bg-gray-50",   title: "text-gray-700" }
    }
    this.aiRadarListTarget.innerHTML = findings.map(f => {
      const st = styles[f.severity] || styles.info
      // Link the citation to the Specs/Drawings page — it filters to the cited
      // document and opens it in the viewer at the cited page with the quoted
      // passage highlighted (ai_hl). Same-tab on purpose: ai_return brings the
      // user back to this action item when they close the viewer.
      let src = ""
      if (f.link && f.link.id) {
        const base = f.link.kind === "drawing" ? "drawings" : "specifications"
        const back = encodeURIComponent(`${this.baseUrlValue}/${this._aiItemId}`)
        const href = `/projects/${this.projectIdValue}/${base}?ai_open=${f.link.id}&ai_page=${f.link.page || 1}` +
                     `&ai_q=${encodeURIComponent(f.link.q || "")}&ai_hl=${encodeURIComponent(f.quote || "")}&ai_return=${back}`
        src = `<a href="${href}" onclick="event.stopPropagation()"
                  class="inline-flex items-center gap-1 text-[11px] font-medium text-purple-700 hover:underline mt-1">${esc(f.source || "Open source")} →</a>`
      } else if (f.source) {
        src = `<div class="text-[11px] text-gray-400 mt-1">${esc(f.source)}</div>`
      }
      return `<div class="border ${st.box} rounded-lg p-2.5">
          <div class="text-[12px] font-medium ${st.title}">${esc(f.title)}</div>
          <div class="text-[12px] text-gray-600 mt-0.5">${esc(f.detail)}</div>
          ${src}
        </div>`
    }).join("")
  }

  // The company's own fields on the item (Studio step 3, 2026-10-19): "Label: value" in the header.
  _customFieldsHtml(pl) {
    const rows = Array.isArray(pl?.custom_fields) ? pl.custom_fields.filter((r) => r && r.label) : []
    if (!rows.length) return ""
    return `<div class="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-gray-700" data-custom-fields-show>${rows.map((r) => `<span><span class="font-medium text-gray-500">${this._escapeHtml(r.label)}:</span> ${this._escapeHtml(r.value || "—")}</span>`).join("")}</div>`
  }

  _renderTimeline(data) {
    const pl = data.punchlist
    const timeline = data.timeline || []
    this._activeItemCreatorId = pl.author?.id || null
    // § 3.5 Tier 5 — keep the rendered punchlist accessible to the
    // ACC sync handlers (Send to ACC, Resolve Conflict). Cleared when
    // the slide-over closes via `closeDetail`.
    this._currentDetail = pl
    // SLC3-11 — the item's target-date email reminders (the Reminders chip).
    this._reminders = data.reminders || null

    // Update header links
    if (this.hasEditLinkTarget) {
      this.editLinkTarget.href = pl.edit_url || "#"
      this.editLinkTarget.style.display = data.can_edit ? "inline-flex" : "none"
    }
    if (this.hasPdfLinkTarget) {
      this.pdfLinkTarget.href = pl.pdf_url || "#"
    }
    if (this.hasDeleteBtnTarget) {
      // can_delete covers authors plus owners when the company setting
      // "Allow Owners to Delete Any Action Item" is on (falls back to can_edit).
      const canDelete = data.can_delete !== undefined ? data.can_delete : data.can_edit
      this.deleteBtnTarget.style.display = canDelete ? "inline-flex" : "none"
    }
    if (this.hasDetailFooterTarget) {
      this.detailFooterTarget.classList.remove("hidden")
    }

    // Build detail content
    const muted = pl.display_status === "resolved"
    const typeBadge = this._typeCodeBadge(pl.kind_name, pl.sequential, pl.critical_level, muted)

    // Derive latest action from timeline for non-resolved items
    let latestAction = null
    if (pl.display_status !== "resolved") {
      const timeline = data.timeline || []
      for (let i = timeline.length - 1; i >= 0; i--) {
        const e = timeline[i]
        if (e.approval_event || e.punchlist_kind_action_id) {
          latestAction = {
            approval_event: e.approval_event,
            approved_type: e.approved_type,
            action_name: e.action_name,
            action_closes_item: e.action_closes_item,
            action_increments_version: e.action_increments_version
          }
          break
        }
      }
    }
    const statusChip = this._statusChip(pl.display_status, pl.approved_type, pl.action_name, latestAction)

    // Update header: type badge, status, quick actions
    this.detailTitleTarget.innerHTML = typeBadge
    if (this.hasDetailStatusTarget) this.detailStatusTarget.innerHTML = statusChip
    if (this.hasDetailQuickActionsTarget) {
      this.detailQuickActionsTarget.innerHTML = ""
    }

    // Show/hide footer Reopen button and disable commenting when resolved
    this._activeItemResolved = pl.display_status === "resolved"
    if (this.hasReopenBtnTarget) {
      if (this._activeItemResolved) {
        this.reopenBtnTarget.classList.remove("hidden")
      } else {
        this.reopenBtnTarget.classList.add("hidden")
      }
    }
    this._updateCommentInputState()

    // Populate action dropdown
    this._populateActionDropdown(data, pl)

    // Store project members for BIC picker
    if (data.project_members) this._projectMembers = data.project_members

    // Show/hide BIC button (only when not resolved)
    if (this.hasBicContainerTarget) {
      if (pl.display_status !== "resolved" && this._projectMembers.length > 0) {
        this.bicContainerTarget.classList.remove("hidden")
      } else {
        this.bicContainerTarget.classList.add("hidden")
      }
    }

    // Aggregate all attachments for the strip and lightbox
    this._allImageAttachments = []
    this._allAttachments = []

    // Initial photos from punchlist.photos
    ;(pl.photos || []).forEach(p => {
      const obj = {
        id: p.id,
        photo_id: p.photo_id,
        original_url: p.url,
        optimized_url: p.optimized_url,
        thumbnail_url: p.thumb_url,
        photo_url: p.photo_url,
        filename: p.filename,
        content_type: p.content_type,
        created_at: p.created_at,
        imageable_type: "Punchlist",
        source_detail: pl.kind_name ? `${pl.kind_name} ${pl.sequential || ""}`.trim() : pl.title,
        comment_count: 0,
        annotations: p.annotations,
        rotation: p.rotation || 0,
        owner_id: p.user_id,
        user_id: p.user_id,
        user_name: pl.author?.name,
        is_image: p.is_image !== false,
        is_pdf: !!p.is_pdf
      }
      this._allAttachments.push(obj)
      if (obj.is_image) this._allImageAttachments.push(obj)
    })

    // Timeline entry attachments (skip any already added from pl.photos)
    const seenAttIds = new Set(this._allAttachments.map(a => a.id))
    ;(timeline || []).forEach(entry => {
      ;(entry.attachments || []).forEach(att => {
        if (seenAttIds.has(att.id)) return
        seenAttIds.add(att.id)
        const obj = {
          id: att.id,
          photo_id: null,
          original_url: att.url,
          optimized_url: att.optimized_url,
          thumbnail_url: att.thumb_url,
          photo_url: att.photo_url,
          filename: att.filename,
          content_type: att.content_type,
          created_at: entry.created_at,
          imageable_type: "Punchlist",
          source_detail: null,
          comment_count: 0,
          annotations: att.annotations || null,
          rotation: 0,
          owner_id: entry.user_id,
          user_id: entry.user_id,
          user_name: entry.user_name,
          is_image: att.is_image,
          is_pdf: !!att.is_pdf
        }
        this._allAttachments.push(obj)
        if (att.is_image) this._allImageAttachments.push(obj)
      })
    })

    // Separate attachments into creation vs conversation for PDF merge modal
    this._creationAttachments = (pl.photos || []).map(p => ({
      id: p.id, filename: p.filename, is_pdf: !!p.is_pdf, is_image: p.is_image !== false,
      mergeable: !!p.is_pdf || (p.is_image !== false)
    }))
    const creationIds = new Set(this._creationAttachments.map(a => a.id))
    this._conversationAttachments = []
    ;(timeline || []).forEach(entry => {
      ;(entry.attachments || []).forEach(att => {
        if (creationIds.has(att.id)) return
        this._conversationAttachments.push({
          id: att.id, filename: att.filename, is_pdf: !!att.is_pdf, is_image: !!att.is_image,
          mergeable: !!att.is_pdf || !!att.is_image
        })
      })
    })

    // People chips (inline, compact)
    const bicChips = (pl.ball_in_court || []).map(b => {
      let rawName = b.name || b.initials || "?"
      if (rawName.includes("@")) rawName = rawName.split("@")[0]
      const name = this._escapeHtml(rawName)
      const isMe = b.id === this.currentUserIdValue && !muted
      const cls = isMe ? "bg-white text-gray-700 border-gray-300" : "bg-white text-gray-700 border-gray-300"
      const yourTurn = isMe ? ` <span class="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 ring-1 ring-amber-300 px-1.5 py-0.5 rounded-full leading-none">Your Turn</span>` : ""
      return `<span class="inline-flex items-center gap-1.5 px-1.5 py-px rounded border text-[13px] font-medium ${cls}">${name}${yourTurn}</span>`
    }).join("")
    const approverChips = (pl.approvers || []).map(a => {
      const raw = a.name || a.email || "?"
      const label = raw.includes("@") ? raw.split("@")[0] : raw
      return `<span class="inline-flex items-center px-1.5 py-px rounded border border-gray-300 text-[13px] font-medium bg-white text-gray-700">${this._escapeHtml(label)}</span>`
    }).join("")
    // Metadata row beside the title: "Due … · added by …"; the Reminders link
    // sits at the right end of the people row below (Phelipe 2026-09-29).
    const authorHtml = pl.author
      ? `<span data-detail-author><span class="text-gray-500">${this.i18nAddedByValue}&nbsp;</span><span class="text-gray-800">${this._escapeHtml(pl.author.name)}</span></span>`
      : ""
    const metaRow = `<div data-detail-meta><span data-detail-due>${this._detailDueHtml(pl.target_date, !!pl.author)}</span>${authorHtml}</div>`

    // Phase 2.7 Tier 2 — "From Meeting" backref pill. Surfaces the
    // Unified Action Item Engine differentiator: the Punchlist knows
    // which meeting it came from, and the user can drill back.
    let fromMeetingPill = ""
    if (pl.from_meeting) {
      const m = pl.from_meeting
      const labelBits = [m.reference_label, m.title].filter(Boolean).join(" — ")
      const topicHint = m.topic_title ? ` · ${this._escapeHtml(m.topic_title)}` : ""
      fromMeetingPill = `<a href="${this._escapeHtml(m.url)}"
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium
        bg-violet-50 text-violet-800 border border-violet-200 hover:bg-violet-100"
        data-from-meeting>
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
        From Meeting
        <span class="font-semibold">${this._escapeHtml(labelBits)}</span>${topicHint}
      </a>`
    }

    // An RFI or submittal that came in from Procore (Colin Simons 2026-10-06),
    // with Procore's number. Same pill on public_action_item.html.erb (parity rule).
    let fromProcorePill = ""
    if (pl.from_procore) {
      const number = pl.from_procore.number ? ` <span class="font-semibold">${this._escapeHtml(pl.from_procore.number)}</span>` : ""
      fromProcorePill = `<span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[13px] font-medium bg-orange-50 text-orange-800 border border-orange-200" data-from-procore>
        <img src="/procore-icon.png" class="h-4 shrink-0" style="width:auto" alt="">${this._escapeHtml(this.i18nFromProcoreValue)}${number}</span>`
    }

    // § 3.5 Tier 4 — "From 3D" pill + a 3D Context block (thumbnail +
    // element list + Restore Viewpoint). The pill is a fast-glance
    // marker; the block lives below the metadata row and renders in
    // both the slide-over here and on `public_action_item.html.erb`
    // (CLAUDE.md parity rule). Orphan banner shows when the pinned
    // model has been superseded — the pin overlay shows a gray
    // sprite for the same condition.
    let bimContextPill = ""
    let bimContextBlock = ""
    if (pl.bim_context) {
      const bc = pl.bim_context
      // Only the superseded case warrants the orphan banner — a model
      // that's still translating is "loading," not "stale," and the
      // pin will resolve once translation finishes.
      const orphan = bc.superseded === true
      const label = bc.bim_model_name ? this._escapeHtml(bc.bim_model_name) : "3D model"
      bimContextPill = `<a href="${this._escapeHtml(bc.viewer_url)}"
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium
        bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100"
        data-bim-context-pill>
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"/></svg>
        From 3D
        <span class="font-semibold">${label}</span>
      </a>`

      const elementChips = (bc.element_external_ids || []).slice(0, 3).map(id =>
        `<code class="text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-700 truncate inline-block max-w-[10rem] align-middle">${this._escapeHtml(id)}</code>`
      ).join(" ")
      const moreElements = (bc.element_external_ids || []).length > 3 ?
        `<span class="text-[11px] text-gray-500">+${(bc.element_external_ids || []).length - 3} more</span>` : ""
      const orphanBanner = orphan ? `
        <div class="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
          3D context references a model version that's been replaced. The pin may not resolve in the latest model.
        </div>
      ` : ""
      const thumb = bc.thumbnail_url
        ? `<img src="${this._escapeHtml(bc.thumbnail_url)}" alt="3D context"
              class="w-24 h-16 rounded object-cover border border-gray-200 bg-white shrink-0">`
        : `<div class="w-24 h-16 rounded bg-white border border-gray-200 flex items-center justify-center shrink-0">
              <svg class="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"/></svg>
           </div>`
      bimContextBlock = `
        <div class="px-5 py-3 border-b border-gray-200 bg-white" data-bim-context-block>
          <div class="flex items-start gap-3">
            ${thumb}
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-[12px] font-medium text-gray-700">3D Context</span>
                ${bc.element_count ? `<span class="text-[11px] text-gray-500">${bc.element_count} element${bc.element_count === 1 ? "" : "s"}</span>` : ""}
              </div>
              <div class="flex flex-wrap items-center gap-1 mb-2">${elementChips} ${moreElements}</div>
              <a href="${this._escapeHtml(bc.viewer_url)}"
                 class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-brand text-white hover:bg-brand/90 text-[12px] font-medium">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M14 4l6 6m0 0l-6 6m6-6H4"/></svg>
                View in 3D
              </a>
              ${orphanBanner}
            </div>
          </div>
        </div>
      `
    }

    // Row 2: spec/drawings (left) + APPR & BIC (right)
    let specDisplay = ""
    if (pl.resolved_dev_pdfs && pl.resolved_dev_pdfs.length > 0) {
      specDisplay = pl.resolved_dev_pdfs.map(p => this._escapeHtml(p.title)).join(", ")
    } else if (pl.spec_drawings) {
      specDisplay = this._escapeHtml(pl.spec_drawings)
    }
    const specLabel = pl.resolved_dev_pdfs && pl.resolved_dev_pdfs.length > 0 ? "Design PDFs" : this.i18nSpecDrawingsValue
    const specPart = specDisplay ? `<span class="inline-flex items-center gap-1.5 text-[13px] shrink-0"><span class="text-gray-400 shrink-0">${specLabel}</span> <span class="text-gray-700 font-medium" data-truncate-tip>${specDisplay}</span></span>` : ""
    const apprPart = approverChips ? `<span class="inline-flex items-center gap-1.5 shrink-0"><span class="text-[13px] text-gray-400 uppercase font-medium">${this.i18nApprValue}</span> ${approverChips}</span>` : ""
    const bicPart = bicChips ? `<span data-detail-bic class="inline-flex items-center gap-1.5 shrink-0"><span class="relative w-3.5 h-3.5 shrink-0"><span class="absolute inset-[2px] rounded-full bg-white"></span>${this._futbolIcon("w-3.5 h-3.5 text-gray-400 relative")}</span> ${bicChips}</span>` : ""
    const row2Parts = [specPart, apprPart, bicPart].filter(Boolean).join("")
    // The Reminders link at the right end of that row (it wraps under it when
    // the row is full).
    // History sits at the right end of the files row; with no files, here.
    const historyHere = this._allAttachments.length === 0
    const remindersHtml = (this._reminders || historyHere)
      ? `<div class="ml-auto flex items-center gap-2 shrink-0 text-[13px]">${this._reminders ? `<span data-detail-reminders>${this._remindersChipHtml()}</span>` : ""}${historyHere ? this._historyButtonHtml() : ""}</div>`
      : ""
    const row2Html = (row2Parts || remindersHtml)
      ? `<div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
          ${row2Parts ? `<div class="flex items-center gap-3 min-w-0 max-w-full overflow-x-auto">${row2Parts}</div>` : ""}
          ${remindersHtml}
        </div>`
      : ""
    // Submittal package + schedule (K3-40): one grey line under the row above.
    const submittalHtml = this._renderSubmittalLine(data.submittal)

    // Attachment strip
    let attachStripHtml = ""
    if (this._allAttachments.length > 0) {
      const countLabel = `<span class="text-[13px] text-gray-500 font-medium shrink-0">${this._allAttachments.length} file${this._allAttachments.length !== 1 ? "s" : ""}</span>`
      const thumbs = this._allAttachments.map(att => {
        if (att.is_image && att.thumbnail_url) {
          return `<button type="button" class="w-12 h-12 rounded bg-gray-100 overflow-hidden ring-1 ring-gray-200 hover:ring-brand shrink-0 cursor-pointer transition-all" data-action="click->action-items#openLightboxFromStrip" data-attachment-id="${att.id}"><img src="${this._escapeHtml(att.thumbnail_url)}" class="w-full h-full object-cover opacity-0 transition-opacity duration-300" data-controller="img-handler" data-action="load->img-handler#showLoaded error->img-handler#removeParent"></button>`
        } else if (att.is_pdf) {
          const fname = this._escapeHtml(att.filename || "File")
          return `<button type="button" class="w-12 h-12 rounded bg-white border border-gray-300 hover:border-brand shrink-0 cursor-pointer transition-all flex flex-col items-center justify-center gap-0.5" data-action="click->action-items#openPdfViewer mouseenter->action-items#showFileTip mouseleave->action-items#hideFileTip" data-attachment-id="${att.id}" data-filename="${fname}" data-tip="${fname}"><svg class="w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg><span class="text-[10px] font-semibold text-gray-500 leading-none pointer-events-none">PDF</span></button>`
        } else if (att.is_image) {
          // Image without a usable thumbnail — show placeholder icon
          return `<button type="button" class="w-12 h-12 rounded bg-gray-100 ring-1 ring-gray-200 hover:ring-brand shrink-0 cursor-pointer transition-all flex items-center justify-center" data-action="click->action-items#openLightboxFromStrip" data-attachment-id="${att.id}"><svg class="w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg></button>`
        } else {
          const fname = this._escapeHtml(att.filename || "File")
          const rawExt = (att.filename || "").split(".").pop() || ""
          const ext = rawExt.length > 0 && rawExt.length <= 5 ? rawExt.toUpperCase() : "FILE"
          return `<a href="${this._escapeHtml(att.original_url || "#")}" target="_blank" rel="noopener" class="w-12 h-12 rounded bg-white ring-1 ring-gray-200 hover:ring-brand shrink-0 cursor-pointer transition-all flex flex-col items-center justify-center gap-0.5" data-action="mouseenter->action-items#showFileTip mouseleave->action-items#hideFileTip" data-tip="${fname}"><svg class="w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg><span class="text-[10px] font-semibold text-gray-500 leading-none pointer-events-none">${ext}</span></a>`
        }
      }).join("")
      attachStripHtml = `<div class="border-t border-gray-200 mt-2 pt-2 -mb-1">
        <div class="flex items-center gap-1.5 text-[13px] text-gray-500 mb-1">${countLabel}<span class="font-normal">— Click any to annotate</span><span class="ml-auto">${this._historyButtonHtml()}</span></div>
        <div class="flex items-center gap-1.5 overflow-x-auto py-px">${thumbs}</div>
      </div>`
    }

    // § 3.5 Tier 5 Step 57 — "Synced to ACC · Issue #N" badge + sync-
    // history dropdown trigger. Hidden when the project isn't linked
    // to an ACC project; shown as a "Send to ACC" button when linked
    // but not yet synced (manual trigger for non-3D items, or for
    // items the auto-sync gate hasn't tripped). Anchored to the slide-
    // over so the same partial drives the public page (which doesn't
    // include the action buttons).
    let accSyncPill = ""
    let accConflictBanner = ""
    if (pl.acc_sync) {
      const acc = pl.acc_sync
      if (acc.synced && acc.external_id) {
        const externalLabel = `Issue #${this._escapeHtml(acc.external_id)}`
        const statusBadge = acc.conflict
          ? `<span class="inline-flex items-center px-1.5 py-px rounded-full bg-amber-50 text-amber-800 text-[10px] font-medium uppercase ml-1">Conflict</span>`
          : ""
        accSyncPill = `<button type="button" data-action="click->action-items#toggleAccSyncMenu"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          Synced to ACC · ${externalLabel}${statusBadge}
        </button>`
        if (acc.conflict) {
          const fieldLabel = acc.conflict_field ? this._escapeHtml(acc.conflict_field) : "field"
          accConflictBanner = `<div class="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[13px] text-amber-900"
            data-acc-conflict-banner>
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <span><strong>Synced from ACC.</strong> The ${fieldLabel} field changed in Autodesk Construction Cloud — review and pick a winner.</span>
              <span class="flex items-center gap-1.5 shrink-0">
                <button type="button" class="px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100 text-[12px] font-medium"
                        data-action="click->action-items#resolveAccConflict" data-winner="conslog">Keep ConsLog</button>
                <button type="button" class="px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100 text-[12px] font-medium"
                        data-action="click->action-items#resolveAccConflict" data-winner="acc">Keep ACC</button>
                <button type="button" class="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 text-[12px] font-medium"
                        data-action="click->action-items#resolveAccConflict" data-winner="merge">Merge</button>
              </span>
            </div>
          </div>`
        }
      } else if (acc.eligible_for_auto_sync || acc.last_status === "failed") {
        const labelText = acc.last_status === "failed" ? "Retry sync to ACC" : "Send to ACC"
        accSyncPill = `<button type="button" data-action="click->action-items#triggerAccSync"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-50">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M14 4l6 6m0 0l-6 6m6-6H4"/></svg>
          ${labelText}
        </button>`
      }
    }

    // The PCO draft made from this item (2026-10-15) — only for the team that
    // makes PCOs (the cor_pco payload is theirs alone), only once it exists.
    const cp = data.cor_pco
    const pcoPill = cp?.converted_sequence ? this._pcoChipHtml(cp.converted_sequence, cp.converted_url || cp.finances_url) : ""
    const headerPills = [pcoPill, fromMeetingPill, fromProcorePill, bimContextPill, accSyncPill].filter(Boolean).join(" ")
    const corPcoBlock = this._renderCorPcoBlock(data)
    // The checklist inside the item (K3-38), under the header.
    this._currentPunchlist = pl
    const checklistBlock = this._renderChecklistBlock(data)
    let html = `
      <div class="px-5 py-3 border-b border-gray-200 sticky top-0 bg-gray-100 z-10">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-[15px] font-semibold text-gray-900 leading-snug truncate" data-truncate-tip>${this._escapeHtml(pl.title)}${pl.version ? ` <span class="text-gray-400 font-normal text-[13px]">v${pl.version}</span>` : ""}</h2>
          <div class="flex items-center shrink-0 gap-x-0 text-[13px]">${metaRow}</div>
        </div>
        <div class="mt-1.5 flex flex-wrap gap-1.5 ${headerPills ? "" : "hidden"}" data-header-pills>${headerPills}</div>
        ${row2Html}
        ${this._customFieldsHtml(pl)}
        ${submittalHtml}
        ${attachStripHtml}
      </div>
      ${accConflictBanner}
      ${bimContextBlock}
      ${corPcoBlock}
      ${checklistBlock}
    `

    // Timeline
    html += `<div class="relative px-5 py-4" data-chat-timeline>
      <div class="absolute left-[35px] top-4 bottom-4 w-0.5 bg-gray-100 pointer-events-none" aria-hidden="true"></div>`

    // Who each message and reminder emailed (Phelipe 2026-09-29): a small grey
    // line under the message that sent it, or a line of its own at its time
    // (reminders, and anything whose message isn't shown).
    const activity = data.email_activity || []
    const shownIds = new Set(timeline.filter(e => !(e.comment && e.comment.includes("added as external user"))).map(e => String(e.id)))
    const anchoredLines = {}
    const items = [...timeline]
    activity.forEach(batch => {
      if (batch.anchor != null && shownIds.has(String(batch.anchor))) {
        (anchoredLines[String(batch.anchor)] ||= []).push(batch)
      } else {
        items.push({ type: "email_activity", created_at: batch.at, batch })
      }
    })
    if (items.length > timeline.length) items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    const linesFor = (id) => (anchoredLines[String(id)] || []).map(batch => this._emailLineHtml(batch, false)).join("")

    if (items.length === 0) {
      html += `<p class="text-[13px] text-gray-400 text-center py-4">No messages yet</p>`
      this._lastAuthorKey = null
      this._lastTimestamp = null
      this._lastDayKey = null
    } else {
      let lastAuthorKey = null
      let lastTimestamp = null
      const GROUP_GAP_MS = 5 * 60 * 1000 // 5 minutes
      let pendingAttachments = [] // buffer for consecutive attachment-only entries
      let pendingIsGrouped = false // whether the first buffered entry was itself grouped
      const authorKey = (e) => e.is_public_user_msg ? `public:${e.user_name}` : e.user_id
      const withinGroupWindow = (ts) => {
        if (!lastTimestamp || !ts) return false
        return (new Date(ts) - new Date(lastTimestamp)) < GROUP_GAP_MS
      }
      const flushAttachments = () => {
        if (pendingAttachments.length === 0) return
        const merged = { ...pendingAttachments[0], attachments: pendingAttachments.flatMap(e => e.attachments || []) }
        html += this._renderChatBubble(merged, pendingIsGrouped, pendingAttachments.map(e => linesFor(e.id)).join(""))
        pendingAttachments = []
      }
      let lastDay = null
      items.forEach(entry => {
        // A new day: its dot and date on the line, and a fresh box.
        const dayKey = this._dayKey(entry.created_at)
        if (dayKey && dayKey !== lastDay && !(entry.comment && entry.comment.includes("added as external user"))) {
          flushAttachments()
          html += this._dayDividerHtml(entry.created_at)
          lastDay = dayKey
          lastAuthorKey = null
          lastTimestamp = null
        }
        if (entry.type === "email_activity") {
          flushAttachments()
          html += this._emailLineHtml(entry.batch, true)
          lastAuthorKey = null
          lastTimestamp = null
          return
        }
        // Skip "added as external user" system messages
        if (entry.comment && entry.comment.includes("added as external user")) return

        const key = authorKey(entry)
        const sameGroup = lastAuthorKey === key && withinGroupWindow(entry.created_at)
        if (entry.type === "status_event" || entry.type === "bic_move") {
          flushAttachments()
          html += this._renderChatBubble(entry, sameGroup, linesFor(entry.id))
          lastAuthorKey = key
          lastTimestamp = entry.created_at
        } else {
          const isAttachmentOnly = !entry.comment && entry.attachments && entry.attachments.length > 0
          if (isAttachmentOnly) {
            // Buffer attachment-only entries (both new-group and same-group)
            // so consecutive photo-only messages merge into one visual row
            if (pendingAttachments.length === 0) pendingIsGrouped = sameGroup
            pendingAttachments.push(entry)
          } else {
            flushAttachments()
            html += this._renderChatBubble(entry, sameGroup, linesFor(entry.id))
          }
          lastAuthorKey = key
          lastTimestamp = entry.created_at
        }
      })
      flushAttachments()
      this._lastAuthorKey = lastAuthorKey
      this._lastTimestamp = lastTimestamp
      this._lastDayKey = lastDay
    }

    // Extract current user avatar from timeline entries (for appending own messages)
    const myEntry = timeline.find(e => e.user_id === this.currentUserIdValue)
    if (myEntry?.user_avatar_url) this._currentUserAvatarUrl = myEntry.user_avatar_url

    html += `</div>`

    // Collect already-loaded image srcs so we can skip fade-in for cached images
    const loadedSrcs = new Set()
    this.detailContentTarget.querySelectorAll("img").forEach(img => {
      if (img.complete && img.naturalWidth > 0) loadedSrcs.add(img.src)
    })

    this.detailContentTarget.innerHTML = html
    this.detailLoadingTarget.classList.add("hidden")
    this.detailContentTarget.classList.remove("hidden")

    // Store reference to chat container for appending messages
    this._chatContainer = this.detailContentTarget.querySelector("[data-chat-timeline]")
    this._syncTrashTips()

    // Instantly reveal images that were already loaded (prevents flash on re-render)
    if (loadedSrcs.size > 0) {
      this.detailContentTarget.querySelectorAll("img.opacity-0").forEach(img => {
        if (loadedSrcs.has(img.src) || (img.complete && img.naturalWidth > 0)) {
          img.classList.remove("opacity-0")
        }
      })
    }

    // Scroll to bottom of timeline
    this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight

    // Render "Seen by" chips and mark as read
    this._currentReaders = data.readers || []
    this._renderSeenBy(this._currentReaders)
    this._markAsRead(pl.id)
  }

  _markAsRead(itemId) {
    if (!itemId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const url = `/projects/${this.projectIdValue}/action_items/${itemId}/set_read_messages`
    fetch(url, {
      method: "PUT",
      headers: { "Accept": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin"
    }).then(r => r.json()).then(data => {
      if (data.readers) {
        this._currentReaders = data.readers
        this._renderSeenBy(this._currentReaders)
      }
    }).catch(() => {})

    // Optimistically update table row badge to gray (read)
    this._updateRowBadgeAfterRead(itemId)
  }

  _updateRowBadgeAfterRead(itemId) {
    if (!this.hasTableBodyTarget) return
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${itemId}"]`)
    if (!row) return
    const msgCell = row.querySelector("td:last-child")
    if (!msgCell) return

    // Find item to get total count
    const item = this._allItems.find(i => String(i.id) === String(itemId))
    const total = item ? (item.attached_messages_sum || 0) : 0
    if (!total) return

    // Update in-memory unread to 0
    if (item) item.unread_count = 0

    const icon = `<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"/></svg>`
    msgCell.innerHTML = `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
      ${icon}<span class="text-[13px] font-medium">${total}</span>
    </span>`
  }

  _renderSeenBy(readers) {
    if (!this.hasSeenByContainerTarget) return
    // Filter out current user
    const others = (readers || []).filter(r => r.id !== this.currentUserIdValue)
    if (others.length === 0) {
      this.seenByContainerTarget.classList.add("hidden")
      this.seenByContainerTarget.classList.remove("flex")
      return
    }

    const initialsFallback = (initials) => `<span class="w-5 h-5 rounded-full bg-gray-300 text-white text-[10px] font-bold flex items-center justify-center">${this._escapeHtml(initials || "?")}</span>`
    const chips = others.map(r => {
      const name = this._escapeHtml(r.name || r.initials || "?")
      const initials = this._escapeHtml(r.initials || "?")
      if (r.avatar_url) {
        return `<span class="inline-flex items-center gap-1"><img src="${this._escapeHtml(r.avatar_url)}" data-initials="${initials}" class="w-5 h-5 rounded-full object-cover" data-controller="img-handler" data-img-handler-fallback-class-value="w-5 h-5 rounded-full bg-gray-300 text-white text-[10px] font-bold flex items-center justify-center" data-action="error->img-handler#replaceWithInitials">${name}</span>`
      }
      return `<span class="inline-flex items-center gap-1">${initialsFallback(r.initials)}${name}</span>`
    }).join("")

    this.seenByContainerTarget.innerHTML = `<span class="text-gray-400">Seen by</span>${chips}`
    this.seenByContainerTarget.classList.remove("hidden")
    this.seenByContainerTarget.classList.add("flex")
  }

  _appendMessage(entry) {
    if (!this._chatContainer) return

    const GROUP_GAP_MS = 5 * 60 * 1000
    const authorKey = entry.is_public_user_msg ? `public:${entry.user_name}` : entry.user_id
    let withinGroup = this._lastAuthorKey === authorKey &&
      this._lastTimestamp &&
      (new Date(entry.created_at) - new Date(this._lastTimestamp)) < GROUP_GAP_MS

    // Register attachments so lightbox/PDF viewer can find them
    ;(entry.attachments || []).forEach(att => {
      const obj = {
        id: att.id,
        photo_id: null,
        original_url: att.url || att.thumb_url,
        optimized_url: att.url || att.thumb_url,
        thumbnail_url: att.thumb_url,
        photo_url: null,
        filename: att.filename,
        content_type: null,
        created_at: entry.created_at,
        imageable_type: "Punchlist",
        source_detail: null,
        comment_count: 0,
        annotations: null,
        rotation: 0,
        owner_id: entry.user_id,
        user_id: entry.user_id,
        user_name: entry.user_name,
        is_image: att.is_image,
        is_pdf: !!att.is_pdf
      }
      if (this._allAttachments) this._allAttachments.push(obj)
      if (att.is_image && this._allImageAttachments) this._allImageAttachments.push(obj)
      // Keep PDF merge modal list up to date
      if (this._conversationAttachments) {
        this._conversationAttachments.push({
          id: att.id, filename: att.filename, is_pdf: !!att.is_pdf, is_image: !!att.is_image,
          mergeable: !!att.is_pdf || !!att.is_image
        })
      }
    })

    // Remove "No messages yet" placeholder if present
    const emptyMsg = this._chatContainer.querySelector("p.text-gray-400")
    if (emptyMsg) emptyMsg.remove()

    // First message of a new day: its date on the line first.
    const dayKey = this._dayKey(entry.created_at)
    if (dayKey && dayKey !== this._lastDayKey) {
      this._chatContainer.insertAdjacentHTML("beforeend", this._dayDividerHtml(entry.created_at))
      this._lastDayKey = dayKey
      withinGroup = false
    }

    const bubbleHtml = this._renderChatBubble(entry, withinGroup)
    this._chatContainer.insertAdjacentHTML("beforeend", bubbleHtml)

    this._lastAuthorKey = authorKey
    this._lastTimestamp = entry.created_at

    // Scroll to bottom
    this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight
  }

  _appendNotifiedNote(names) {
    if (!this._chatContainer || !names || names.length === 0) return

    // Remove previous notified note so only the latest one shows
    const prev = this._chatContainer.querySelector("[data-notified-note]")
    if (prev) prev.remove()

    const envelope = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/></svg>`
    const nameList = names.map(n => this._escapeHtml(n)).join(", ")
    const html = `<div data-notified-note class="flex justify-end mt-1 mb-2 pr-1">
      <div class="text-[13px] text-gray-400 text-right leading-relaxed">
        <div class="flex items-center justify-end gap-1 mb-0.5">${envelope} Notified by email:</div>
        <div>${nameList}</div>
      </div>
    </div>`

    this._chatContainer.insertAdjacentHTML("beforeend", html)
    this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight
  }

  _renderStatusBadge(entry, asParts = false) {
    // Icons
    const checkSvg = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
    const xSvg = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>`
    const reopenSvg = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3"/></svg>`
    // Arrow-path icon — generic action icon for custom actions (submitted, under review, etc.)
    const actionSvg = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>`
    // Version increment icon (arrow-up-circle)
    const versionSvg = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0-6.75 6.75M12 4.5l6.75 6.75"/></svg>`

    let label, icon, color

    if (entry.action_name) {
      label = this._escapeHtml(entry.action_name)
      const nameLower = entry.action_name.toLowerCase()
      if (nameLower.includes("reject")) {
        // Rejected — always red
        icon = xSvg
        color = "#dc2626"
      } else if (entry.action_closes_item) {
        // Closes item — green with checkmark
        icon = checkSvg
        color = "#16a34a"
      } else if (entry.action_increments_version) {
        // Increments version — blue
        icon = versionSvg
        color = "#2563eb"
      } else {
        // Other custom actions (Submitted, Under Review, etc.) — orange
        icon = actionSvg
        color = "#d97706"
      }
    } else if (entry.approval_event === "approved") {
      const types = {
        approved: { label: this.i18nApprovedValue, color: "#16a34a" },
        approved_as_noted: { label: "Approved as Noted", color: "#16a34a" },
        approved_and_resubmit: { label: "Revise & Resubmit", color: "#2563eb" },
        no_exceptions: { label: "No Exceptions", color: "#8A2BE2" },
        closed: { label: this.i18nClosedValue, color: "#16a34a" }
      }
      const t = types[entry.approved_type] || types.approved
      label = t.label
      // Revise & Resubmit increments version → version icon
      icon = entry.approved_type === "approved_and_resubmit" ? versionSvg : checkSvg
      color = t.color
    } else if (entry.approval_event === "reopened") {
      label = this.i18nReopenedValue
      icon = reopenSvg
      color = "#d97706"
    } else if (entry.completion_event === "completed") {
      label = this.i18nCompletedValue
      icon = checkSvg
      color = "#16a34a"
    }

    if (asParts) return { label, icon, color }
    // A soft pill: pale tint of the status color, colored words (Phelipe 2026-10-26).
    return `<div class="mt-px">
      <span class="inline-flex items-center gap-1 rounded-full px-2 py-px text-[13px] font-semibold" style="color:${color};background:${color}1f">${icon} ${label}</span>
    </div>`
  }

  _renderBicBadge(entry, asNames = false) {
    const comment = entry.comment || ""
    let namesStr = comment
    if (comment.startsWith("Ball in court moved to")) {
      namesStr = comment.replace("Ball in court moved to ", "")
    } else if (comment.includes("has moved the ball to")) {
      const match = comment.match(/has moved the ball to (.+?)(?:'s court)?$/)
      namesStr = match ? match[1] : comment
    }
    const bicIcon = this._futbolIcon("w-3 h-3 shrink-0")
    const shortName = (n) => n.includes("@") ? n.split("@")[0] : n.trim()
    const names = namesStr.split(",").map(n => n.trim()).filter(n => n.length > 0)
    const displayNames = names.map(n => this._escapeHtml(shortName(n))).join(", ")
    if (asNames) return displayNames
    return `<div class="mt-px">
      <span class="inline-flex items-center gap-1 rounded px-1.5 py-px text-[13px] font-medium" style="color:#6b7280;border:1px solid #d1d5db;">${bicIcon} → ${displayNames}</span>
    </div>`
  }

  _renderTargetDateBadge(entry) {
    const comment = entry.comment || ""
    const calendarIcon = `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/></svg>`
    // Extract the date portion for display
    let displayText = this._escapeHtml(comment)
    if (comment.startsWith("Target date changed")) {
      displayText = this._escapeHtml(comment)
    }
    return `<div class="mt-px">
      <span class="inline-flex items-center gap-1 rounded px-1.5 py-px text-[13px] font-medium" style="color:#6b7280;border:1px solid #d1d5db;">${calendarIcon} ${displayText}</span>
    </div>`
  }

  // The chat as a timeline (Phelipe 2026-10-26, mockup G1): a light grey line
  // runs down the left through the avatars; each message sits in a box beside
  // it with its "Emailed to ..." line inside; back-to-back messages from one
  // person join into one box; ball moves, due dates and bare status changes
  // are a small dot on the line; a dot and a date start each day.
  _renderChatBubble(entry, grouped = false, emailLines = "") {
    const isPublic = entry.is_public_user_msg

    const initials = this._escapeHtml(entry.user_initials || "?")
    // A white ring keeps the line from showing through the avatar's edge.
    const initialsEl = `<span class="relative z-[1] ring-[3px] ring-white w-[33px] h-[33px] rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-medium text-gray-500 shrink-0 mt-[3px]">${initials}</span>`
    const avatar = entry.user_avatar_url && !entry.user_avatar_url?.includes("avatar.png")
      ? `<img src="${this._escapeHtml(entry.user_avatar_url)}" data-initials="${initials}" class="relative z-[1] ring-[3px] ring-white w-[33px] h-[33px] rounded-full object-cover shrink-0 mt-[3px] opacity-0 transition-opacity duration-300" data-controller="img-handler" data-img-handler-fallback-class-value="relative z-[1] ring-[3px] ring-white w-[33px] h-[33px] rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-medium text-gray-500 shrink-0 mt-[3px]" data-action="load->img-handler#showLoaded error->img-handler#replaceWithInitials">`
      : initialsEl

    // Can this user delete things on this entry?
    const isOwner = entry.id && !entry.is_email_notif && entry.user_id === this.currentUserIdValue
    const canDeleteAttachment = isOwner
    // × button for individual attachments
    const attDeleteSvg = `<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>`

    // Attachments — separate images and files into distinct rows
    let attachHtml = ""
    if (entry.attachments && entry.attachments.length > 0) {
      const images = entry.attachments.filter(a => a.is_image && a.thumb_url)
      const files = entry.attachments.filter(a => !a.is_image || !a.thumb_url)

      if (images.length > 0) {
        attachHtml += `<div class="flex flex-wrap gap-1.5 mt-1">`
        images.forEach(att => {
          const removeBtn = canDeleteAttachment
            ? `<button type="button" data-action="click->action-items#deleteAttachment" data-comment-id="${entry.id}" data-attachment-id="${att.id}" class="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 cursor-pointer opacity-0 group-hover/att:opacity-100 transition-opacity" title="Remove file">${attDeleteSvg}</button>`
            : ""
          attachHtml += `<div class="relative group/att" data-attachment-id="${att.id}"><div class="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden cursor-pointer hover:ring-2 hover:ring-brand transition-all" data-action="click->action-items#openLightboxFromTimeline" data-attachment-id="${att.id}"><img src="${this._escapeHtml(att.thumb_url)}" class="w-full h-full object-cover opacity-0 transition-opacity duration-300" data-controller="img-handler" data-action="load->img-handler#showLoaded error->img-handler#removeParent"></div>${removeBtn}</div>`
        })
        attachHtml += `</div>`
      }

      if (files.length > 0) {
        const fileDeleteBtn = (attId) => canDeleteAttachment
          ? `<button type="button" data-action="click->action-items#deleteAttachment" data-comment-id="${entry.id}" data-attachment-id="${attId}" class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 cursor-pointer opacity-0 group-hover/att:opacity-100 transition-opacity" title="Remove file"><svg class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg></button>`
          : ""
        attachHtml += `<div class="flex flex-wrap gap-1.5 mt-1">`
        files.forEach(att => {
          const docSvg = `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>`
          if (att.is_pdf) {
            attachHtml += `<div class="relative group/att" data-attachment-id="${att.id}"><button type="button" data-action="click->action-items#openPdfViewer" data-attachment-id="${att.id}" data-filename="${this._escapeHtml(att.filename || "document.pdf")}" class="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-[13px] text-gray-600 hover:bg-gray-200 cursor-pointer">${docSvg}${this._escapeHtml(att.filename || "document.pdf")}</button>${fileDeleteBtn(att.id)}</div>`
          } else {
            attachHtml += `<div class="relative group/att" data-attachment-id="${att.id}"><a href="${this._escapeHtml(att.url || "#")}" target="_blank" rel="noopener" class="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-[13px] text-gray-600 hover:bg-gray-200">${docSvg}${this._escapeHtml(att.filename || "File")}</a>${fileDeleteBtn(att.id)}</div>`
          }
        })
        attachHtml += `</div>`
      }
    }

    // Inline badge for status events, BIC moves, and target date changes
    let badgeHtml = ""
    if (entry.type === "status_event") badgeHtml = this._renderStatusBadge(entry)
    else if (entry.type === "bic_move") badgeHtml = this._renderBicBadge(entry)
    else if (entry.type === "target_date_change") badgeHtml = this._renderTargetDateBadge(entry)

    // BIC moves and target date changes store the description in comment — suppress it since badge shows it
    const commentText = (entry.type === "bic_move" || entry.type === "target_date_change") ? null : entry.comment

    // Delete button for the whole message (regular comments only — status events
    // represent action responses and should not be deleted entirely, only individual
    // attachments can be removed via the × button)
    const canDelete = isOwner && entry.type === "comment"
    // A small grey trash can on the far right, red on hover (Phelipe
    // 2026-10-26); its tip says whom the email already reached.
    // Three dots at the box's top right open a small menu: Delete message,
    // and under it whom the email already reached (Phelipe 2026-10-26).
    const deleteBtn = canDelete
      ? `<span class="absolute right-1 top-1" data-trash>
          <button type="button" data-action="click->action-items#toggleMessageMenu" aria-haspopup="menu" aria-expanded="false" aria-label="${this._escapeAttr(this._undoT("more"))}" class="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"><svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="4" r="1.6"/><circle cx="10" cy="10" r="1.6"/><circle cx="10" cy="16" r="1.6"/></svg></button>
          <div data-msg-menu role="menu" hidden class="absolute right-0 top-7 z-20 w-64 rounded-lg border border-gray-300 bg-white py-1 shadow-lg">
            <button type="button" role="menuitem" data-action="click->action-items#deleteComment" data-comment-id="${entry.id}" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] font-medium text-red-600 hover:bg-red-50 cursor-pointer"><svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>${this._escapeHtml(this._undoT("menu_delete"))}</button>
            <p data-trash-tip class="px-3 pb-1.5 pt-0.5 text-[13px] leading-snug text-gray-500" hidden></p>
          </div>
        </span>`
      : ""

    // Ball moves, due dates and status changes with no words or files: a dot on the line.
    const bareStatus = entry.type === "status_event" && !commentText && !(entry.attachments || []).length
    if (entry.type === "bic_move" || entry.type === "target_date_change" || bareStatus) return this._renderChatEvent(entry, emailLines)

    let badges = ""
    if (isPublic) badges += '<span class="text-[13px] text-gray-500 bg-gray-100 px-1 rounded ml-1">External</span>'
    const header = grouped ? "" : `<div class="flex items-baseline gap-1.5 ${canDelete ? "pr-6" : ""}">
          <span class="text-sm font-bold text-gray-900">${this._escapeHtml(entry.user_name || "")}</span>
          ${badges}
          <span class="text-[13px] text-gray-400">${this._friendlyDateTime(entry.created_at)}</span>
        </div>`
    const box = `<div class="relative flex-1 min-w-0 rounded-[10px] border border-gray-200 bg-white px-2.5 py-1.5" style="border-left:2px solid ${this._edgeColor(entry)}" data-chat-box>
        ${header}
        <div class="${canDelete ? "pr-6" : ""}" data-bubble-body>
          ${commentText ? `<p class="text-sm text-gray-800 whitespace-pre-wrap${grouped ? "" : " mt-0.5"}">${this._escapeHtml(commentText)}</p>` : ""}
          ${attachHtml}
          ${badgeHtml}
        </div>
        <div data-email-slot>${emailLines}</div>
        ${deleteBtn}
      </div>`

    if (grouped) return `<div class="pl-[43px]" data-chat-row data-chat-grouped data-comment-id="${entry.id || ""}">${box}</div>`
    return `<div class="flex gap-2.5 mt-3" data-chat-row data-comment-id="${entry.id || ""}">${avatar}${box}</div>`
  }

  // Each person's color on their boxes' left edge - the same everywhere
  // (PunchlistsHelper#chat_edge_color picks it the same way for the guest page).
  _edgeColor(entry) {
    const colors = [ "#93c5fd", "#fcd34d", "#c4b5fd", "#99f6e4", "#fda4af", "#86efac", "#fdba74" ]
    const key = entry.is_public_user_msg ? `p${String(entry.user_name || "").trim().toLowerCase()}` : `u${entry.user_id ?? ""}`
    let sum = 0
    for (const ch of key) sum += ch.codePointAt(0)
    return colors[sum % colors.length]
  }

  // One line beside a small dot on the timeline's line: who moved the ball /
  // changed the due date / set the status, and when.
  _renderChatEvent(entry, emailLines = "") {
    const who = `<span class="font-semibold text-gray-700">${this._escapeHtml(entry.user_name || "")}</span>`
    const when = `<span class="text-gray-400">· ${this._friendlyDateTime(entry.created_at)}</span>`
    let icon, tone, what, color = ""
    if (entry.type === "bic_move") {
      icon = this._futbolIcon("w-3 h-3")
      tone = "border-amber-200 bg-amber-50 text-amber-700"
      what = `→ <span class="font-semibold text-gray-700">${this._renderBicBadge(entry, true)}</span>`
    } else if (entry.type === "target_date_change") {
      icon = `<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/></svg>`
      tone = "border-gray-200 bg-white text-gray-500"
      what = this._escapeHtml(entry.comment || "")
    } else {
      const st = this._renderStatusBadge(entry, true)
      icon = st.icon
      tone = "border-gray-200 bg-white"
      color = ` style="color:${st.color}"`
      what = `<span class="inline-flex items-center rounded-full px-2 py-px font-semibold" style="color:${st.color};background:${st.color}1f">${st.label || ""}</span>`
    }
    return `<div class="flex items-start gap-2.5 my-2" data-chat-row data-chat-event data-comment-id="${entry.id || ""}">
      <span class="relative z-[1] ml-[6px] w-5 h-5 rounded-full border ring-[3px] ring-white flex items-center justify-center shrink-0 ${tone}"${color}>${icon}</span>
      <div class="flex-1 min-w-0 pt-px text-[13px] text-gray-500">
        <div data-bubble-body>${who} ${what} ${when}</div>
        <div data-email-slot>${emailLines}</div>
      </div>
    </div>`
  }

  // A dot and a date on the line where a new day starts; "Today" in blue.
  _dayKey(ts) {
    const d = new Date(ts)
    return isNaN(d) ? "" : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  }

  _dayDividerHtml(ts) {
    const d = new Date(ts)
    const now = new Date()
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    const key = this._dayKey(ts)
    const today = key === this._dayKey(now)
    const label = today ? this._historyT("today")
      : key === this._dayKey(yesterday) ? this._historyT("yesterday")
      : d.toLocaleDateString(this._uiLocale(), { month: "short", day: "numeric", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) })
    return `<div class="flex items-center gap-2 mt-4 mb-1 text-[13px] font-semibold ${today ? "text-blue-600" : "text-gray-400"}" data-chat-day="${key}">
      <span class="relative z-[1] ml-[11px] w-2.5 h-2.5 rounded-full ring-[3px] ring-white shrink-0 ${today ? "bg-blue-600" : "bg-gray-300"}"></span>
      <span>${this._escapeHtml(label)}</span><span class="flex-1 h-px bg-gray-100"></span>
    </div>`
  }

  // The page's language as Intl wants it (the app's br / jp / sa codes).
  _uiLocale() {
    const lang = document.documentElement.lang || "en"
    return { br: "pt-BR", jp: "ja", sa: "en-ZA" }[lang] || lang
  }

  // One small grey line per email send (Phelipe 2026-09-29): "Emailed to
  // 14 people", "Reminder emailed to 2 people · due tomorrow". Hover (or focus)
  // opens its card - who got it, how, and when they opened it and clicked to
  // view (email_card_controller). A line of its own also says when.
  _emailLineHtml(batch, loose) {
    const mail = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/></svg>`
    const text = loose ? `${batch.line} · ${this._friendlyDateTime(batch.at)}` : batch.line
    const anchorAttr = !loose && batch.anchor != null ? ` data-email-anchor="${this._escapeAttr(String(batch.anchor))}"` : ""
    return `<div class="${loose ? "pl-[43px] my-2" : "mt-0.5"}" data-email-activity${anchorAttr}>
      <span class="inline-flex items-center gap-1 text-[13px] italic text-gray-400 hover:text-gray-500 focus:text-gray-500 cursor-default focus:outline-none" tabindex="0"
            data-controller="email-card" data-email-card-batch-value="${this._escapeAttr(JSON.stringify(batch))}"
            data-action="mouseenter->email-card#show mouseleave->email-card#hide focus->email-card#show blur->email-card#hide">${mail}<span>${this._escapeHtml(text)}</span></span>
    </div>`
  }

  // Right after sending: the line's words (from the server), under the message,
  // with no card yet - the refresh swaps in the full line.
  _showPendingEmailLine(anchor, text) {
    const bubble = this._chatContainer && this._chatContainer.querySelector(`[data-comment-id="${CSS.escape(String(anchor))}"]`)
    if (!bubble) return
    const existing = this._chatContainer.querySelector(`[data-email-anchor="${CSS.escape(String(anchor))}"]`)
    if (existing) return
    const mail = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/></svg>`
    const html = `<div class="mt-0.5" data-email-activity data-email-pending data-email-anchor="${this._escapeAttr(String(anchor))}">
      <span class="inline-flex items-center gap-1 text-[13px] italic text-gray-400">${mail}<span>${this._escapeHtml(text)}</span></span>
    </div>`
    const slot = bubble.querySelector("[data-email-slot]")
    if (slot) slot.insertAdjacentHTML("beforeend", html)
    else bubble.insertAdjacentHTML("afterend", html)
    this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight
  }

  // Emails go out a few seconds after a message (a background job); fetch the
  // item's lines again then, a couple of times, and put each under its message.
  _scheduleEmailActivityRefresh() {
    ;(this._emailRefreshTimers || []).forEach(clearTimeout)
    const itemId = this._activeItemId
    this._emailRefreshTimers = [ 3000, 8000, 20000, 32000, 45000, 75000, 150000 ].map(ms => setTimeout(() => this._refreshEmailActivity(itemId), ms))
  }

  async _refreshEmailActivity(itemId) {
    if (!itemId || itemId !== this._activeItemId || !this._chatContainer) return
    try {
      const url = this.itemsUrlValue.replace(".json", `/${itemId}/email_activity`)
      const response = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      if (!response.ok || itemId !== this._activeItemId) return
      const data = await response.json()
      ;(data.email_activity || []).forEach(batch => {
        if (batch.anchor == null) return
        const key = CSS.escape(String(batch.anchor))
        const bubble = this._chatContainer.querySelector(`[data-comment-id="${key}"]`)
        if (!bubble) return
        const html = this._emailLineHtml(batch, false)
        const existing = this._chatContainer.querySelector(`[data-email-anchor="${key}"]`)
        const slot = bubble.querySelector("[data-email-slot]")
        if (existing) existing.outerHTML = html
        else if (slot) slot.insertAdjacentHTML("beforeend", html)
        else bubble.insertAdjacentHTML("afterend", html)
      })
      this._syncTrashTips()
    } catch (_e) { /* the lines show on the next open */ }
  }

  // ── 25 seconds to undo (Phelipe 2026-10-26) ────────────────────────
  // The change shows at once; its emails and phone alerts wait on the
  // server, which sends them when the time is up even if this page is gone.

  _undoT(key, vars = {}) {
    const english = {
      emails_in: "Emails go out in %{seconds}s", undo: "Undo", undoing: "Undoing…",
      too_late: "Too late — the emails already went out.",
      trash_tip: "Deletes this message from the action item.",
      trash_tip_sent: "Deletes this message from the action item. The email already sent to %{names} can't be pulled back.",
      more: "More", menu_delete: "Delete message", menu_note_sent: "The email already sent to %{names} can't be pulled back.",
      delete_confirm: "Delete this message?"
    }
    const text = (this.i18nUndoValue && this.i18nUndoValue[key]) || english[key] || key
    return Object.entries(vars).reduce((str, [k, v]) => str.replaceAll(`%{${k}}`, v), text)
  }

  _nameList(names) {
    try { return new Intl.ListFormat(this._uiLocale(), { type: "conjunction" }).format(names) } catch (_e) { return names.join(", ") }
  }

  // A small pill at the end of the new entry - next to its Ball in Court or
  // status chip, else under its words (Phelipe 2026-10-26): a ring that runs
  // down, "Emails go out in 9s" and Undo. When it runs out, the grey
  // "Emailed to ..." line follows.
  _showUndoBar(pending, ctx = {}) {
    const anchor = String(pending.anchor)
    const find = (id) => this._chatContainer && this._chatContainer.querySelector(`div[data-comment-id="${CSS.escape(String(id))}"]`)
    const bubble = find(anchor)
    if (!bubble) return
    this._chatContainer.querySelectorAll(`[data-email-anchor="${CSS.escape(anchor)}"]`).forEach(el => el.remove())
    // The send's last entry (a message with a ball move shows as two).
    const host = (ctx.entryIds || []).map(find).filter(Boolean).pop() || bubble
    const content = host.querySelector("[data-bubble-body]") || host

    const total = Math.max(pending.seconds || 0, 1)
    const endsAt = Date.now() + (pending.seconds || 0) * 1000
    const ring = 2 * Math.PI * 7
    const bar = document.createElement("span")
    bar.dataset.undoBar = anchor
    bar.className = "inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 pl-1 pr-2 py-px text-[13px] text-gray-700 align-middle whitespace-nowrap"
    bar.innerHTML = `<svg class="w-4 h-4 -rotate-90 shrink-0" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="7" fill="none" stroke="#fde68a" stroke-width="2.5"/><circle cx="9" cy="9" r="7" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="${ring}" stroke-dashoffset="0" data-role="ring" style="transition:stroke-dashoffset .3s linear"/></svg>
      <span data-role="label"></span><span class="text-gray-300">·</span>
      <button type="button" class="font-semibold text-blue-600 hover:underline cursor-pointer disabled:opacity-60" data-role="undo">${this._escapeHtml(this._undoT("undo"))}</button>`
    // Beside the chip when the entry ends with one, else on a line of its own.
    const chipRow = content.querySelector(":scope > div.mt-px:last-child")
    let holder = null
    if (chipRow) {
      bar.classList.add("ml-2")
      chipRow.appendChild(bar)
    } else {
      holder = document.createElement("div")
      holder.className = "mt-1"
      holder.appendChild(bar)
      content.appendChild(holder)
    }
    // The trash can waits until the emails are out - Undo is the way back now.
    const trash = bubble.querySelector("[data-trash]")
    if (trash) trash.hidden = true
    this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight

    const label = bar.querySelector('[data-role="label"]')
    const arc = bar.querySelector('[data-role="ring"]')
    const [ before, after = "" ] = this._undoT("emails_in").split("%{seconds}")
    let timer = null
    const finish = () => {
      clearInterval(timer)
      if (!bar.isConnected) return
      ;(holder || bar).remove()
      if (trash) {
        trash.hidden = false
        const tip = trash.querySelector("[data-trash-tip]")
        if (tip && ctx.notifiedNames.length) {
          tip.textContent = this._undoT("menu_note_sent", { names: this._nameList(ctx.notifiedNames) })
          tip.hidden = false
        }
      }
      if (ctx.emailLine) this._showPendingEmailLine(anchor, ctx.emailLine)
      else if (ctx.notifiedNames.length) this._appendNotifiedNote(ctx.notifiedNames)
      const sentLine = this._chatContainer?.querySelector(`[data-email-anchor="${CSS.escape(anchor)}"] > span`) ||
                       this._chatContainer?.querySelector("[data-notified-note] div")
      if (sentLine) this._flashSentCheck(sentLine)
      this._scheduleEmailActivityRefresh()
    }
    const tick = () => {
      if (!bar.isConnected) { clearInterval(timer); return }
      const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
      label.innerHTML = `${this._escapeHtml(before)}<b>${left}</b>${this._escapeHtml(after)}`
      arc.setAttribute("stroke-dashoffset", String(ring * (1 - left / total)))
      if (left <= 0) finish()
    }
    timer = setInterval(tick, 250)
    tick()
    bar.querySelector('[data-role="undo"]').addEventListener("click", () => this._undoSend(pending.token, { bar, timer, ctx }))
  }

  // The emails are out: a green check draws itself after the line, then
  // fades away (Phelipe 2026-10-26).
  _flashSentCheck(el) {
    const check = document.createElement("span")
    check.className = "inline-flex ml-1 align-middle"
    check.style.transition = "opacity .4s ease"
    check.innerHTML = `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 12.75l6 6 9-13.5" pathLength="1" style="stroke-dasharray:1;stroke-dashoffset:1;transition:stroke-dashoffset .45s ease-out"/></svg>`
    el.appendChild(check)
    const path = check.querySelector("path")
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) path.style.strokeDashoffset = "0"
    else requestAnimationFrame(() => requestAnimationFrame(() => { path.style.strokeDashoffset = "0" }))
    setTimeout(() => { check.style.opacity = "0" }, 1800)
    setTimeout(() => check.remove(), 2300)
  }

  async _undoSend(token, { bar, timer, ctx }) {
    const btn = bar.querySelector('[data-role="undo"]')
    btn.disabled = true
    btn.textContent = this._undoT("undoing")
    const itemId = this._activeItemId
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const url = this.itemsUrlValue.replace(".json", `/${itemId}/undo_send`)
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin",
        body: JSON.stringify({ token })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showToast(data.error || this._undoT("too_late"), "alert")
        btn.disabled = false
        btn.textContent = this._undoT("undo")
        return
      }
      clearInterval(timer)
      // The note takes the entries' place: "Undone. The ball is back with ..."
      const note = document.createElement("div")
      note.className = "pl-[43px] my-1 text-[13px] italic text-gray-400"
      note.dataset.undoNote = ""
      note.textContent = data.note || ""
      const gone = (data.removed_ids || []).map(id => this._chatContainer?.querySelector(`div[data-comment-id="${CSS.escape(String(id))}"]`)).filter(Boolean)
      if (gone.length) gone[0].insertAdjacentElement("beforebegin", note)
      else bar.closest("div[data-comment-id]")?.insertAdjacentElement("afterend", note)
      gone.forEach(el => el.remove())
      bar.remove()
      this._lastAuthorKey = null
      if (data.row_update && itemId === this._activeItemId) {
        this._applyRowUpdateToDetail(data.row_update)
        this._updateTableRow(itemId, data.row_update)
      }
      // Their words back in the box, to fix and send again.
      if (ctx.text && this.hasCommentInputTarget && !this.commentInputTarget.value.trim()) {
        this.commentInputTarget.value = ctx.text
        this.autoGrowTextarea()
      }
    } catch (e) {
      console.error("[ActionItems] undoSend error:", e)
      btn.disabled = false
      btn.textContent = this._undoT("undo")
    }
  }

  // The header after an Undo: status chip, Ball in Court, due date.
  _applyRowUpdateToDetail(ru) {
    const ds = ru.display_status
    if (this.hasDetailStatusTarget && ds) {
      this.detailStatusTarget.innerHTML = this._statusChip(ds, ru.approved_type, ru.action_name, ru.latest_action)
    }
    this._activeItemResolved = ds === "resolved"
    this._updateCommentInputState()
    this._updateSendButtonState()
    if (this.hasReopenBtnTarget) this.reopenBtnTarget.classList.toggle("hidden", !this._activeItemResolved)
    this._updateDetailBicChips(this._enrichBicFromMembers(ru.ball_in_court || []), ds)
    if ("target_date" in ru) {
      this._updateDetailTargetDate(ru.target_date)
      const item = this._allItems.find(i => i.id === this._activeItemId)
      if (item) {
        item.target_date = ru.target_date
        item.days_overdue = ru.days_overdue
        item.is_delayed = ru.is_delayed
      }
    }
  }

  // ── History (Phelipe 2026-10-26) ───────────────────────────────────
  // Everything that happened to the item, newest first: comments, files,
  // Ball in Court, status, due dates, edits, emails, and also deletes and
  // undos.

  _historyT(key, vars = {}) {
    const english = { button: "History", title: "History", empty: "Nothing yet.", loading: "Loading…", today: "Today", yesterday: "Yesterday",
                      failed: "The history couldn't be loaded. Try again." }
    const text = (this.i18nHistoryValue && typeof this.i18nHistoryValue[key] === "string" && this.i18nHistoryValue[key]) || english[key] || key
    return Object.entries(vars).reduce((str, [k, v]) => str.replaceAll(`%{${k}}`, v), text)
  }

  _historyButtonHtml() {
    const label = this._escapeHtml(this._historyT("button"))
    const clock = `<svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>`
    return `<button type="button" data-action="click->action-items#openHistory" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-300 bg-white text-[13px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 cursor-pointer transition-colors">${clock}<span>${label}</span></button>`
  }

  async openHistory() {
    const itemId = this._activeItemId
    if (!itemId) return
    this.element.querySelector("[data-history-modal]")?.remove()

    const modal = document.createElement("div")
    modal.dataset.historyModal = ""
    modal.className = "fixed inset-0 z-[2147483005] flex items-center justify-center bg-black/50 p-4"
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]" data-role="panel">
        <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 class="text-base font-semibold text-gray-900">${this._escapeHtml(this._historyT("title"))}</h3>
          <button type="button" class="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer" data-role="close" aria-label="Close"><svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg></button>
        </div>
        <div class="overflow-y-auto px-5 py-3" data-role="body"><p class="text-[13px] text-gray-400">${this._escapeHtml(this._historyT("loading"))}</p></div>
      </div>`
    this.element.appendChild(modal)
    const onKey = (e) => { if (e.key === "Escape") hide() }
    const hide = () => { modal.remove(); document.removeEventListener("keydown", onKey) }
    modal.addEventListener("click", (e) => { if (e.target === modal) hide() })
    modal.querySelector('[data-role="close"]').addEventListener("click", hide)
    document.addEventListener("keydown", onKey)

    const body = modal.querySelector('[data-role="body"]')
    try {
      const url = this.itemsUrlValue.replace(".json", `/${itemId}/history`)
      const response = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      body.innerHTML = this._historyListHtml(data.entries || [])
    } catch (_e) {
      body.innerHTML = `<p class="text-[13px] text-red-600">${this._escapeHtml(this._historyT("failed"))}</p>`
    }
  }

  _historyListHtml(entries) {
    if (!entries.length) return `<p class="text-[13px] text-gray-400">${this._escapeHtml(this._historyT("empty"))}</p>`
    const dot = { comment: "bg-blue-500", ball: "bg-amber-500", status: "bg-green-600", due: "bg-purple-500",
                  deleted: "bg-red-500", file_removed: "bg-red-500", undone: "bg-red-500", created: "bg-gray-800" }
    return `<ol class="relative border-l border-gray-200 ml-1.5">${entries.map(e => `
      <li class="ml-4 py-2">
        <span class="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${dot[e.kind] || "bg-gray-400"}"></span>
        <div class="text-sm text-gray-800">${e.who ? `<span class="font-semibold">${this._escapeHtml(e.who)}</span> ` : ""}${this._escapeHtml(e.text || "")}</div>
        ${e.quote ? `<div class="text-[13px] text-gray-500 italic whitespace-pre-wrap mt-0.5">“${this._escapeHtml(e.quote)}”</div>` : ""}
        <div class="text-[13px] text-gray-400 mt-0.5">${this._friendlyDateTime(e.at)}</div>
      </li>`).join("")}</ol>`
  }

  // Each message's trash can says whom its email already reached.
  _syncTrashTips() {
    if (!this._chatContainer) return
    this._chatContainer.querySelectorAll("[data-trash-tip]").forEach(tip => {
      const id = tip.closest("div[data-comment-id]")?.dataset.commentId
      const line = id && this._chatContainer.querySelector(`[data-email-anchor="${CSS.escape(id)}"] [data-email-card-batch-value]`)
      let names = []
      try { names = line ? (JSON.parse(line.dataset.emailCardBatchValue).rows || []).map(r => r.name).filter(Boolean) : [] } catch (_e) { names = [] }
      tip.textContent = names.length ? this._undoT("menu_note_sent", { names: this._nameList(names) }) : ""
      tip.hidden = !names.length
    })
  }

  // For attribute values: _escapeHtml leaves quotes as they are.
  _escapeAttr(str) {
    return String(str ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // ── Quick actions ──────────────────────────────────────────────────

  async completeItem() {
    await this._submitAction({ completion_status: "completed" })
  }

  async approveItem() {
    await this._submitAction({ status: "approved", approved_type: "approved" })
  }

  // ── Action item → PCO draft (internal slide-over only; the public page
  //    intentionally never shows this — external users can't create PCOs) ──
  // Since 2026-10-15 a status with "PCO Draft" ticked in Settings → Action
  // Item Types makes the draft by itself (in the background); this box says
  // which status does it, shows progress, and keeps the button as a backup.
  // Types with no ticked status keep the older COR / RFI button.
  _renderCorPcoBlock(data) {
    const c = data.cor_pco
    if (!c) return ""

    const noun = c.noun || "COR"
    const place = c.place || "Change Events"
    const builder = c.module === "builder"
    const label = this._pcoLabel(builder)
    if (c.converted_sequence) return this._corPcoDoneHtml(c.converted_sequence, noun, place, c.converted_url || c.finances_url, label)

    if (c.state === "pending") {
      this._schedulePcoPoll()
      return `<div class="px-5 py-3 border-b border-gray-200" data-cor-pco-section>
        ${label}
        <p class="text-[13px] text-gray-600 flex items-center gap-1.5">${this._pcoSpinner()} Creating the PCO draft under Finances → ${place}…</p>
      </div>`
    }

    if (!c.can_convert) return ""
    const failed = this._pcoFailedNote(c.state, place)
    if (c.status_driven) {
      const names = this._pcoStatusNames(c.trigger_names)
      if (!c.approved && !failed) {
        return `<div class="px-5 py-3 border-b border-gray-200" data-cor-pco-section>
          ${label}
          <p class="text-[13px] text-gray-500">To create a PCO draft, change the status to ${names}. The draft appears under Finances → ${place}.</p>
        </div>`
      }
      return this._pcoButtonHtml(c, noun, place, builder, label,
        failed || `This item reached ${names}, but no PCO draft was made yet.`)
    }

    // Older flow: the section stays visible before it's ready so the flow is
    // discoverable (Phelipe 2026-08-05) — the hint explains what unlocks it.
    if (!c.approved) {
      const hint = noun === "RFI"
        ? "Once this RFI has an answer, you can turn it into a draft PCO here — for when the answer changes the cost or the schedule."
        : "Once this COR is fully approved, you can turn it into a draft PCO here."
      return `<div class="px-5 py-3 border-b border-gray-200" data-cor-pco-section>
        ${label}
        <p class="text-[13px] text-gray-500">${hint}</p>
      </div>`
    }
    if (!c.sov_approved) {
      return `<div class="px-5 py-3 border-b border-gray-200" data-cor-pco-section>
        ${label}
        <p class="text-[13px] text-gray-500">The project's SOV must be approved before this ${this._escapeHtml(noun)} can become a PCO.</p>
      </div>`
    }
    return this._pcoButtonHtml(c, noun, place, builder, label, failed)
  }

  _pcoChipHtml(seq, url) {
    return `<a href="${this._escapeHtml(url || "#")}" data-pco-chip title="The PCO draft made from this item"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100">PCO #${seq} ↗</a>`
  }

  // Add the chip to the open panel's header once the draft exists.
  _showPcoChip(seq, url) {
    const box = this.detailContentTarget.querySelector("[data-header-pills]")
    if (!box || box.querySelector("[data-pco-chip]")) return
    box.insertAdjacentHTML("afterbegin", this._pcoChipHtml(seq, url))
    box.classList.remove("hidden")
  }

  _pcoLabel(builder) {
    return `<p class="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-1">${builder ? "Proposed Change Order (PCO)" : "Change Event (PCO)"}</p>`
  }

  _pcoSpinner() {
    return `<svg class="animate-spin w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  // "“Approved”" / "“Approved” or “Answered”"
  _pcoStatusNames(names) {
    const quoted = (names || []).map(n => `“${this._escapeHtml(n)}”`)
    if (quoted.length <= 1) return quoted[0] || "its PCO status"
    return `${quoted.slice(0, -1).join(", ")} or ${quoted[quoted.length - 1]}`
  }

  // Why a ticked status couldn't make the draft (punchlists.pco_draft_state).
  _pcoFailedNote(state, place) {
    switch (state) {
      case "no_sov": return "The PCO draft wasn't made: the project's SOV must be approved first."
      case "not_allowed": return "The PCO draft wasn't made: the person who changed the status isn't on the team that makes PCOs."
      case "ai_failed": return `The PCO draft couldn't be made automatically. Try again, or add it by hand in ${place}.`
      case "stuck": return "The PCO draft is taking longer than expected. Try again."
      default: return ""
    }
  }

  _pcoButtonHtml(c, noun, place, builder, label, note) {
    return `<div class="px-5 py-3 border-b border-gray-200" data-cor-pco-section>
      ${label}
      ${note ? `<p class="text-[13px] text-amber-700 mb-1.5">${this._escapeHtml(note)}</p>` : ""}
      <button type="button"
              data-action="click->action-items#convertCorToPco"
              data-convert-url="${c.convert_url}" data-finances-url="${c.finances_url}"
              data-noun="${this._escapeHtml(noun)}" data-place="${place}" data-builder="${builder ? "1" : ""}"
              class="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark active:bg-brand-700 text-white text-[13px] font-medium rounded-lg px-3 py-1.5 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/></svg>
        Create PCO draft with AI
      </button>
      <p class="text-[13px] text-gray-400 mt-1.5">Reads this ${this._escapeHtml(noun)}, its discussion, and attached files, then prefills a draft PCO under Finances → ${place}.</p>
    </div>`
  }

  _corPcoDoneHtml(seq, noun, place, url, label, inner = false) {
    const body = `${label}
      <p class="text-[13px] text-gray-800 flex items-center gap-1.5">
        <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
        PCO #${seq} draft was created from this ${this._escapeHtml(noun)}
      </p>
      <a href="${url}" class="inline-flex items-center gap-1 text-[13px] text-brand hover:underline mt-1">Review it in ${place} →</a>`
    return inner ? body : `<div class="px-5 py-3 border-b border-gray-200 bg-emerald-50/60" data-cor-pco-section>${body}</div>`
  }

  // While a ticked status makes the draft in the background, re-read the item
  // every few seconds and swap just this box when it's done (up to ~2 min).
  _schedulePcoPoll() {
    if (this._pcoPollTimer && this._pcoPollItem === this._activeItemId) return
    clearTimeout(this._pcoPollTimer)
    const itemId = this._activeItemId
    this._pcoPollItem = itemId
    let tries = 0
    const tick = async () => {
      this._pcoPollTimer = null
      if (!itemId || itemId !== this._activeItemId || ++tries > 40) return
      try {
        const url = this.itemsUrlValue.replace(".json", `/${itemId}/timeline`)
        const resp = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
        const data = resp.ok ? await resp.json() : null
        const c = data?.cor_pco
        if (itemId !== this._activeItemId) return
        if (c && c.state !== "pending") {
          const section = this.detailContentTarget.querySelector("[data-cor-pco-section]")
          const html = this._renderCorPcoBlock(data)
          if (section) section.outerHTML = html
          if (c.converted_sequence) {
            this._showPcoChip(c.converted_sequence, c.converted_url || c.finances_url)
            this._showToast(`PCO #${c.converted_sequence} draft created — review it in ${c.place}`)
          }
          return
        }
      } catch (_) { /* try again on the next tick */ }
      this._pcoPollTimer = setTimeout(tick, 3000)
    }
    this._pcoPollTimer = setTimeout(tick, 3000)
  }

  async convertCorToPco(event) {
    const btn = event.currentTarget
    if (btn.disabled) return
    const url = btn.dataset.convertUrl
    const financesUrl = btn.dataset.financesUrl
    const noun = btn.dataset.noun || "COR"
    const place = btn.dataset.place || "Change Events"
    const builder = !!btn.dataset.builder
    btn.disabled = true
    const originalHtml = btn.innerHTML
    btn.innerHTML = `${this._pcoSpinner()} Reading the ${this._escapeHtml(noun)}…`

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the PCO draft.")

      const section = this.detailContentTarget.querySelector("[data-cor-pco-section]")
      if (section) {
        section.classList.add("bg-emerald-50/60")
        section.innerHTML = this._corPcoDoneHtml(data.sequence, noun, place, data.url || financesUrl, this._pcoLabel(builder), true)
      }
      this._showPcoChip(data.sequence, data.url || financesUrl)
      const note = builder && data.ai === false ? " — no prices could be read, add them on the PCO" : ""
      this._showToast(`PCO #${data.sequence} draft created — review it in ${place}${note}`)
    } catch (err) {
      btn.disabled = false
      btn.innerHTML = originalHtml
      this._showToast(err.message, "alert")
    }
  }

  reopenItem() {
    if (this.hasReopenConfirmModalTarget) {
      this.reopenConfirmModalTarget.classList.remove("hidden")
    }
  }

  cancelReopen() {
    if (this.hasReopenConfirmModalTarget) {
      this.reopenConfirmModalTarget.classList.add("hidden")
    }
  }

  async confirmReopen() {
    this.cancelReopen()
    await this._submitAction({ reopen: true })
  }

  // ── Action dropdown ─────────────────────────────────────────────

  toggleActionDropdown() {
    if (!this.hasActionDropdownMenuTarget) return
    this.actionDropdownMenuTarget.classList.toggle("hidden")
  }

  selectAction(event) {
    const actionId = event.currentTarget.dataset.actionId
    if (!actionId) return
    if (this.hasActionDropdownMenuTarget) this.actionDropdownMenuTarget.classList.add("hidden")

    // Find action metadata from cached kind_actions
    const action = this._kindActions.find(a => String(a.id) === String(actionId))
    if (!action) return

    // Stage the action (replace if one is already staged)
    this._stagedAction = {
      id: action.id,
      name: action.name,
      closes_item: action.closes_item,
      increments_version: action.increments_version
    }
    this._renderStagedAction()
  }

  _onClickOutsideDropdown(event) {
    if (this.hasActionDropdownContainerTarget && !this.actionDropdownContainerTarget.contains(event.target)) {
      if (this.hasActionDropdownMenuTarget) this.actionDropdownMenuTarget.classList.add("hidden")
    }
    if (this.hasBicContainerTarget && event.target.isConnected && !this.bicContainerTarget.contains(event.target)) {
      if (this.hasBicPickerTarget) this.bicPickerTarget.classList.add("hidden")
    }
  }

  // ── "Who has the ball now?" prompt (company setting) ─────────────

  _openBicPrompt(ask = { askBic: true, askStatus: false }) {
    if (!this.hasBicPromptModalTarget) return
    this._bicPromptAsk = ask
    this._bicPromptStatusId = null
    if (this.hasBicPromptBicWrapTarget) this.bicPromptBicWrapTarget.classList.toggle("hidden", !ask.askBic)
    if (this.hasBicPromptStatusWrapTarget) this.bicPromptStatusWrapTarget.classList.toggle("hidden", !ask.askStatus)
    if (this.hasBicPromptTitleTarget) {
      this.bicPromptTitleTarget.textContent = ask.askBic && ask.askStatus ? "Who has the ball now, and did the status change?"
        : ask.askBic ? "Who has the ball now?" : "Did the status change?"
    }
    if (this.hasBicPromptHintTarget) {
      this.bicPromptHintTarget.textContent = ask.askBic && ask.askStatus
        ? "Your update is ready to send. Move Ball in Court and/or pick a new status if that changed — it goes out with your message. Otherwise keep it as is."
        : ask.askBic ? "Your update is ready to send. If it's someone else's turn, move Ball in Court to them — it goes out with your message."
        : "Your update is ready to send. If this changes the status, pick it here — it goes out with your message. Otherwise keep it as is."
    }
    if (ask.askStatus && this.hasBicPromptStatusTarget) {
      const current = this._currentStatusLabel ? ` (${this._currentStatusLabel})` : ""
      this.bicPromptStatusTarget.innerHTML = `<option value="">Keep the current status${this._escapeHtml(current)}</option>` +
        (this._kindActions || []).map(a => `<option value="${this._escapeHtml(String(a.id))}">${this._escapeHtml(a.name)}${a.closes_item ? " — closes the item" : ""}</option>`).join("")
    }
    const item = (this._allItems || []).find(i => String(i.id) === String(this._activeItemId))
    const current = (item && item.ball_in_court) || []
    // Whoever has the ball starts selected (Phelipe 2026-09-07) — Update only
    // lights up once the pick actually changes.
    // A current holder who is not on the team list (an email-only entry)
    // still shows as the dropdown's default, keyed by email.
    const members = this._projectMembers || []
    const keyOf = b => {
      const n = parseInt(b.user_id || b.id, 10)
      return !isNaN(n) && members.some(m => String(m.id) === String(n)) ? n : (b.email ? `e:${String(b.email).toLowerCase()}` : null)
    }
    this._bicPromptExtra = current.filter(b => typeof keyOf(b) === "string")
      .map(b => ({ id: keyOf(b), name: b.name || b.email || "", email: b.email || "", user_id: parseInt(b.user_id || b.id, 10) || null }))
    this._bicPromptCurrentIds = current.map(keyOf).filter(k => k != null)
    this._bicPromptRows = this._bicPromptCurrentIds.length ? this._bicPromptCurrentIds.slice() : [null]
    if (this.hasBicPromptAddBtnTarget) this.bicPromptAddBtnTarget.classList.toggle("hidden", !this.allowMultiBicValue)
    this._renderBicPromptRows()
    this._syncBicPromptApply()
    this.bicPromptModalTarget.classList.remove("hidden")
    this.bicPromptModalTarget.classList.add("flex")
  }

  // ── SLC3-11 (SLCC, 2026-09-27) — target-date email reminders ─────────
  // A label in the detail's people row ("Reminders: 2 days before · every 2
  // days after" - blue when on, grey when off) that opens the pop-up for
  // everyone (Phelipe 2026-09-29): anyone who can comment on the item changes
  // them there, unless its company sets them for every item. The explanation
  // sits highlighted under the choices, the history of changes below it.

  _remindersChipHtml() {
    const r = this._reminders
    if (!r) return ""
    const text = this._escapeHtml(this.i18nRemindersChipValue.replace("%{label}", r.label || ""))
    const bell = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/></svg>`
    const on = r.before != null || r.after != null
    const tone = on ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
    return `<button type="button" data-action="click->action-items#openReminders" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[13px] font-medium cursor-pointer transition-colors ${tone}">${bell}<span>${text}</span></button>`
  }

  // Anyone who can comment on the item, while its company leaves reminders to
  // each item - the server decides (reminders_json `editable`).
  _remindersEditable() {
    return !!this._reminders && !!this._reminders.editable
  }

  // Who changed the item's reminders, newest first.
  _renderRemindersHistory() {
    if (!this.hasRemindersHistoryTarget) return
    const rows = (this._reminders && this._reminders.history) || []
    if (rows.length === 0) {
      this.remindersHistoryTarget.innerHTML = `<p class="text-[13px] text-gray-400">${this._escapeHtml(this.i18nRemindersHistoryEmptyValue)}</p>`
      return
    }
    this.remindersHistoryTarget.innerHTML = `<ul class="divide-y divide-gray-100">${rows.map(h => {
      const tpl = h.created ? this.i18nRemindersHistoryCreatedValue : this.i18nRemindersHistoryChangedValue
      const text = this._escapeHtml(tpl).replace("%{name}", `<span class="font-medium text-gray-800">${this._escapeHtml(h.name || "?")}</span>`)
                                         .replace("%{label}", `<span class="font-medium text-gray-800">${this._escapeHtml(h.label || "")}</span>`)
      return `<li class="flex items-baseline justify-between gap-3 py-1.5 text-[13px] text-gray-600"><span>${text}</span><span class="shrink-0 text-gray-400">${this._escapeHtml(this._displayDateTime(h.at))}</span></li>`
    }).join("")}</ul>`
  }

  openReminders(event) {
    if (event) event.preventDefault()
    if (!this.hasRemindersModalTarget || !this._reminders) return
    const editable = this._remindersEditable()
    this.remindBeforeTarget.value = this._reminders.before == null ? "" : String(this._reminders.before)
    this.remindAfterTarget.value = this._reminders.after == null ? "" : String(this._reminders.after)
    this.remindBeforeTarget.disabled = !editable
    this.remindAfterTarget.disabled = !editable
    this.remindersSaveTarget.classList.toggle("hidden", !editable)
    if (this.hasRemindersCancelTarget) {
      const btn = this.remindersCancelTarget
      btn.textContent = editable ? btn.dataset.labelCancel : btn.dataset.labelClose
    }
    const note = this._reminders.company ? this.i18nRemindersCompanyValue : (editable ? "" : this.i18nRemindersReopenValue)
    if (this.hasRemindersNoteTarget) {
      this.remindersNoteTarget.textContent = note
      this.remindersNoteTarget.classList.toggle("hidden", !note)
    }
    this._remindersError(null)
    this._renderRemindersHistory()
    this.remindersModalTarget.classList.remove("hidden")
    this.remindersModalTarget.classList.add("flex")
    if (editable) this.remindBeforeTarget.focus()
    else if (this.hasRemindersCancelTarget) this.remindersCancelTarget.focus()
    track("Action Item Reminders Opened", { project_id: this.projectIdValue, punchlist_id: this._activeItemId,
                                            can_change: editable, set_by_company: !!this._reminders.company })
  }

  closeReminders(event) {
    if (event) event.preventDefault()
    if (!this.hasRemindersModalTarget) return
    this.remindersModalTarget.classList.add("hidden")
    this.remindersModalTarget.classList.remove("flex")
  }

  async saveReminders() {
    if (!this._activeItemId || this._savingReminders || !this._remindersEditable()) return
    this._savingReminders = true
    this.remindersSaveTarget.disabled = true
    this._remindersError(null)
    try {
      const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}/reminders`)
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({ before: this.remindBeforeTarget.value, after: this.remindAfterTarget.value })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.reminders) throw new Error(data.error || this.i18nRemindersFailedValue)
      this._reminders = data.reminders
      const chip = this.element.querySelector("[data-detail-reminders]")
      if (chip) chip.innerHTML = this._remindersChipHtml()
      this.closeReminders()
      this._showToast(this.i18nRemindersSavedValue)
    } catch (error) {
      this._remindersError(error.message || this.i18nRemindersFailedValue)
    } finally {
      this._savingReminders = false
      this.remindersSaveTarget.disabled = false
    }
  }

  _remindersError(message) {
    if (!this.hasRemindersErrorTarget) return
    this.remindersErrorTarget.textContent = message || ""
    this.remindersErrorTarget.classList.toggle("hidden", !message)
  }

  closeBicPrompt(event) {
    if (event) event.preventDefault()
    if (!this.hasBicPromptModalTarget) return
    this.bicPromptModalTarget.classList.add("hidden")
    this.bicPromptModalTarget.classList.remove("flex")
  }

  // One dropdown of the project team per person on the ball (Phelipe
  // 2026-09-08). With "Allow Multiple Ball in Court" off there is one row.
  _renderBicPromptRows() {
    if (!this.hasBicPromptListTarget) return
    const members = [...(this._bicPromptExtra || []), ...(this._projectMembers || [])]
    const rows = this._bicPromptRows || [null]
    const cls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand cursor-pointer"
    this.bicPromptListTarget.innerHTML = rows.map((id, i) => `
      <div class="flex items-center gap-2" data-bic-row="${i}">
        <select data-action="change->action-items#changeBicPromptRow" data-row-index="${i}" class="${cls}">
          <option value="">${this._escapeHtml("Choose a team member…")}</option>
          ${members.map(m => `<option value="${this._escapeHtml(String(m.id))}" ${String(m.id) === String(id) ? "selected" : ""}>${this._escapeHtml(m.name || m.email)}</option>`).join("")}
        </select>
        ${rows.length > 1 ? `<button type="button" data-action="click->action-items#removeBicPromptRow" data-row-index="${i}" title="Remove" class="text-gray-400 hover:text-red-500 text-[16px] leading-none cursor-pointer">×</button>` : ""}
      </div>`).join("")
  }

  changeBicPromptRow(event) {
    const i = parseInt(event.currentTarget.dataset.rowIndex, 10)
    const val = event.currentTarget.value
    if (!this._bicPromptRows) this._bicPromptRows = [null]
    this._bicPromptRows[i] = val ? (val.startsWith("e:") ? val : parseInt(val, 10)) : null
    this._syncBicPromptApply()
  }

  addBicPromptRow() {
    if (!this.allowMultiBicValue) return
    if (!this._bicPromptRows) this._bicPromptRows = [null]
    this._bicPromptRows.push(null)
    this._renderBicPromptRows()
    this._syncBicPromptApply()
    const selects = this.bicPromptListTarget.querySelectorAll("select")
    selects[selects.length - 1]?.focus()
  }

  removeBicPromptRow(event) {
    const i = parseInt(event.currentTarget.dataset.rowIndex, 10)
    if (!this._bicPromptRows || this._bicPromptRows.length <= 1) return
    this._bicPromptRows.splice(i, 1)
    this._renderBicPromptRows()
    this._syncBicPromptApply()
  }

  _bicPromptPicked() {
    const members = [...(this._bicPromptExtra || []), ...(this._projectMembers || [])]
    const ids = [...new Set((this._bicPromptRows || []).filter(id => id != null).map(String))]
    return ids.map(id => members.find(m => String(m.id) === id)).filter(Boolean)
      .map(m => ({ id: typeof m.id === "string" ? (m.user_id || null) : m.id, name: m.name || m.email, email: m.email || "" }))
  }

  changeBicPromptStatus(event) {
    this._bicPromptStatusId = event.currentTarget.value || null
    this._syncBicPromptApply()
  }

  _bicPromptBicChanged() {
    const ask = this._bicPromptAsk || { askBic: true }
    if (!ask.askBic) return false
    const picked = [...new Set((this._bicPromptRows || []).filter(id => id != null).map(String))].sort()
    const current = (this._bicPromptCurrentIds || []).map(String).sort()
    const same = picked.length === current.length && picked.every((id, i) => id === current[i])
    return picked.length > 0 && !same
  }

  // Update is only offered when something actually changes: the ball moved
  // to someone else, and/or a new status was picked.
  _syncBicPromptApply() {
    if (!this.hasBicPromptApplyTarget) return
    const ask = this._bicPromptAsk || { askBic: true, askStatus: false }
    const statusChosen = !!(ask.askStatus && this._bicPromptStatusId)
    this.bicPromptApplyTarget.disabled = !(this._bicPromptBicChanged() || statusChosen)
  }

  // "Keep as is & send": the message goes out exactly as written.
  async keepBicAndSend(event) {
    if (event) event.preventDefault()
    this.closeBicPrompt()
    await this.sendMessage({ bicPromptDone: true })
  }

  // "Update & send": the ball pick and/or the status are staged through the
  // regular flows and ride along with the message in the same send (one
  // notification).
  async applyBicPrompt() {
    const ask = this._bicPromptAsk || { askBic: true, askStatus: false }
    const bicChanged = this._bicPromptBicChanged()
    const statusId = ask.askStatus ? this._bicPromptStatusId : null
    if (!bicChanged && !statusId) return
    this.closeBicPrompt()
    if (bicChanged) {
      const sel = this._bicPromptPicked()
      if (sel.length) {
        this._bicSelected = sel.map(s => ({ ...s }))
        this.stageBicTransfer()
      }
    }
    if (statusId) {
      const action = (this._kindActions || []).find(a => String(a.id) === String(statusId))
      if (action) {
        this._stagedAction = { id: action.id, name: action.name, closes_item: action.closes_item, increments_version: action.increments_version }
        this._renderStagedAction()
      }
    }
    await this.sendMessage({ bicPromptDone: true })
  }

  // ── BIC picker (multi-select) ──────────────────────────────────

  toggleBicPicker() {
    if (!this.hasBicPickerTarget) return
    const isHidden = this.bicPickerTarget.classList.contains("hidden")
    if (isHidden) {
      this._bicSelected = []
      this._renderBicMembers()
      this._renderBicChips()
      this.bicPickerTarget.classList.remove("hidden")
      if (this.hasBicSearchTarget) {
        this.bicSearchTarget.value = ""
        this.bicSearchTarget.focus()
      }
    } else {
      this.bicPickerTarget.classList.add("hidden")
    }
  }

  filterBicMembers() {
    this._renderBicMembers()
  }

  _renderBicMembers() {
    if (!this.hasBicMemberListTarget) return
    const query = (this.hasBicSearchTarget ? this.bicSearchTarget.value : "").toLowerCase().trim()
    const selectedIds = (this._bicSelected || []).map(s => s.id)
    const members = this._projectMembers.filter(m => {
      if (!query) return true
      return (m.name || "").toLowerCase().includes(query) || (m.email || "").toLowerCase().includes(query)
    })

    if (members.length === 0) {
      this.bicMemberListTarget.innerHTML = '<p class="px-3 py-2 text-[13px] text-gray-400">No members found</p>'
      return
    }

    this.bicMemberListTarget.innerHTML = members.map(m => {
      const name = this._escapeHtml(m.name || m.email)
      const initials = (m.name || m.email || "?").split(/\s+/).map(w => w[0]).join("").substring(0, 2).toUpperCase()
      const isSelected = selectedIds.includes(m.id)
      const checkCls = isSelected ? "bg-brand border-brand text-white" : "border-gray-300 bg-white"
      const checkSvg = isSelected ? '<svg class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>' : ""
      const rowBg = isSelected ? "bg-brand/5" : ""
      return `<button type="button"
        class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2.5 ${rowBg}"
        data-action="click->action-items#toggleBicMember"
        data-member-id="${m.id}"
        data-member-name="${this._escapeHtml(m.name || m.email)}"
        data-member-email="${this._escapeHtml(m.email || "")}">
        <span class="w-4 h-4 rounded flex items-center justify-center border shrink-0 ${checkCls}">${checkSvg}</span>
        <span class="relative w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-500 shrink-0">${initials}${m.avatar_url && !m.avatar_url.includes("avatar.png") ? `<img src="${this._escapeHtml(m.avatar_url)}" class="absolute inset-0 w-6 h-6 rounded-full object-cover opacity-0 transition-opacity duration-300" data-controller="img-handler" data-action="load->img-handler#showLoaded error->img-handler#removeSelf">` : ""}</span>
        <span class="truncate">${name}</span>
      </button>`
    }).join("")
  }

  _renderBicChips() {
    if (!this.hasBicSelectedChipsTarget || !this.hasBicConfirmRowTarget) return
    const selected = this._bicSelected || []

    if (selected.length === 0) {
      this.bicSelectedChipsTarget.classList.add("hidden")
      this.bicSelectedChipsTarget.classList.remove("flex")
      this.bicConfirmRowTarget.classList.add("hidden")
      return
    }

    this.bicSelectedChipsTarget.classList.remove("hidden")
    this.bicSelectedChipsTarget.classList.add("flex")
    this.bicConfirmRowTarget.classList.remove("hidden")

    this.bicSelectedChipsTarget.innerHTML = selected.map(s => {
      const shortName = this._escapeHtml(s.name || s.email)
      return `<span class="inline-flex items-center gap-1 px-1.5 py-px rounded text-[13px] font-medium bg-brand/10 text-brand">
        ${shortName}
        <button type="button" data-action="click->action-items#removeBicMember" data-member-id="${s.id}" class="hover:text-brand-dark cursor-pointer">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </span>`
    }).join("")
  }

  toggleBicMember(event) {
    const btn = event.currentTarget
    const id = parseInt(btn.dataset.memberId, 10)
    const name = btn.dataset.memberName
    const email = btn.dataset.memberEmail

    if (!this._bicSelected) this._bicSelected = []
    const idx = this._bicSelected.findIndex(s => s.id === id)
    const isNowSelected = idx < 0
    // "Allow Multiple Ball in Court" off → picking someone replaces the
    // previous pick (Phelipe 2026-07-28); the server clamps too.
    if (isNowSelected && !this.allowMultiBicValue) {
      this._bicSelected.forEach((prev) => {
        const row = this.bicMemberListTarget.querySelector(`[data-member-id="${prev.id}"]`)
        const box = row?.querySelector("span:first-child")
        if (box) {
          box.classList.remove("bg-brand", "border-brand", "text-white")
          box.classList.add("border-gray-300", "bg-white")
          box.innerHTML = ""
        }
        row?.classList.remove("bg-brand/5")
      })
      this._bicSelected = []
    }
    if (idx >= 0) {
      this._bicSelected.splice(idx, 1)
    } else {
      this._bicSelected.push({ id, name, email })
    }

    // Update only the clicked row's checkbox + background (avoid full re-render which flashes avatars)
    const checkbox = btn.querySelector("span:first-child")
    if (checkbox) {
      if (isNowSelected) {
        checkbox.classList.remove("border-gray-300", "bg-white")
        checkbox.classList.add("bg-brand", "border-brand", "text-white")
        checkbox.innerHTML = '<svg class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>'
        btn.classList.add("bg-brand/5")
      } else {
        checkbox.classList.remove("bg-brand", "border-brand", "text-white")
        checkbox.classList.add("border-gray-300", "bg-white")
        checkbox.innerHTML = ""
        btn.classList.remove("bg-brand/5")
      }
    }

    this._renderBicChips()
  }

  removeBicMember(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.memberId, 10)
    if (!this._bicSelected) return
    this._bicSelected = this._bicSelected.filter(s => s.id !== id)

    // Update only the unchecked row (avoid full re-render which flashes avatars)
    const row = this.bicMemberListTarget.querySelector(`[data-member-id="${id}"]`)
    if (row) {
      const checkbox = row.querySelector("span:first-child")
      if (checkbox) {
        checkbox.classList.remove("bg-brand", "border-brand", "text-white")
        checkbox.classList.add("border-gray-300", "bg-white")
        checkbox.innerHTML = ""
      }
      row.classList.remove("bg-brand/5")
    }

    this._renderBicChips()
  }

  // Legacy alias
  confirmBicTransfer() { this.stageBicTransfer() }

  stageBicTransfer() {
    if (!this._bicSelected || this._bicSelected.length === 0) return

    // Close picker
    if (this.hasBicPickerTarget) this.bicPickerTarget.classList.add("hidden")

    // Stage BIC — will be sent with the next sendMessage()
    this._stagedBic = this._bicSelected.map(s => ({ user_id: s.id, name: s.name, email: s.email }))
    this._renderStagedBicChips()
  }

  _renderStagedBicChips() {
    if (!this.hasStagedBicChipsTarget || !this.hasStagedBicContainerTarget) return

    if (!this._stagedBic || this._stagedBic.length === 0) {
      this.stagedBicContainerTarget.classList.add("hidden")
      this.stagedBicChipsTarget.innerHTML = ""
      return
    }

    this.stagedBicContainerTarget.classList.remove("hidden")
    this.stagedBicChipsTarget.innerHTML = this._stagedBic.map((member, idx) => {
      const displayName = (member.name || member.email || "").split("@")[0]
      return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] bg-gray-200 text-gray-700">
        ${this._escapeHtml(displayName)}
        <button type="button" data-staged-bic-idx="${idx}"
                class="text-gray-400 hover:text-gray-600 cursor-pointer">
          <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
        </button>
      </span>`
    }).join("")

    // Wire up remove buttons
    this.stagedBicChipsTarget.querySelectorAll("[data-staged-bic-idx]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        const idx = parseInt(btn.dataset.stagedBicIdx, 10)
        this._stagedBic.splice(idx, 1)
        if (this._stagedBic.length === 0) this._stagedBic = null
        this._renderStagedBicChips()
      })
    })
    this._updateSendButtonState()
    this._scrollChatToBottom()
  }

  _renderStagedAction() {
    if (!this.hasStagedActionContainerTarget) return

    if (!this._stagedAction) {
      this._notifyParticipants = true
      this.stagedActionContainerTarget.classList.add("hidden")
      this.stagedActionContainerTarget.innerHTML = ""
      this._updateSendButtonState()
      this._scrollChatToBottom()
      return
    }

    const a = this._stagedAction
    const name = this._escapeHtml(a.name)
    const nameLower = a.name.toLowerCase()

    // Icons — same SVGs as _renderStatusBadge
    const checkIcon = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
    const xIcon = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>`
    const actionIcon = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>`
    const versionIcon = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0-6.75 6.75M12 4.5l6.75 6.75"/></svg>`

    // Color + icon logic — matches _renderStatusBadge exactly
    let color, icon
    if (nameLower.includes("reject")) {
      color = "#dc2626"; icon = xIcon
    } else if (a.closes_item) {
      color = "#16a34a"; icon = checkIcon
    } else if (a.increments_version) {
      color = "#2563eb"; icon = versionIcon
    } else {
      color = "#d97706"; icon = actionIcon
    }

    // Badges
    const closeBadge = a.closes_item ? '<span class="text-[13px] font-medium text-red-500 bg-red-50 px-1 py-px rounded">closes</span>' : ""
    const versionBadge = a.increments_version ? '<span class="text-[13px] font-medium text-brand bg-brand/10 px-1 py-px rounded">v++</span>' : ""
    // Picking this status makes the PCO draft (Settings → Action Item Types, 2026-10-15).
    const pcoBadge = a.creates_pco_draft ? '<span class="text-[13px] font-medium text-purple-700 bg-purple-50 px-1 py-px rounded">PCO</span>' : ""

    const chip = `<span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-current/20 text-[13px] font-medium" style="color:${color}; background: color-mix(in srgb, ${color} 8%, white);">
      ${icon}
      ${name}
      ${closeBadge}${versionBadge}${pcoBadge}
      <button type="button" class="ml-0.5 opacity-50 hover:opacity-80 cursor-pointer" data-action="click->action-items#removeStagedAction">
        <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
      </button>
    </span>`

    // ── "Notify participants" toggle (right-aligned on the staged action row) ──
    // Default on. Turning it off sends the status change silently (no emails/push).
    const notifyOn = this._notifyParticipants !== false
    const bellIcon = `<svg class="w-4 h-4 shrink-0 ${notifyOn ? "text-gray-400" : "text-gray-300"}" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/></svg>`
    const notifyToggle = `<button type="button" role="switch" aria-checked="${notifyOn}"
        data-action="click->action-items#toggleNotify" data-action-items-target="notifySwitch"
        title="${this._escapeHtml(this.i18nNotifyHintValue)}"
        class="inline-flex items-center gap-1.5 shrink-0 cursor-pointer select-none">
        ${bellIcon}
        <span class="text-[13px] ${notifyOn ? "text-gray-600" : "text-gray-400"}">${this._escapeHtml(this.i18nNotifyValue)}</span>
        <span data-action-items-target="notifyTrack" class="relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors ${notifyOn ? "bg-brand" : "bg-gray-300"}">
          <span data-action-items-target="notifyKnob" class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${notifyOn ? "translate-x-[16px]" : "translate-x-0.5"}"></span>
        </span>
      </button>`

    this.stagedActionContainerTarget.innerHTML = chip + notifyToggle

    this.stagedActionContainerTarget.classList.remove("hidden")
    this._updateSendButtonState()
    this._scrollChatToBottom()
  }

  removeStagedAction() {
    this._stagedAction = null
    this._renderStagedAction()
  }

  // Flip the "Notify participants" switch. Updates the DOM in place (no re-render)
  // so the chat doesn't scroll. sendMessage reads this._notifyParticipants.
  toggleNotify() {
    this._notifyParticipants = !this._notifyParticipants
    this._updateNotifyToggle()
  }

  _updateNotifyToggle() {
    const on = this._notifyParticipants !== false
    if (this.hasNotifySwitchTarget) this.notifySwitchTarget.setAttribute("aria-checked", on ? "true" : "false")
    if (this.hasNotifyTrackTarget) {
      this.notifyTrackTarget.classList.toggle("bg-brand", on)
      this.notifyTrackTarget.classList.toggle("bg-gray-300", !on)
    }
    if (this.hasNotifyKnobTarget) {
      this.notifyKnobTarget.classList.toggle("translate-x-[16px]", on)
      this.notifyKnobTarget.classList.toggle("translate-x-0.5", !on)
    }
  }

  // ── Target date picker ──────────────────────────────────────────────

  toggleTargetDatePicker() {
    if (!this.hasTargetDateInputTarget || !this.hasTargetDateBtnTarget) return
    if (!this._targetDateFp) {
      this._targetDateFp = flatpickr(this.targetDateInputTarget, {
        dateFormat: "Y-m-d",
        altInput: false,
        disableMobile: true,
        appendTo: document.body,
        positionElement: this.targetDateBtnTarget,
        onOpen: (selectedDates, _dateStr, instance) => {
          // Must sit above the slide-over's z-index
          instance.calendarContainer.style.zIndex = "2147483005"
          // Add label header if not already present
          if (!instance.calendarContainer.querySelector("[data-fp-label]")) {
            const label = document.createElement("div")
            label.setAttribute("data-fp-label", "")
            label.textContent = "Select a new target date"
            label.style.cssText = "padding:8px 12px;font-size:13px;font-weight:500;color:#374151;border-bottom:1px solid #e5e7eb;background:#f9fafb;"
            instance.calendarContainer.prepend(label)
          }
          if (selectedDates.length === 0) instance.jumpToDate(new Date())
        },
        onChange: (selectedDates, dateStr) => {
          if (dateStr) {
            this._stagedTargetDate = dateStr
            this._renderStagedTargetDate()
            this._targetDateFp.close()
          }
        }
      })
    }
    this._targetDateFp.open()
  }

  _renderStagedTargetDate() {
    if (!this.hasStagedTargetDateContainerTarget || !this.hasStagedTargetDateLabelTarget) return

    if (!this._stagedTargetDate) {
      this.stagedTargetDateContainerTarget.classList.add("hidden")
      this._updateSendButtonState()
      this._scrollChatToBottom()
      return
    }

    const displayDate = this._displayDate(this._stagedTargetDate)
    this.stagedTargetDateLabelTarget.innerHTML = `<span class="shrink-0">→</span>
      <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] bg-gray-200 text-gray-700">
        ${displayDate}
        <button type="button" data-action="click->action-items#removeStagedTargetDate"
                class="text-gray-400 hover:text-gray-600 cursor-pointer">
          <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
        </button>
      </span>`
    this.stagedTargetDateContainerTarget.classList.remove("hidden")
    this._updateSendButtonState()
    this._scrollChatToBottom()
  }

  removeStagedTargetDate() {
    this._stagedTargetDate = null
    this._renderStagedTargetDate()
  }

  _updateDetailTargetDate(newDate) {
    // Update the "Due [date]" in the detail header metadata row
    const metaContainer = this.hasDetailContentTarget && this.detailContentTarget.querySelector("[data-detail-meta]")
    if (!metaContainer) return
    const dueSpan = metaContainer.querySelector("[data-detail-due]")
    if (dueSpan) dueSpan.innerHTML = this._detailDueHtml(newDate, !!metaContainer.querySelector("[data-detail-author]"))
  }

  // "Due Dec 22, 2025 ·" beside the title (the dot only when "added by" follows).
  _detailDueHtml(targetDate, beforeAuthor) {
    if (!targetDate) return ""
    return `<span class="text-gray-500">${this.i18nDueValue}&nbsp;</span><span class="text-gray-800 font-medium">${this._displayDate(targetDate)}</span>` +
      (beforeAuthor ? '<span class="text-gray-300 mx-1">·</span>' : "")
  }

  _populateActionDropdown(data, pl) {
    if (!this.hasActionDropdownContainerTarget) return

    // Kinds with no configured status actions still need a way for permitted
    // users (approver / creator / owner, per the company toggles) to close the
    // item — offer the built-in "Mark Complete" (Kloepfer item 12). The
    // "__complete__" sentinel is handled by PunchlistService as approve+close.
    // Builder module only (Phelipe 2026-07-30) — owner projects keep their
    // pre-batch behavior; the server refuses the sentinel there too.
    let kindActions = data.kind_actions || []
    if (kindActions.length === 0 && data.can_approve && this.builderProjectValue) {
      kindActions = [{ id: "__complete__", name: this.i18nMarkCompleteValue, closes_item: true, increments_version: false }]
    }

    // Cache kind_actions for staged action lookup
    this._kindActions = kindActions

    const showDropdown = data.can_approve &&
      kindActions.length > 0 &&
      pl.display_status !== "resolved"
    // The before-send pop-up offers the status too (Larry 2026-09-08) — only
    // when this person could change it from the composer anyway.
    this._canChangeStatus = !!showDropdown
    this._currentStatusLabel = pl.status_label || pl.display_status || ""

    if (showDropdown) {
      this.actionDropdownContainerTarget.classList.remove("hidden")
      let menuHtml = ""
      kindActions.forEach(action => {
        const closeBadge = action.closes_item
          ? '<span class="ml-auto text-[13px] font-medium text-red-500 bg-red-50 px-1 py-px rounded">closes</span>'
          : ""
        const versionBadge = action.increments_version
          ? '<span class="ml-auto text-[13px] font-medium text-brand bg-brand/10 px-1 py-px rounded">v++</span>'
          : ""
        // Picking it makes the PCO draft.
        const pcoBadge = action.creates_pco_draft
          ? `<span class="${action.closes_item || action.increments_version ? "" : "ml-auto "}text-[13px] font-medium text-purple-700 bg-purple-50 px-1 py-px rounded" title="Makes a PCO draft">PCO</span>`
          : ""
        menuHtml += `<button type="button"
          class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2"
          data-action="click->action-items#selectAction"
          data-action-id="${action.id}">
          <span>${this._escapeHtml(action.name)}</span>
          ${closeBadge}${versionBadge}${pcoBadge}
        </button>`
      })
      this.actionDropdownMenuTarget.innerHTML = menuHtml
    } else {
      this.actionDropdownContainerTarget.classList.add("hidden")
    }
  }

  async _submitAction(extraParams) {
    if (!this._activeItemId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const url = this.itemsUrlValue.replace(".json", `/${this._activeItemId}/add_comment`)
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify({ comment: { comment: "", ...extraParams } })
      })

      if (response.ok) {
        const result = await response.json()
        await this._loadTimeline(this._activeItemId)
        if (result.row_update) this._updateTableRow(this._activeItemId, result.row_update)
        if (result.pending_send && result.pending_send.anchor) {
          this._showUndoBar(result.pending_send, { notifiedNames: result.notified_names || [] })
        } else if (result.notified_names && result.notified_names.length > 0) {
          // Its grey "Emailed to ..." line shows up once the emails are out.
          this._scheduleEmailActivityRefresh()
          this._showDrawerToast(this.i18nNotifiedValue)
        }
      }
    } catch (e) {
      console.error("[ActionItems] submitAction error:", e)
    }
  }

  // ── Modal management ───────────────────────────────────────────────

  _closeModal() {
    this._modalOpen = false
    this._currentReaders = []
    this._clearStagedState()
    document.removeEventListener("keydown", this._onKeydown)
    this._unsubscribeChannel()

    this.detailBackdropTarget.classList.add("opacity-0")
    this.detailPanelTarget.classList.add("translate-x-full")

    setTimeout(() => {
      this.detailModalTarget.classList.add("hidden")
    }, 300)

    this._activeItemId = null
    history.replaceState(null, "", this.baseUrlValue)
  }

  navigateToEdit(event) {
    event.preventDefault()
    const editUrl = this.editLinkTarget.href
    if (!editUrl || editUrl === "#") return
    // Push the clean index URL so browser back from edit returns to the table
    history.pushState(null, "", this.baseUrlValue)
    window.location.href = editUrl
  }

  _onKeydown(event) {
    if (!this._modalOpen) return
    if (event.key === "Escape") this._closeModal()
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      // Don't hijack arrow keys while the user is editing text in the comment box,
      // title input, date picker, etc. — they're moving the text cursor.
      const el = event.target
      const tag = el?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) return
      if (event.key === "ArrowLeft") this.prevItem()
      if (event.key === "ArrowRight") this.nextItem()
    }
  }

  _checkDeepLink() {
    if (this.deepLinkIdValue) {
      this._pendingDeepLinkId = this.deepLinkIdValue
    }
  }

  // ── ActionCable ────────────────────────────────────────────────────

  _subscribeChannel(itemId) {
    this._unsubscribeChannel()

    this._subscription = getConsumer().subscriptions.create(
      { channel: "PendingItemChatChannel", pending_item_id: itemId },
      { received: (data) => this._onChannelReceived(data) }
    )
  }

  _unsubscribeChannel() {
    if (this._subscription) {
      this._subscription.unsubscribe()
      this._subscription = null
    }
  }

  _onChannelReceived(data) {
    if (data.type === "new_message") {
      const msg = data.message
      // Skip own messages to avoid duplicates (we already append after sending)
      if (msg.user_id === this.currentUserIdValue) return

      // If slide-over is open for this item, append the message
      if (this._modalOpen && this._activeItemId) {
        // Status/action events need full reload (header updates)
        if (msg.approval_event || msg.completion_event || msg.action_name) {
          this._loadTimeline(this._activeItemId)
        } else {
          this._appendMessage({
            id: msg.id,
            type: "comment",
            comment: msg.comment,
            user_id: msg.user_id,
            user_name: msg.user_name,
            user_initials: msg.user_initials,
            user_avatar_url: null,
            created_at: msg.created_at,
            attachments: msg.attachments || []
          })
        }
        // Their message's grey "Emailed to ..." line, once its emails are out.
        this._scheduleEmailActivityRefresh()
      }

      // Update message count in table row
      this._updateRowMessageCount(data.punchlist_id || this._activeItemId)
    } else if (data.type === "status_change") {
      // Skip own status changes (already handled inline after sendMessage)
      if (data.user_id === this.currentUserIdValue) return

      const plId = String(data.punchlist_id)

      // Update table row status chip
      const row = this.hasTableBodyTarget
        ? this.tableBodyTarget.querySelector(`tr[data-item-id="${plId}"]`)
        : null
      if (row) {
        const cells = row.querySelectorAll("td")
        if (cells[4]) cells[4].innerHTML = this._statusChip(data.display_status, data.approved_type, data.action_name, data.latest_action)
      }

      // If slide-over is open for this item, reload timeline to get updated status
      if (this._modalOpen && String(this._activeItemId) === plId) {
        this._loadTimeline(this._activeItemId)
      }
    } else if (data.type === "bic_change") {
      const msg = data.message
      // Skip own BIC changes (already handled inline after sendMessage)
      if (msg && msg.user_id === this.currentUserIdValue) return

      if (this._modalOpen && this._activeItemId) {
        if (msg) {
          this._appendMessage({
            type: "bic_move",
            comment: msg.comment,
            user_id: msg.user_id,
            user_name: msg.user_name,
            user_initials: msg.user_initials,
            user_avatar_url: null,
            created_at: msg.created_at || new Date().toISOString(),
            attachments: []
          })
        }
        // Update header BIC chips from project members data
        const enrichedBic = this._enrichBicFromMembers(data.ball_in_court || [])
        this._updateDetailBicChips(enrichedBic, null)
      }

      // Update table row BIC chips
      if (data.punchlist_id && this.hasTableBodyTarget) {
        const plId = String(data.punchlist_id)
        const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${plId}"]`)
        if (row) {
          const cells = row.querySelectorAll("td")
          const enrichedBic = this._enrichBicFromMembers(data.ball_in_court || [])
          if (cells[2]) cells[2].innerHTML = this._bicChips(enrichedBic, null)
        }
      }
    } else if (data.type === "target_date_change") {
      const msg = data.message
      // Skip own target date changes (already handled inline after sendMessage)
      if (msg && msg.user_id === this.currentUserIdValue) return

      if (this._modalOpen && this._activeItemId && String(data.punchlist_id) === String(this._activeItemId)) {
        if (msg) {
          this._appendMessage({
            type: "target_date_change",
            comment: msg.comment,
            user_id: msg.user_id,
            user_name: msg.user_name,
            user_initials: msg.user_initials,
            user_avatar_url: null,
            created_at: msg.created_at || new Date().toISOString(),
            attachments: []
          })
        }
        // Update detail header due date
        this._updateDetailTargetDate(data.target_date)
      }

      // Update table row due date cell
      if (data.punchlist_id && this.hasTableBodyTarget) {
        const plId = String(data.punchlist_id)
        const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${plId}"]`)
        if (row) {
          const cells = row.querySelectorAll("td")
          if (cells[5] && data.target_date) {
            cells[5].innerHTML = `<span class="text-[13px] text-gray-500">${this._displayDate(data.target_date)}</span>`
          }
        }
        // Update in-memory item
        const item = this._allItems.find(i => String(i.id) === plId)
        if (item) {
          item.target_date = data.target_date
          item.days_overdue = data.days_overdue
          item.is_delayed = data.is_delayed
        }
      }
    } else if (data.type === "delete_message") {
      // Remove deleted message from chat
      if (this._modalOpen && this._chatContainer) {
        const bubble = this._chatContainer.querySelector(`[data-comment-id="${data.comment_id}"]`)
        if (bubble) bubble.remove()
      }
    } else if (data.type === "delete_attachment") {
      if (this._modalOpen) {
        this._removeAttachmentFromUI(data.attachment_id)
      }
    } else if (data.type === "read_receipt") {
      // Real-time read receipt: add reader to "Seen by" chips (deduplicate)
      if (this._modalOpen && this._currentReaders) {
        const reader = data.reader
        if (reader && !this._currentReaders.find(r => r.id === reader.id)) {
          this._currentReaders.push(reader)
        }
        this._renderSeenBy(this._currentReaders)
      }
    }
  }

  _updateRowMessageCount(itemId) {
    if (!itemId || !this.hasTableBodyTarget) return
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${itemId}"]`)
    if (!row) return

    const msgCell = row.querySelector("td:last-child")
    if (!msgCell) return

    // Increment the displayed count with red unread badge
    const badge = msgCell.querySelector("span")
    if (badge && badge.classList.contains("bg-red-500")) {
      const countEl = badge.querySelector("span:last-child")
      if (countEl) {
        const current = parseInt(countEl.textContent, 10) || 0
        countEl.textContent = current + 1
      }
    } else {
      const icon = `<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"/></svg>`
      msgCell.innerHTML = `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white">
        ${icon}<span class="text-[13px] font-bold">1</span>
      </span>`
    }

    // Also update in-memory unread_count
    const item = this._allItems.find(i => String(i.id) === String(itemId))
    if (item) item.unread_count = (item.unread_count || 0) + 1
  }

  // Update a single table row in-place from row_update JSON (avoids full table reload)
  _updateTableRow(itemId, data) {
    if (!this.hasTableBodyTarget) return
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${itemId}"]`)
    if (!row) return

    const cells = row.querySelectorAll("td")
    // cells: [0] Type, [1] Title, [2] BIC, [3] Approvers, [4] Status, [5] Due, [6] Messages
    const muted = data.display_status === "resolved"

    // Status chip (cell 4)
    if (cells[4]) {
      cells[4].innerHTML = this._statusChip(data.display_status, data.approved_type, data.action_name, data.latest_action)
    }

    // BIC chips (cell 2) + Approvers (cell 3)
    if (muted && data.resolved_by) {
      if (cells[2]) cells[2].innerHTML = ""
      if (cells[3]) {
        const rb = data.resolved_by
        const rawName = rb.name || "Unknown"
        const displayName = this._escapeHtml(rawName)
        const initials = this._escapeHtml(rb.initials || rawName.split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2))
        const fallbackBg = "bg-gray-100 text-gray-400"
        const avatar = rb.avatar_url && !rb.avatar_url.includes("avatar.png")
          ? `<span class="relative w-7 h-7 shrink-0"><span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium absolute inset-0">${initials}</span><img src="${this._escapeHtml(rb.avatar_url)}" class="w-7 h-7 rounded-full object-cover absolute inset-0 opacity-0 transition-opacity duration-300" data-controller="img-handler" data-img-handler-opacity-value="0.4" data-action="load->img-handler#revealOverInitials error->img-handler#removeSelf"></span>`
          : `<span class="w-7 h-7 rounded-full ${fallbackBg} flex items-center justify-center text-[10px] font-medium shrink-0">${initials}</span>`
        const action = data.approved_type === "closed" ? "closed" : "approved"
        cells[3].innerHTML = `<span class="inline-flex items-center gap-1 rounded-full pl-px pr-2 py-px bg-gray-50 text-gray-400 text-[13px] italic">${avatar} ${displayName} ${action}</span>`
      }
    } else {
      if (cells[2]) cells[2].innerHTML = this._bicChips(data.ball_in_court || [], data.display_status)
      if (cells[3]) cells[3].innerHTML = this._approverChips(data.approvers || [], muted)
    }

    // Due date (cell 5) — update if target_date present in data
    if (cells[5] && data.target_date !== undefined) {
      const fakeItem = { target_date: data.target_date, display_status: data.display_status, days_overdue: data.days_overdue }
      cells[5].innerHTML = this._dueDateHtml(fakeItem)
    }

    // Message badge (cell 6) — update if unread_count/attached_messages_sum present
    if (cells[6] && data.attached_messages_sum !== undefined) {
      cells[6].innerHTML = this._messageBadge(data.unread_count || 0, data.attached_messages_sum || 0)
    }

    // Update row muted styling
    // Note: the text-gray class lives on the <td> (set in _renderRow), not the <span>
    if (muted) {
      row.classList.add("bg-gray-50")
      row.classList.remove("bg-white")
      // Mute type badge (cell 0)
      if (cells[0]) cells[0].querySelectorAll("span").forEach(s => { s.classList.add("opacity-50") })
      // Mute the title (cell 1) — grey only, no strikeout.
      if (cells[1]) {
        cells[1].classList.add("text-gray-400")
        cells[1].classList.remove("text-gray-700", "line-through")
      }
      // Mute due date (cell 5)
      if (cells[5]) cells[5].classList.add("text-gray-400")
    } else {
      row.classList.remove("bg-gray-50")
      row.classList.add("bg-white")
      if (cells[0]) cells[0].querySelectorAll("span").forEach(s => { s.classList.remove("opacity-50") })
      if (cells[1]) {
        cells[1].classList.remove("text-gray-400", "line-through")
        cells[1].classList.add("text-gray-700")
      }
      if (cells[5]) cells[5].classList.remove("text-gray-400")
    }

    // Update in-memory item data
    const item = this._allItems.find(i => String(i.id) === String(itemId))
    if (item) {
      item.display_status = data.display_status
      item.approved_type = data.approved_type
      item.action_name = data.action_name
      item.resolved_by = data.resolved_by
      item.latest_action = data.latest_action
      item.ball_in_court = data.ball_in_court
      item.approvers = data.approvers
      if (data.target_date !== undefined) {
        item.target_date = data.target_date
        item.days_overdue = data.days_overdue
        item.is_delayed = data.is_delayed
      }
      if (data.unread_count !== undefined) item.unread_count = data.unread_count
      if (data.attached_messages_sum !== undefined) item.attached_messages_sum = data.attached_messages_sum
    }
  }

  // Update detail panel header BIC chips in-place (no full reload)
  _updateDetailBicChips(bicData, displayStatus) {
    const el = this.detailContentTarget?.querySelector("[data-detail-bic]")
    if (!el) return
    const muted = displayStatus === "resolved"
    const bicChips = (bicData || []).map(b => {
      let rawName = b.name || b.initials || "?"
      if (rawName.includes("@")) rawName = rawName.split("@")[0]
      const name = this._escapeHtml(rawName)
      const isMe = b.id === this.currentUserIdValue && !muted
      const cls = "bg-white text-gray-700 border-gray-300"
      const yourTurn = isMe ? ` <span class="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 ring-1 ring-amber-300 px-1.5 py-0.5 rounded-full leading-none">Your Turn</span>` : ""
      return `<span class="inline-flex items-center gap-1.5 px-1.5 py-px rounded border text-[13px] font-medium ${cls}">${name}${yourTurn}</span>`
    }).join("")
    // Replace just the chips after the futbol icon
    el.innerHTML = `<span class="relative w-3.5 h-3.5 shrink-0"><span class="absolute inset-[2px] rounded-full bg-white"></span>${this._futbolIcon("w-3.5 h-3.5 text-gray-400 relative")}</span> ${bicChips}`
  }

  // Enrich raw BIC data (user_id, name, email) with avatar/initials from project members
  _enrichBicFromMembers(rawBic) {
    if (!rawBic || !this._projectMembers) return rawBic
    const membersById = {}
    this._projectMembers.forEach(m => { membersById[m.id] = m })
    return rawBic.map(b => {
      const uid = b.user_id || b.id
      const member = membersById[uid]
      const name = b.name || (member && member.name) || b.email || "?"
      const initials = (name || "?").split(/\s+/).map(w => w[0]).join("").substring(0, 2).toUpperCase()
      return {
        id: uid,
        name: name,
        email: b.email || (member && member.email) || "",
        initials: initials,
        avatar_url: member ? member.avatar_url : null
      }
    })
  }

  // ── Utilities ──────────────────────────────────────────────────────

  _displayDate(dateStr) {
    if (!dateStr) return ""
    const d = new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00"))
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  }

  _displayDateTime(dateStr) {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
  }

  _formatTime(dateStr) {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  }

  _friendlyDate(dateStr) {
    const d = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (d.toDateString() === today.toDateString()) return "Today"
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday"
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  }

  _friendlyDateTime(dateStr) {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })

    if (d.toDateString() === today.toDateString()) return time
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    if (d.getFullYear() !== today.getFullYear()) {
      return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${time}`
    }
    return `${date} ${time}`
  }

  _formatDate(d) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  }

  _futbolIcon(css = "w-4 h-4") {
    return `<svg class="${css}" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true"><path d="M435.4 361.4l-89.7-6c-5.2-.3-10.3 1.1-14.5 4.2s-7.2 7.4-8.4 12.5l-22 87.2c-14.4 3.2-29.4 4.8-44.8 4.8s-30.3-1.7-44.8-4.8l-22-87.2c-1.3-5-4.3-9.4-8.4-12.5s-9.3-4.5-14.5-4.2l-89.7 6C61.7 335.9 51.9 307 49 276.2L125 228.3c4.4-2.8 7.6-7 9.2-11.9s1.4-10.2-.5-15L100.4 118c19.9-22.4 44.6-40.5 72.4-52.7l69.1 57.6c4 3.3 9 5.1 14.1 5.1s10.2-1.8 14.1-5.1l69.1-57.6c27.8 12.2 52.5 30.3 72.4 52.7l-33.4 83.4c-1.9 4.8-2.1 10.1-.5 15s4.9 9.1 9.2 11.9L463 276.2c-3 30.8-12.7 59.7-27.6 85.2zM256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm14.1-325.7c-8.4-6.1-19.8-6.1-28.2 0L194 221c-8.4 6.1-11.9 16.9-8.7 26.8l18.3 56.3c3.2 9.9 12.4 16.6 22.8 16.6h59.2c10.4 0 19.6-6.7 22.8-16.6l18.3-56.3c3.2-9.9-.3-20.7-8.7-26.8l-47.9-34.8z"/></svg>`
  }

  // ── PDF viewer bridge ────────────────────────────────────────────

  openPdfViewer(event) {
    event.preventDefault()
    event.stopPropagation()
    const attId = parseInt(event.currentTarget.dataset.attachmentId, 10)
    const filename = event.currentTarget.dataset.filename || "document.pdf"
    const viewer = this._getPdfViewerController()
    if (!viewer) return
    const commentEl = this.hasCommentInputTarget ? this.commentInputTarget : null
    viewer.open({
      attachmentId: attId,
      punchlistId: this._activeItemId,
      filename,
      commentEl,
      onDraftUpdate: () => { if (this.hasCommentInputTarget) this.autoGrowTextarea() }
    })
  }

  _getPdfViewerController() {
    const el = document.querySelector('[data-controller~="action-item-pdf-viewer"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "action-item-pdf-viewer") : null
  }

  // ── Lightbox bridge ──────────────────────────────────────────────

  openLightboxFromStrip(event) {
    const attId = parseInt(event.currentTarget.dataset.attachmentId, 10)
    this._openLightboxAtAttachment(attId)
  }

  openLightboxFromTimeline(event) {
    const el = event.currentTarget.closest("[data-attachment-id]") || event.currentTarget
    const attId = parseInt(el.dataset.attachmentId, 10)
    this._openLightboxAtAttachment(attId)
  }

  _openLightboxAtAttachment(attId) {
    if (!this._allImageAttachments || this._allImageAttachments.length === 0) return
    const photo = this._allImageAttachments.find(p => p.id === attId)
    if (!photo) return

    // Open markup tool directly instead of lightbox
    const markupCtrl = this._getMarkupController()
    if (markupCtrl) {
      const imageUrl = photo.optimized_url || photo.original_url
      const saveUrl = `/projects/${this.projectIdValue}/action_items/${this._activeItemId}/save_image_annotation`
      markupCtrl.open({
        photoId: photo.id,
        imageUrl,
        existingAnnotations: photo.annotations || null,
        currentUserName: this.currentUserNameValue,
        saveUrl,
        onSave: (attId, annotations) => {
          // Update in-memory data so re-opening shows saved annotations
          const att = this._allImageAttachments?.find(a => a.id === attId)
          if (att) att.annotations = annotations
        },
        onClose: () => {}
      })
    }
  }

  _getMarkupController() {
    const el = document.querySelector('[data-controller~="photo-markup"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "photo-markup") : null
  }

  // ── File tooltip ─────────────────────────────────────────────────

  showFileTip(event) {
    const el = event.currentTarget
    const text = el.dataset.tip
    if (!text) return

    // Skip if already showing for this element
    if (this._fileTipEl === el) return
    this.hideFileTip()
    this._fileTipEl = el

    const tip = document.createElement("div")
    tip.id = "action-items-file-tip"
    tip.className = "fixed z-[2147483005] px-2 py-1 rounded bg-gray-800 text-white text-[11px] whitespace-nowrap pointer-events-none"
    tip.textContent = text
    document.body.appendChild(tip)

    const rect = el.getBoundingClientRect()
    const tipW = tip.offsetWidth
    const tipH = tip.offsetHeight
    // Position above by default; if no room, below
    let top = rect.top - tipH - 6
    if (top < 4) top = rect.bottom + 6
    let left = rect.left + rect.width / 2 - tipW / 2
    if (left < 4) left = 4
    if (left + tipW > window.innerWidth - 4) left = window.innerWidth - tipW - 4
    tip.style.left = `${left}px`
    tip.style.top = `${top}px`
  }

  hideFileTip() {
    this._fileTipEl = null
    document.getElementById("action-items-file-tip")?.remove()
  }

  // ── Truncation tooltip ────────────────────────────────────────

  _showTruncateTip(el) {
    this._hideTruncateTip()
    const tip = document.createElement("div")
    tip.id = "truncate-tip"
    tip.className = "fixed z-[2147483005] px-2.5 py-1.5 rounded-md bg-gray-800 text-white text-xs leading-snug pointer-events-none max-w-sm"
    tip.style.whiteSpace = "pre-line"
    tip.textContent = el.textContent.trim()
    document.body.appendChild(tip)

    const rect = el.getBoundingClientRect()
    const tipW = tip.offsetWidth
    const tipH = tip.offsetHeight
    // Position below by default; if no room, above
    let top = rect.bottom + 6
    if (top + tipH > window.innerHeight - 4) top = rect.top - tipH - 6
    let left = rect.left + rect.width / 2 - tipW / 2
    if (left < 4) left = 4
    if (left + tipW > window.innerWidth - 4) left = window.innerWidth - tipW - 4
    tip.style.left = `${left}px`
    tip.style.top = `${top}px`
  }

  _hideTruncateTip() {
    document.getElementById("truncate-tip")?.remove()
  }

  // ── File upload ─────────────────────────────────────────────────

  triggerFileUpload() {
    if (this._activeItemResolved) {
      this._showToast(this.i18nReopenToCommentValue, "alert")
      return
    }
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  async handleFileSelected(event) {
    await this._stageFiles(Array.from(event.target.files || []))
  }

  // Paste a screenshot or a copied file straight into the chat, exactly
  // like ChatLog (Phelipe 2026-09-01). Document-level so it works whether
  // or not the composer has focus, but only while a chat is open, and
  // never stealing a paste aimed at another field.
  async handleChatPaste(event) {
    if (!this._activeItemId || this._activeItemResolved) return

    const files = [...(event.clipboardData?.files || [])]
    if (files.length === 0) return

    const target = event.target
    const typingElsewhere = target instanceof HTMLElement &&
      target.closest("input, textarea, [contenteditable='true']") &&
      !this.element.contains(target)
    if (typingElsewhere) return

    event.preventDefault()
    await this._stageFiles(files.map((file, i) => (
      file.name ? file : new File([file], `pasted-${Date.now()}${files.length > 1 ? `-${i + 1}` : ""}.png`, { type: file.type || "image/png" })
    )))
  }

  async _stageFiles(files) {
    if (files.length === 0) return

    const total = files.length
    const label = total === 1 ? this.i18nUploadingOneValue : this.i18nUploadingManyValue.replace("%{count}", total)

    if (this.hasUploadBtnTarget) this.uploadBtnTarget.disabled = true
    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.textContent = label
      this.uploadStatusTarget.classList.remove("hidden")
    }

    try {
      for (const file of files) {
        // Create staged entry with uploading flag
        const staged = { s3Key: null, filename: file.name, contentType: file.type || "application/octet-stream", blobUrl: null, uploading: true, chipEl: null }
        this._stagedFiles.push(staged)
        staged.chipEl = this._createStagedFileChip(staged)

        // Upload to S3 in background (no register)
        try {
          const result = await this._stageFileUpload(file)
          staged.s3Key = result.s3Key
          staged.filename = result.filename
          staged.contentType = result.contentType
          staged.blobUrl = result.blobUrl
          staged.uploading = false
          this._updateStagedFileChip(staged)
        } catch (uploadErr) {
          console.error("[ActionItems] file upload error:", uploadErr)
          // Remove failed upload from staged list
          const idx = this._stagedFiles.indexOf(staged)
          if (idx !== -1) this._stagedFiles.splice(idx, 1)
          staged.chipEl?.remove()
          this._toggleStagedFilesVisibility()
        }
      }
    } catch (e) {
      console.error("[ActionItems] file staging error:", e)
    } finally {
      if (this.hasUploadBtnTarget) this.uploadBtnTarget.disabled = false
      if (this.hasUploadStatusTarget) this.uploadStatusTarget.classList.add("hidden")
      if (this.hasFileInputTarget) this.fileInputTarget.value = ""
    }
  }

  // Upload file to S3 only (presign + PUT), no register. Returns metadata for staging.
  async _stageFileUpload(file) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    let uploadBlob = file
    let contentType = file.type || "application/octet-stream"
    const filename = file.name

    // Convert HEIC → JPEG before compression
    if (this._isHeic(file)) {
      try {
        uploadBlob = await this._convertHeicToJpeg(file)
        contentType = "image/jpeg"
      } catch (e) {
        console.warn("HEIC conversion failed, uploading raw:", e)
      }
    }

    // Compress images client-side
    const isImage = contentType.startsWith("image/") && !contentType.includes("svg")
    if (isImage) {
      uploadBlob = await this._compressImage(uploadBlob)
      contentType = "image/jpeg"
    }

    // 1. Get presigned URL
    const presignUrl = this.itemsUrlValue.replace(".json", "/presign_uploads")
    const presignResp = await fetch(presignUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ files: [{ filename, content_type: contentType, byte_size: uploadBlob.size }] })
    })

    if (!presignResp.ok) {
      const err = await presignResp.json().catch(() => ({}))
      throw new Error(err.error || `Presign failed: ${presignResp.status}`)
    }

    const { uploads } = await presignResp.json()
    const upload = uploads[0]

    // 2. PUT file to S3
    const putResp = await fetch(upload.upload_url, { method: "PUT", body: uploadBlob })
    if (!putResp.ok) throw new Error(`S3 upload failed: ${putResp.status}`)

    return {
      s3Key: upload.cache_name,
      filename: upload.filename,
      contentType,
      blobUrl: isImage ? URL.createObjectURL(uploadBlob) : null
    }
  }

  _createStagedFileChip(staged) {
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] bg-blue-100 text-blue-600 border border-blue-200"
    const safeName = this._escapeHtml(staged.filename)
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="30 70" stroke-linecap="round"/></svg><span class="truncate max-w-[140px]">${safeName}</span>`

    if (this.hasStagedFilesContainerTarget) {
      this.stagedFilesContainerTarget.appendChild(chip)
      this.stagedFilesContainerTarget.classList.remove("hidden")
    }
    this._updateSendButtonState()
    this._scrollChatToBottom()

    return chip
  }

  _updateStagedFileChip(staged) {
    const chip = staged.chipEl
    if (!chip) return

    // Switch from blue (uploading) to gray (done)
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] bg-gray-200 text-gray-700 border border-gray-300"

    // Rebuild chip content: doc icon + filename + remove button
    const safeName = this._escapeHtml(staged.filename)
    chip.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg><span class="truncate max-w-[140px]">${safeName}</span>`

    // Add remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "ml-0.5 text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
    removeBtn.innerHTML = '<svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>'
    removeBtn.addEventListener("click", () => {
      const idx = this._stagedFiles.indexOf(staged)
      if (idx !== -1) this._stagedFiles.splice(idx, 1)
      chip.remove()
      this._toggleStagedFilesVisibility()
    })
    chip.appendChild(removeBtn)
  }

  _toggleStagedFilesVisibility() {
    if (!this.hasStagedFilesContainerTarget) return
    if (this._stagedFiles.length === 0) {
      this.stagedFilesContainerTarget.classList.add("hidden")
    } else {
      this.stagedFilesContainerTarget.classList.remove("hidden")
    }
    this._updateSendButtonState()
    this._scrollChatToBottom()
  }

  _removeStagedFile(staged) {
    const idx = this._stagedFiles.indexOf(staged)
    if (idx !== -1) this._stagedFiles.splice(idx, 1)
    staged.chipEl?.remove()
    this._toggleStagedFilesVisibility()
  }

  _updateSendButtonState() {
    if (!this.hasSendBtnTarget) return
    const hasText = this.hasCommentInputTarget && this.commentInputTarget.value.trim().length > 0
    const hasFiles = this._stagedFiles.length > 0
    const hasBic = this._stagedBic && this._stagedBic.length > 0
    const hasAction = this._stagedAction !== null
    const hasTargetDate = this._stagedTargetDate !== null
    const active = hasText || hasFiles || hasBic || hasAction || hasTargetDate

    const btn = this.sendBtnTarget
    if (active) {
      btn.classList.remove("bg-white", "text-gray-400", "border-gray-200")
      btn.classList.add("bg-brand", "hover:bg-brand-dark", "text-white", "border-brand")
    } else {
      btn.classList.remove("bg-brand", "hover:bg-brand-dark", "text-white", "border-brand")
      btn.classList.add("bg-white", "text-gray-400", "border-gray-200")
    }
  }

  _scrollChatToBottom() {
    if (this.hasDetailContentTarget) {
      this.detailContentTarget.scrollTop = this.detailContentTarget.scrollHeight
    }
  }

  _clearStagedState() {
    // Revoke blob URLs to prevent memory leaks
    this._stagedFiles.forEach(f => { if (f.blobUrl) URL.revokeObjectURL(f.blobUrl) })
    this._stagedFiles = []
    this._stagedBic = null
    if (this.hasStagedFilesContainerTarget) {
      this.stagedFilesContainerTarget.innerHTML = ""
      this.stagedFilesContainerTarget.classList.add("hidden")
    }
    if (this.hasStagedBicContainerTarget) {
      this.stagedBicContainerTarget.classList.add("hidden")
      if (this.hasStagedBicChipsTarget) this.stagedBicChipsTarget.innerHTML = ""
    }
    this._stagedAction = null
    this._notifyParticipants = true
    if (this.hasStagedActionContainerTarget) {
      this.stagedActionContainerTarget.classList.add("hidden")
      this.stagedActionContainerTarget.innerHTML = ""
    }
    this._stagedTargetDate = null
    if (this.hasStagedTargetDateContainerTarget) {
      this.stagedTargetDateContainerTarget.classList.add("hidden")
    }
    this._updateSendButtonState()
  }

  _compressImage(file, maxDim = 2000, quality = 0.80) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        let { width, height } = img

        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(file)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  _isHeic(file) {
    const type = (file.type || "").toLowerCase()
    if (type.includes("heic") || type.includes("heif")) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ext === "heic" || ext === "heif"
  }

  async _convertHeicToJpeg(file) {
    if (!this._heicWorker && this.heicWorkerUrlValue) {
      this._heicWorker = new Worker(this.heicWorkerUrlValue)
      this._heicCallbacks = {}
      this._heicWorker.onmessage = (e) => {
        const cb = this._heicCallbacks[e.data.id]
        if (cb) {
          delete this._heicCallbacks[e.data.id]
          if (e.data.error) cb.reject(new Error(e.data.error))
          else cb.resolve(e.data)
        }
      }
      this._heicWorker.onerror = (e) => {
        for (const [cbId, cb] of Object.entries(this._heicCallbacks)) {
          cb.reject(new Error("Worker error"))
          delete this._heicCallbacks[cbId]
        }
      }
    }

    const buffer = await file.arrayBuffer()
    const id = `heic_${Date.now()}_${Math.random()}`

    const result = await new Promise((resolve, reject) => {
      this._heicCallbacks[id] = { resolve, reject }
      this._heicWorker.postMessage({ id, buffer }, [buffer])
    })

    const canvas = document.createElement("canvas")
    canvas.width = result.width
    canvas.height = result.height
    const ctx = canvas.getContext("2d")
    ctx.putImageData(
      new ImageData(new Uint8ClampedArray(result.data), result.width, result.height),
      0, 0
    )

    return new Promise(resolve => {
      canvas.toBlob(blob => resolve(blob), "image/jpeg", 0.8)
    })
  }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }

  // ── Toast notifications ──────────────────────────────────────────

  // Only notice/alert/info/warning have a flash-toast-* class. Call sites also
  // pass "error" and "success", which produced flash-toast-error /
  // flash-toast-success — classes that don't exist, so those toasts rendered
  // completely unstyled. Map them onto the two that do.
  _toastType(type) {
    const aliases = { error: "alert", danger: "alert", success: "notice" }
    const resolved = aliases[type] || type
    return ["notice", "alert", "info", "warning"].includes(resolved) ? resolved : "notice"
  }

  _showToast(message, type = "notice") {
    const icons = {
      notice: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
      alert: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/></svg>`
    }
    const kind = this._toastType(type)
    const el = document.createElement("div")
    el.className = `flash-toast-${kind}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    el.innerHTML = `<div class="shrink-0">${icons[kind] || icons.notice}</div><p class="flex-1 text-sm">${this._escapeHtml(message)}</p>`
    document.body.appendChild(el)
  }

  _showDrawerToast(message) {
    const panel = this.detailPanelTarget
    const panelLeft = panel.getBoundingClientRect().left
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("role", "alert")
    el.innerHTML = `<div class="shrink-0"><svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div><p class="flex-1 text-sm">${this._escapeHtml(message)}</p>`
    // Position to the left of the drawer, bypassing flash controller auto-positioning
    el.style.right = "auto"
    el.style.left = `${panelLeft - el.offsetWidth - 12}px`
    el.style.top = "9px"
    el.style.cursor = "pointer"
    document.body.appendChild(el)

    // Measure after append and reposition
    requestAnimationFrame(() => {
      el.style.left = `${panelLeft - el.offsetWidth - 12}px`
      el.style.opacity = "1"
      el.style.transform = "translateX(0)"
    })

    // Click to dismiss
    el.addEventListener("click", () => {
      el.style.opacity = "0"
      setTimeout(() => el.remove(), 300)
    })

    // Auto-dismiss after 5s
    setTimeout(() => {
      if (el.parentNode) {
        el.style.opacity = "0"
        setTimeout(() => el.remove(), 300)
      }
    }, 5000)
  }

  _updateCommentInputState() {
    const disabled = this._activeItemResolved
    if (this.hasCommentInputTarget) {
      this.commentInputTarget.disabled = disabled
      this.commentInputTarget.placeholder = disabled
        ? this.i18nReopenToCommentValue
        : "Write a comment..."
    }
    if (this.hasSendBtnTarget) this.sendBtnTarget.disabled = disabled
    if (this.hasUploadBtnTarget) this.uploadBtnTarget.disabled = disabled
  }

  _showPendingToast() {
    const msg = sessionStorage.getItem("actionItemToast")
    if (msg) {
      sessionStorage.removeItem("actionItemToast")
      const { message, type } = JSON.parse(msg)
      this._showToast(message, type)
    }
  }

  // ── Checklist inside the item (Keane K3-38, Phelipe 2026-10-13) ──────────
  // Drawn under the header from `data.checklist`; every change goes to the
  // server and the block is redrawn from the answer. Ticking the last line
  // can complete the item (the switch on the form), so the timeline reloads
  // when the server says `completed`.

  _checklistBase() {
    return `${this.itemsUrlValue.replace(".json", "")}/${this._activeItemId}/checklist`
  }

  _renderChecklistBlock(data) {
    const cl = data.checklist
    this._checklistData = cl || null
    this._checklistMembers = data.project_members || []
    // What this person may do comes from the server (who manages the list,
    // who may tick which line, whether the item is closed).
    const canManage = !!cl?.permissions?.manage
    if (!cl || !cl.enabled) {
      if (!canManage) return ""
      // No checklist: one quiet link to start one (the form's switch, from here).
      return `<div class="px-5 py-2 border-b border-gray-200 bg-white text-[13px]" data-checklist-block>
        <button type="button" data-action="click->action-items#enableChecklistOnItem"
                class="inline-flex items-center gap-1.5 text-gray-500 hover:text-brand cursor-pointer">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
          Add a checklist
        </button>
      </div>`
    }
    return `<div class="px-5 py-3 border-b border-gray-200 bg-white" data-checklist-block>${this._checklistInner(cl)}</div>`
  }

  _checklistInner(cl) {
    const perms = cl.permissions || {}
    const canEdit = !!perms.manage
    const total = cl.progress?.total || 0
    const done = cl.progress?.done || 0
    const pct = total ? Math.round((done / total) * 100) : 0
    const open = this._checklistOpenLine
    const lines = (cl.lines || []).map(l => this._checklistLineHtml(l, String(l.id) === String(open), canEdit)).join("")
    const autoOn = cl.autocomplete
    // Folded by default; the way this person leaves it is remembered per item.
    const expanded = !!cl.open
    return `
      <div class="flex items-center gap-3 ${expanded ? "mb-2" : ""}">
        <button type="button" data-action="click->action-items#toggleChecklistOpen" aria-expanded="${expanded ? "true" : "false"}"
                class="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer group" title="${expanded ? "Fold the checklist" : "Open the checklist"}">
          <svg class="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-transform ${expanded ? "rotate-90" : ""}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>
          <span class="text-[13px] font-semibold text-gray-900">Checklist</span>
          <span class="text-[13px] text-gray-500">${done} of ${total} done</span>
          <span class="flex-1 max-w-[220px] h-1.5 rounded-full bg-gray-200 overflow-hidden"><span class="block h-full bg-emerald-500" style="width:${pct}%"></span></span>
        </button>
        ${perms.locked
          ? `<span class="ml-auto text-[12px] text-gray-400 whitespace-nowrap">Closed · reopen it to change the checklist</span>`
          : canEdit
            ? `<button type="button" role="switch" aria-checked="${autoOn ? "true" : "false"}" data-action="click->action-items#toggleChecklistAutocompleteOnItem"
                title="Complete the item when every line is ticked"
                class="ml-auto inline-flex items-center gap-1.5 text-[12px] text-gray-500 cursor-pointer">
          <span class="relative inline-flex h-[16px] w-7 shrink-0 items-center rounded-full transition-colors ${autoOn ? "bg-brand" : "bg-gray-300"}"><span class="inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${autoOn ? "translate-x-[14px]" : "translate-x-0.5"}"></span></span>
          <span class="hidden sm:inline">completes the item</span>
        </button>`
            : (autoOn ? `<span class="ml-auto text-[12px] text-gray-400 whitespace-nowrap hidden sm:inline">completes the item</span>` : "")}
      </div>
      ${cl.waiting_to_close ? `<div class="mt-1.5 mb-1 inline-flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[12px] text-amber-800">
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
        ${cl.waiting_to_close.you ? "All lines ticked. You can close the item now." : `All lines ticked. Waiting for ${this._escapeHtml(cl.waiting_to_close.names)} to close it.`}
      </div>` : ""}
      <div class="rounded-lg border border-gray-200 overflow-hidden ${expanded ? "" : "hidden"}" data-checklist-lines>
        ${lines || `<div class="px-3 py-2 text-[13px] text-gray-400">No lines yet — add the first one below.</div>`}
        ${canEdit ? `<div class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-t border-gray-200">
          <span class="w-4 h-4 rounded border border-gray-300 bg-white shrink-0 opacity-50"></span>
          <input type="text" data-action="keydown->action-items#checklistInlineKeydown" data-checklist-new
                 placeholder="Add a line and press Enter…"
                 class="flex-1 min-w-0 px-1 py-1 text-[13px] bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 placeholder:text-gray-400">
          <button type="button" data-action="click->action-items#openChecklistPasteOnItem" class="text-[12px] font-medium text-brand hover:underline cursor-pointer whitespace-nowrap">Paste a list</button>
          <button type="button" data-action="click->action-items#suggestChecklistLinesOnItem" data-checklist-ai
                  class="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-2 py-0.5 text-[12px] cursor-pointer disabled:opacity-60 disabled:cursor-wait whitespace-nowrap">
            <svg class="w-3.5 h-3.5 ai-gradient-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036a2.63 2.63 0 0 0 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258a2.63 2.63 0 0 0-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.63 2.63 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.63 2.63 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z"/></svg>
            <span class="ai-gradient-text font-semibold">Suggest lines</span>
          </button>
        </div>
        <div class="hidden px-3 py-2 bg-gray-50 border-t border-gray-200 space-y-2" data-checklist-paste>
          <textarea rows="4" data-checklist-paste-input placeholder="One item per line. A name after a dash becomes the line's person." class="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand resize-y"></textarea>
          <div class="flex justify-end gap-2">
            <button type="button" data-action="click->action-items#closeChecklistPasteOnItem" class="px-3 py-1 text-[12px] font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">Cancel</button>
            <button type="button" data-action="click->action-items#addChecklistPasteOnItem" class="px-3 py-1 text-[12px] font-medium text-white bg-brand rounded-lg hover:bg-brand-dark cursor-pointer">Add the lines</button>
          </div>
        </div>` : ""}
      </div>`
  }

  _checklistLineHtml(l, isOpen, canEdit) {
    const canTick = !!l.can_tick
    const esc = (t) => this._escapeHtml(t || "")
    const isSub = l.depth === 1
    const isParent = (l.children_count || 0) > 0
    const subs = isParent ? (this._checklistData?.lines || []).filter(c => c.parent_id === l.id) : []
    const subCount = isParent ? `<span class="text-[12px] text-gray-400 whitespace-nowrap shrink-0">${subs.filter(c => c.done).length} of ${subs.length}</span>` : ""
    const person = l.assignee ? `<span class="inline-flex items-center gap-1 rounded-full pl-px pr-2 py-px bg-gray-100 text-gray-700 text-[12px] font-medium shrink-0"><span class="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-medium">${esc(l.assignee.initials || (l.assignee.name || "?").slice(0, 2).toUpperCase())}</span>${esc(l.assignee.name)}</span>` : ""
    const photos = (l.photos || []).length ? `<span class="inline-flex items-center gap-0.5 text-[12px] text-gray-500 shrink-0" title="${(l.photos || []).length} photo(s)"><svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg>${(l.photos || []).length}</span>` : ""
    const who = l.done && l.done_by ? `<span class="text-[12px] text-gray-400 whitespace-nowrap shrink-0">${esc(l.done_by.name.split(" ")[0])} · ${this._shortDate(l.done_at)}</span>` : ""
    const note = l.note ? `<span class="text-[12px] text-gray-400 truncate max-w-[180px]" title="${esc(l.note)}">${esc(l.note)}</span>` : ""
    const box = `<button type="button" data-action="click->action-items#toggleChecklistLine" data-line-id="${l.id}" ${canTick ? "" : "disabled"}
        class="w-[18px] h-[18px] rounded border shrink-0 flex items-center justify-center transition-colors ${canTick ? "cursor-pointer" : "cursor-not-allowed opacity-70"} ${l.done ? "bg-emerald-500 border-emerald-500 text-white" : `bg-white border-gray-300 ${canTick ? "hover:border-brand" : ""}`}" title="${canTick ? (l.done ? "Untick" : "Tick") : "Only the people this item is assigned to, whoever has the ball, or this line's person can tick it"}">${l.done ? `<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>` : ""}</button>`
    const detail = isOpen ? this._checklistLineDetailHtml(l, canEdit) : ""
    return `<div class="border-b border-gray-100 last:border-b-0" data-checklist-line="${l.id}" data-checklist-depth="${isSub ? 1 : 0}">
      <div class="flex items-center gap-2 py-1.5 pr-3 hover:bg-gray-50 ${isSub ? "pl-9 bg-gray-50/60" : "pl-3"}">
        ${box}
        <button type="button" data-action="click->action-items#openChecklistLine" data-line-id="${l.id}"
                class="flex-1 min-w-0 text-left text-[13px] truncate cursor-pointer ${l.done ? "text-gray-400 line-through" : isParent ? "text-gray-900 font-medium" : "text-gray-800"}" title="Open">${esc(l.text)}</button>
        ${subCount}${note}${person}${photos}${who}
      </div>
      ${detail}
    </div>`
  }

  _checklistLineDetailHtml(l, canEdit) {
    const esc = (t) => this._escapeHtml(t || "")
    const members = this._checklistMembers || []
    const options = [`<option value="">— person —</option>`].concat(members.map(m => `<option value="${m.id}" ${l.assignee && String(m.id) === String(l.assignee.id) ? "selected" : ""}>${esc(m.name || m.email)}</option>`)).join("")
    const photos = (l.photos || []).map(p => `<a href="${esc(p.url)}" target="_blank" class="block w-14 h-11 rounded-md overflow-hidden border border-gray-200 bg-gray-100 shrink-0"><img src="${esc(p.thumb_url || p.url)}" class="w-full h-full object-cover" alt=""></a>`).join("")
    return `<div class="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-100 space-y-2" data-checklist-detail>
      <div class="flex items-center gap-2">
        <input type="text" value="${esc(l.text)}" data-line-id="${l.id}" data-action="change->action-items#saveChecklistLineText keydown->action-items#checklistDetailKeydown" ${canEdit ? "" : "disabled"}
               class="flex-1 min-w-0 px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
        <select data-line-id="${l.id}" data-action="change->action-items#changeChecklistLineAssignee" ${canEdit ? "" : "disabled"}
                class="px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none cursor-pointer max-w-[170px]">${options}</select>
      </div>
      <textarea rows="2" data-line-id="${l.id}" data-action="change->action-items#saveChecklistLineNote" placeholder="A note on this line…" ${l.can_tick ? "" : "disabled"}
                class="w-full px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand resize-y">${esc(l.note)}</textarea>
      <div class="flex items-center gap-2 flex-wrap">
        ${photos}
        ${l.can_tick ? `<label class="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline cursor-pointer">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18A2.25 2.25 0 0 0 4.5 20.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"/></svg>
          Add a photo
          <input type="file" accept="image/*" multiple class="hidden" data-line-id="${l.id}" data-action="change->action-items#checklistLinePhoto">
        </label>` : ""}
        <span class="flex-1"></span>
        ${l.done && l.done_by ? `<span class="text-[12px] text-gray-400">Ticked by ${esc(l.done_by.name)} · ${this._shortDate(l.done_at, true)}</span>` : ""}
        ${canEdit && l.depth !== 1 && this._checklistLineAbove(l) ? `<button type="button" data-action="click->action-items#nestChecklistLine" data-line-id="${l.id}" class="text-[12px] text-gray-600 hover:underline cursor-pointer" title="Becomes a sub-line of the line above">Make it a sub-line</button>` : ""}
        ${canEdit && l.depth === 1 ? `<button type="button" data-action="click->action-items#unnestChecklistLine" data-line-id="${l.id}" class="text-[12px] text-gray-600 hover:underline cursor-pointer">Move out</button>` : ""}
        ${canEdit ? `<button type="button" data-action="click->action-items#deleteChecklistLine" data-line-id="${l.id}" class="text-[12px] text-rose-600 hover:underline cursor-pointer">Remove line</button>` : ""}
        <button type="button" data-action="click->action-items#closeChecklistLine" class="text-[12px] text-gray-500 hover:underline cursor-pointer">Close</button>
      </div>
      ${canEdit && l.depth !== 1 ? `<div class="flex items-center gap-2 pl-6">
        <span class="w-4 h-4 rounded border border-gray-300 bg-white shrink-0 opacity-50"></span>
        <input type="text" data-line-id="${l.id}" data-action="keydown->action-items#checklistSubLineKeydown" data-checklist-sub-new placeholder="Add a sub-line and press Enter…"
               class="flex-1 min-w-0 px-2 py-1 text-[13px] border border-dashed border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
      </div>` : ""}
    </div>`
  }

  // The nearest top-level line above this one (what "Make it a sub-line" nests under).
  _checklistLineAbove(line) {
    const lines = this._checklistData?.lines || []
    const i = lines.findIndex(l => l.id === line.id)
    for (let j = i - 1; j >= 0; j--) if (lines[j].depth !== 1) return lines[j]
    return null
  }

  nestChecklistLine(e) {
    const id = e.currentTarget.dataset.lineId
    const line = (this._checklistData?.lines || []).find(l => String(l.id) === id)
    const above = line ? this._checklistLineAbove(line) : null
    if (!above) return
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}`, { parent_line_id: above.id }))
  }

  unnestChecklistLine(e) {
    const id = e.currentTarget.dataset.lineId
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}`, { parent_line_id: "" }))
  }

  checklistSubLineKeydown(e) {
    if (e.key !== "Enter") return
    e.preventDefault()
    const input = e.currentTarget
    const text = input.value.trim()
    const parentId = input.dataset.lineId
    if (!text) return
    input.value = ""
    this._checklistOpenLine = parentId
    this._checklistAction(() => this._checklistRequest("POST", `${this._checklistBase()}/lines`, { text, source: "manual", parent_line_id: parentId }))
      .then(() => { const again = this.detailContentTarget.querySelector(`[data-checklist-sub-new][data-line-id="${parentId}"]`); if (again) again.focus() })
  }

  _shortDate(iso, withTime = false) {
    if (!iso) return ""
    const d = new Date(iso)
    if (isNaN(d)) return ""
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    return withTime ? `${date}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : date
  }

  async _checklistRequest(method, url, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(url, {
      method, credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      body: body ? JSON.stringify(body) : undefined
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || "Couldn't save the checklist.")
    return data
  }

  _repaintChecklist(payload) {
    const block = this.detailContentTarget.querySelector("[data-checklist-block]")
    if (!block) return
    this._checklistData = payload
    if (!payload.enabled) {
      block.outerHTML = this._renderChecklistBlock({ checklist: payload, project_members: this._checklistMembers })
    } else {
      block.className = "px-5 py-3 border-b border-gray-200 bg-white"
      block.innerHTML = this._checklistInner(payload)
    }
    // The row in the list behind the drawer shows "3 of 6".
    const item = (this._allItems || []).find(i => String(i.id) === String(this._activeItemId))
    if (item) item.checklist = payload.enabled ? payload.progress : null
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${this._activeItemId}"]`)
    if (row && item) row.outerHTML = this._renderRow(item)
  }

  async _checklistAction(fn, successMessage) {
    try {
      const data = await fn()
      this._repaintChecklist(data)
      if (data.completed) {
        this._showToast("Every line is ticked — the item is complete.")
        this._loadTimeline(this._activeItemId)
      } else if (data.just_ticked && data.waiting_to_close && !data.waiting_to_close.you) {
        this._showToast(`All lines ticked. Waiting for ${data.waiting_to_close.names} to close it.`)
      } else if (successMessage) {
        this._showToast(successMessage)
      }
      return data
    } catch (e) {
      this._showToast(e.message || "Couldn't save the checklist.", "alert")
      return null
    }
  }

  // Open or fold the whole checklist; the server remembers it for this person.
  toggleChecklistOpen() {
    const open = !(this._checklistData && this._checklistData.open)
    if (this._checklistData) { this._checklistData.open = open; this._repaintChecklist(this._checklistData) }
    this._checklistRequest("PATCH", `${this._checklistBase()}/open`, { open }).then(data => { if (data) this._repaintChecklist(data) }).catch(() => {})
  }

  // Reordering happens on the edit form (drag), not here (Phelipe 2026-10-13).

  enableChecklistOnItem() {
    this._checklistAction(() => this._checklistRequest("PATCH", this._checklistBase(), { enabled: true }))
      .then(() => { const input = this.detailContentTarget.querySelector("[data-checklist-new]"); if (input) input.focus() })
  }

  toggleChecklistAutocompleteOnItem() {
    const on = !(this._checklistData && this._checklistData.autocomplete)
    this._checklistAction(() => this._checklistRequest("PATCH", this._checklistBase(), { autocomplete: on }),
      on ? "The item will complete itself when every line is ticked." : "The item now closes the way it does today.")
  }

  toggleChecklistLine(e) {
    const id = e.currentTarget.dataset.lineId
    const line = (this._checklistData?.lines || []).find(l => String(l.id) === id)
    const done = line ? !line.done : true
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}/toggle`, { done }))
  }

  openChecklistLine(e) {
    const id = e.currentTarget.dataset.lineId
    this._checklistOpenLine = String(this._checklistOpenLine) === String(id) ? null : id
    if (this._checklistData) this._repaintChecklist(this._checklistData)
  }

  closeChecklistLine() {
    this._checklistOpenLine = null
    if (this._checklistData) this._repaintChecklist(this._checklistData)
  }

  checklistDetailKeydown(e) {
    if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur() }
  }

  saveChecklistLineText(e) {
    const id = e.currentTarget.dataset.lineId
    const text = e.currentTarget.value.trim()
    if (!text) return
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}`, { text }))
  }

  saveChecklistLineNote(e) {
    const id = e.currentTarget.dataset.lineId
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}`, { note: e.currentTarget.value }))
  }

  changeChecklistLineAssignee(e) {
    const id = e.currentTarget.dataset.lineId
    const uid = e.currentTarget.value || ""
    this._checklistAction(() => this._checklistRequest("PATCH", `${this._checklistBase()}/lines/${id}`, { assignee_user_id: uid }))
  }

  deleteChecklistLine(e) {
    const id = e.currentTarget.dataset.lineId
    this._checklistOpenLine = null
    this._checklistAction(() => this._checklistRequest("DELETE", `${this._checklistBase()}/lines/${id}`))
  }

  checklistInlineKeydown(e) {
    if (e.key !== "Enter") return
    e.preventDefault()
    const input = e.currentTarget
    const text = input.value.trim()
    if (!text) return
    input.value = ""
    this._checklistAction(() => this._checklistRequest("POST", `${this._checklistBase()}/lines`, { text, source: "manual" }))
      .then(() => { const again = this.detailContentTarget.querySelector("[data-checklist-new]"); if (again) again.focus() })
  }

  openChecklistPasteOnItem() {
    const box = this.detailContentTarget.querySelector("[data-checklist-paste]")
    if (!box) return
    box.classList.remove("hidden")
    box.querySelector("[data-checklist-paste-input]")?.focus()
  }

  closeChecklistPasteOnItem() {
    this.detailContentTarget.querySelector("[data-checklist-paste]")?.classList.add("hidden")
  }

  async addChecklistPasteOnItem() {
    const ta = this.detailContentTarget.querySelector("[data-checklist-paste-input]")
    const text = ta ? ta.value : ""
    if (!text.trim()) { this.closeChecklistPasteOnItem(); return }
    let lines = []
    try {
      const parsed = await this._checklistRequest("POST", `${this.itemsUrlValue.replace(".json", "")}/checklist/parse_text`, { text })
      lines = parsed.lines || []
    } catch (_) {
      lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(t => ({ text: t }))
    }
    if (!lines.length) return
    this._checklistAction(() => this._checklistRequest("POST", `${this._checklistBase()}/lines`, { lines, source: "paste" }), `Added ${lines.length} ${lines.length === 1 ? "line" : "lines"}`)
  }

  async suggestChecklistLinesOnItem() {
    const btn = this.detailContentTarget.querySelector("[data-checklist-ai]")
    const pl = this._currentPunchlist || {}
    if (btn) btn.disabled = true
    try {
      const data = await this._checklistRequest("POST", `${this.itemsUrlValue.replace(".json", "")}/checklist/suggest`,
        { title: pl.title || "", kind: pl.kind_name || "", notes: pl.item || "", spec_drawings: pl.spec_drawings || "" })
      const lines = data.lines || []
      if (!lines.length) { this._showToast(data.error || "ConsLog AI couldn't come up with lines for this.", "alert"); return }
      await this._checklistAction(() => this._checklistRequest("POST", `${this._checklistBase()}/lines`, { lines, source: "ai" }), `AI suggested ${lines.length} ${lines.length === 1 ? "line" : "lines"} — remove any you don't want.`)
    } catch (e) {
      this._showToast(e.message || "ConsLog AI couldn't run — try again in a moment.", "alert")
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // Photos on a line go up like chat photos: presign → PUT → register, then
  // the registered uploads are attached to the line.
  async checklistLinePhoto(e) {
    const id = e.currentTarget.dataset.lineId
    const files = Array.from(e.currentTarget.files || [])
    e.currentTarget.value = ""
    if (!files.length) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const base = this.itemsUrlValue.replace(".json", "")
    try {
      const presignRes = await fetch(`${base}/presign_uploads`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      })
      if (!presignRes.ok) throw new Error("Couldn't start the upload.")
      const { uploads } = await presignRes.json()
      const done = []
      await Promise.all(uploads.map(async (up, i) => {
        const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": files[i].type || up.content_type }, body: files[i] })
        if (put.ok) done.push({ cache_name: up.cache_name, filename: up.filename })
      }))
      if (!done.length) throw new Error("The upload failed.")
      const registerRes = await fetch(`${base}/register_uploads`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ files: done })
      })
      const registered = await registerRes.json()
      const photoIds = (Array.isArray(registered) ? registered : []).map(r => r.photo_id).filter(Boolean)
      this._checklistOpenLine = id
      await this._checklistAction(() => this._checklistRequest("POST", `${this._checklistBase()}/lines/${id}/photos`, { photo_ids: photoIds }), "Photo added to the line.")
    } catch (err) {
      this._showToast(err.message || "Couldn't add the photo.", "alert")
    }
  }

  // Spec and Package columns show on the Submittal tab only (K3-40).
  _submittalColumnsOn() {
    if (!this.formalValue) return false
    const kindId = this._filters?.kind_id
    if (!kindId || !this._kinds) return false
    const kind = this._kinds.find(k => String(k.id) === String(kindId))
    return !!(kind && /submittal/i.test(kind.name || ""))
  }

  _applySubmittalColumns() {
    const on = this._submittalColumnsOn()
    this.element.querySelectorAll(".col-submittal").forEach(el => el.classList.toggle("hidden", !on))
  }

  // Package · needed on site · lead time · review · submit by (K3-40).
  _renderSubmittalLine(sub) {
    if (!sub) return ""
    const parts = []
    const pair = (k, v) => `<span class="inline-flex items-center gap-1"><span class="text-gray-400">${k}</span><span class="text-gray-700 font-medium">${v}</span></span>`
    if (sub.package) parts.push(pair("Package", this._escapeHtml(sub.package.name)))
    if (sub.needed_on_site) parts.push(pair("Needed on site", this._shortDate(sub.needed_on_site)))
    if (sub.lead_time_days != null && sub.lead_time_days !== "") parts.push(pair("Lead time", `${sub.lead_time_days} days`))
    if (sub.review_days != null && sub.review_days !== "") parts.push(pair("Review", `${sub.review_days} days`))
    if (sub.submit_by) parts.push(pair("Submit by", this._shortDate(sub.submit_by)))
    if (!parts.length) return ""
    return `<div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[13px]" data-detail-submittal>${parts.join("")}</div>`
  }
}
